/**
 * ALL magic numbers live here. (Brief §15.5)
 *
 * Rule: nothing in /sim may hardcode a tunable. If you are about to type a
 * number into a simulation file, it belongs in this file instead. The dev
 * overlay (leva) binds directly to `TUNABLES` so feel can be iterated live
 * without a rebuild — football feel comes from 200 iterations on 50 numbers.
 *
 * Values are transcribed from the project brief. Section refs are cited so a
 * reader can audit them against the source document.
 */

// ---------------------------------------------------------------------------
// Netcode & tick (§2.2)
// ---------------------------------------------------------------------------

/** Fixed simulation rate. The sim NEVER sees a variable dt. (§3.1) */
export const SIMULATION_TICK_RATE = 60;
/** 16.667ms. Derived, never typed as a literal elsewhere. */
export const FIXED_DT = 1 / SIMULATION_TICK_RATE;
/** Server → client snapshot rate, delta-compressed. */
export const SNAPSHOT_SEND_RATE = 20;
/** Client → server input rate, with redundancy. */
export const INPUT_SEND_RATE = 60;
/** Render remote entities this far in the past to absorb jitter. (§2.5) */
export const INTERPOLATION_DELAY_MS = 100;
/** Beyond this, hard snap rather than re-simulate. (§2.2) */
export const MAX_ROLLBACK_TICKS = 30;
/** Divergence above this triggers reconciliation re-simulation. (§2.4) */
export const POSITION_EPSILON = 0.05;
/** Visual mesh eases toward the corrected physics body over this window. (§2.4) */
export const RECONCILE_SMOOTH_MS = 100;
/** Input ring buffer capacity. (§2.3) */
export const INPUT_BUFFER_CAPACITY = 64;
/** Extrapolate on buffer starvation for at most this long, then freeze. (§2.5) */
export const MAX_EXTRAPOLATION_MS = 200;
/** Server-side rewind ceiling — beyond this, high-ping players reach through time. (§2.6) */
export const MAX_LAG_COMPENSATION_MS = 250;
/** Hitbox history retained for lag compensation. (§2.6) */
export const HITBOX_HISTORY_MS = 500;
/** Reject clients exceeding this input rate. (§2.9) */
export const MAX_INPUTS_PER_SEC = 70;
/** Players beyond this distance from the local camera drop to 10Hz. (§2.8) */
export const RELEVANCY_FAR_DISTANCE = 40;
export const RELEVANCY_FAR_RATE = 10;
/** Matchmaking hard gate — better to wait than play a bad match. (§9.5) */
export const MAX_MATCHMAKING_RTT_MS = 120;
/** Reconnect grace before the slot is surrendered to AI permanently. (§9.3) */
export const RECONNECT_GRACE_MS = 60_000;

// ---------------------------------------------------------------------------
// Quantization bounds — the wire format depends on these. (§2.8)
// Changing any of these is a PROTOCOL BREAK. Bump PROTOCOL_VERSION.
// ---------------------------------------------------------------------------

/** Bounded pitch volume for 16-bit position quantization → ~2mm precision. */
export const QUANT_POS_MIN_X = -60;
export const QUANT_POS_MAX_X = 60;
/**
 * Vertical encoding range (Phase-1 Step 7; protocol v4). Was [−10, 10]: the
 * ball.ts encoding failsafe flattened every lofted clearance against an
 * invisible 9.89 m sky. Only the CEILING was widened.
 *
 * MIN_Y stays −10 deliberately: physics-rapier sizes the ground slab from it
 * (thickness −MIN_Y, top face at y=0), and thinning the slab to 1 m was
 * MEASURED destabilizing the character controller's resting contact on the top
 * face (prediction.test: a pressed move produced −0.5 mm of drift instead of
 * forward motion). Nothing in the game lives below y≈−0.4, so the bottom range
 * costs only wire precision, not correctness.
 *
 * MAX SUPPORTED BALL HEIGHT: the highest realistic in-game trajectory is a
 * full-power loft at the maximum lift angle — 40.25 m/s at 32° ⇒ 15.13 m peak
 * after drag (measured, boundary-behavior Probe 3; GK punt 14.0 m, max lofted
 * shot 12.5 m). 50 m gives ≈ 3.3× headroom, so no football trajectory can
 * touch the encoding ceiling; the ball.ts failsafe at 49.89 m is genuinely
 * dead code. 16-bit over the 60 m span ⇒ 0.92 mm precision — ~54× finer than
 * POSITION_EPSILON (0.05 m), so reconciliation never sees quantization noise.
 */
export const QUANT_POS_MIN_Y = -10;
export const QUANT_POS_MAX_Y = 50;
export const QUANT_POS_MIN_Z = -40;
export const QUANT_POS_MAX_Z = 40;
/** Velocity quantization range, 16-bit per axis. */
export const QUANT_VEL_MIN = -60;
export const QUANT_VEL_MAX = 60;
/** Angular velocity range (rad/s), 16-bit per axis. */
export const QUANT_ANGVEL_MIN = -150;
export const QUANT_ANGVEL_MAX = 150;
export const QUANT_POS_BITS = 16;
export const QUANT_VEL_BITS = 16;
/** smallest-three quaternion compression: 2 bits index + 3×10 bits. (§2.8) */
export const QUANT_QUAT_BITS = 32;
export const QUANT_ANIM_ENUM_BITS = 6;
export const QUANT_ANIM_BLEND_BITS = 8;

/**
 * Bumped on ANY wire-format change. Mismatch → force refresh. (§12.6)
 * v2 (net/switch wave): input flags widened 16→18 bits (Jump/BicycleKick), the
 * Welcome now carries the roster + switchingEnabled, and ControlAssign (0x16) was
 * added. Those three wire changes ship together under this single bump.
 * v3 (fingerprint wave): the Welcome now carries the build fingerprint
 * (simulationVersion + constantsHash + gitCommit) so a client can reject a server
 * running a different SIM even when the wire LAYOUT is unchanged. Adding those
 * three fields to the Welcome payload is itself a wire change, hence this bump.
 * See net/fingerprint.ts.
 * v4 (Phase-1 Step 7): vertical quantization range widened [−10,10] → [−2,50] so
 * lofted clearances stop flattening against the encodable ceiling. Same bit
 * widths, different dequantization mapping — an old client would decode every y
 * wrong, hence the bump.
 * v5 (referee wave, §5.6): a new server→client frame, RefereeSignal (0x17) —
 * "the referee whistled this restart" / "showed this card". Additive: no existing
 * layout changed, and an older client drops the unknown type byte harmlessly. It
 * is bumped anyway because the message-type registry is part of the wire format
 * and §12.6 admits no judgement calls about which halves of it count. See
 * net/refsignal.ts for why a whistle and a card are the only rule events on the
 * wire (they are EDGES no snapshot can reconstruct; a goal is not).
 * v6 (action execution id, §11): the per-player snapshot field mask widens from
 * 12 to 13 and gains `animExec[4]` — the wrapped `players.animExecId`. This one
 * is NOT additive: the mask width is read before any field, so a v5 decoder
 * mis-frames every player in the packet from the first one onward. `anim` alone
 * could not distinguish a HELD state from a re-armed one, which made "one
 * animation start / one contact event per accepted action" undecidable on the
 * client — see sim/anim-exec.ts.
 * v8 (power-kick wave, 2026-08-15): the ball's snapshot field mask widens
 * 12 → 13 and gains `powerKick[1]` (net/snapshot.ts BF_POWERKICK) so a remote
 * opponent's boost-2 / full-charge kick shows the red trail — a v7 decoder
 * mis-frames the ball block.
 */
export const PROTOCOL_VERSION = 8;

/**
 * The version of sim BEHAVIOUR — the gameplay MATH, not the wire format. (§12.6)
 *
 * PROTOCOL_VERSION guards the bytes on the wire; this guards the meaning of a
 * tick. Bump it whenever a change makes the sim produce a DIFFERENT outcome from
 * the same (state, inputs) in a way a constant change alone does not capture — a
 * new term in the physics integration, a reordered rules stage, a changed
 * collision model. A pure tuning change (editing a number in this file) is caught
 * automatically by the constantsHash (net/fingerprint.ts) and does NOT need a bump
 * here; SIMULATION_VERSION is for behaviour changes in the CODE that no constant
 * value reflects. Two peers whose (protocolVersion, simulationVersion,
 * constantsHash) all agree are running the identical game — see
 * gameplayCriticalMismatch().
 */
// v2: strike error became a 2-D cone (every shot/pass/header/bicycle draws a
//     SECOND gaussian for vertical spray — the RNG stream advances differently
//     from v1 even with identical constants).
export const SIMULATION_VERSION = 2;

// ---------------------------------------------------------------------------
// Bandwidth budget — hard requirement, asserted in load tests. (§2.8, §13.3)
// ---------------------------------------------------------------------------

export const BANDWIDTH_BUDGET_DOWN_BPS = 32 * 1024;
export const BANDWIDTH_BUDGET_UP_BPS = 8 * 1024;
/** Server tick must stay under this at 22 players. (§13.3) */
export const SERVER_TICK_BUDGET_MS = 8;

// ---------------------------------------------------------------------------
// Ball — FIFA regulation Size 5. (§3.2)
// ---------------------------------------------------------------------------

export const BALL_MASS = 0.43; // kg
export const BALL_RADIUS = 0.11; // m
export const BALL_CIRCUMFERENCE = 0.69; // m
export const GRAVITY = -9.81; // m/s²
/** Hollow sphere: (2/3)·m·r² */
export const BALL_INERTIA = (2 / 3) * BALL_MASS * BALL_RADIUS * BALL_RADIUS;
/** Cross-sectional area, π·r² */
export const BALL_AREA = Math.PI * BALL_RADIUS * BALL_RADIUS;
export const BALL_RESTITUTION_GRASS = 0.8;
export const BALL_RESTITUTION_POST = 0.85;
export const BALL_FRICTION_GRASS = 0.35; // rolling
/** Rolling resistance coefficient: F = -0.02·m·g·v̂ (§3.2) */
export const BALL_ROLLING_RESISTANCE = 0.02;
/** Spin decay in air: ω *= (1 - 0.05·dt) (§3.2) */
export const BALL_SPIN_DECAY_AIR = 0.05;
/** Much faster decay on ground contact. */
export const BALL_SPIN_DECAY_GROUND = 2.5;
/** Cap |ω| — elite players reach ~10 rev/s. (§3.2) */
export const BALL_MAX_ANGULAR_VELOCITY = 120; // rad/s

/**
 * Drag crisis model (§3.2). Cd is velocity-dependent — this is what makes a
 * knuckleball dip unpredictably and free-kicks feel real. Do not flatten it.
 */
export const DRAG_LAMINAR_MAX_SPEED = 8; // v < 8 → laminar
export const DRAG_CRISIS_MAX_SPEED = 12; // 8..12 → transition
export const DRAG_TURBULENT_MAX_SPEED = 25; // 12..25 → turbulent
export const CD_LAMINAR = 0.47;
export const CD_TURBULENT = 0.2;
export const CD_HIGH_SPEED = 0.22; // v ≥ 25, slight rise

/**
 * Magnus / lift (§3.2):
 *   Cl ≈ clamp(MAGNUS_CL_COEFF · (r·|ω| / |v|)^MAGNUS_CL_EXP, 0, MAGNUS_CL_MAX)
 */
export const MAGNUS_CL_COEFF = 0.385;
export const MAGNUS_CL_EXP = 0.25;
export const MAGNUS_CL_MAX = 0.35;

// ---------------------------------------------------------------------------
// Environment — match settings. (§3.2)
// ---------------------------------------------------------------------------

/** Air density at sea level. A match setting — altitude changes flight. (§3.2) */
export const AIR_DENSITY_SEA_LEVEL = 1.225; // kg/m³
/** Wet pitch: friction × 0.6, restitution × 1.1 (§3.2) */
export const WET_FRICTION_MULTIPLIER = 0.6;
export const WET_RESTITUTION_MULTIPLIER = 1.1;

// ---------------------------------------------------------------------------
// Player physics (§3.3)
// ---------------------------------------------------------------------------

export const PLAYER_CAPSULE_RADIUS = 0.3;
export const PLAYER_CAPSULE_HEIGHT = 1.8;
export const PLAYER_MASS = 75; // kg, baseline; scaled by build

export const MAX_SPEED_SPRINT = 8.5; // ~30.6 km/h — elite pace
export const MAX_SPEED_RUN = 6.0;
export const MAX_SPEED_JOG = 3.5;
/**
 * How far past a locomotion band's edge the player must travel before he is
 * allowed to LEAVE that band. (§3.3)
 *
 * The bands are re-derived from raw speed every tick with bare `>` comparisons,
 * which is fine everywhere except exactly ON a threshold — and one threshold is
 * not an arbitrary number: un-sprinted top speed IS `MAX_SPEED_RUN` (6.0), so a
 * player simply holding forward parks precisely on the Run/Sprint edge and stays
 * there. Measured in the running game, holding W on a clear pitch:
 *
 *   speed 6.00..6.00 (never moved)   anim state changed 51 times in 2.5 s
 *   4333434433343343333334334333333334333433333343344344344333343334...
 *
 * 20 flips a second, from floating-point noise in `sqrt(vx*vx + vz*vz)` alone.
 * Each one costs more than a clip swap: `stepAnim` resets `animBlend` to 0 on
 * every change, so the blend never finishes and the renderer holds the legs
 * permanently averaged between the run and sprint cycles — which is exactly the
 * "legs cancel toward mid-stance and the player glides" failure that
 * the renderer already describes from the other end.
 *
 * 0.35 m/s is wider than the noise by orders of magnitude and far narrower than
 * the 2.5 m/s gap between adjacent bands, so no real acceleration is delayed
 * perceptibly (0.04 s at 9 m/s²). Hysteresis only ever HOLDS the band the player
 * is already in, so it cannot invent a state he never entered.
 */
/**
 * ⚠ 0.35 IS A MEASURED OPTIMUM, NOT A GUESS — do not raise it to quiet the
 * flapping. Widening it always reduces gait changes and always buys that with
 * a clip that disagrees with the actual speed, i.e. foot-sliding. Swept on the
 * 5-minute AI telemetry against both failure modes:
 *
 *     hyst   transitions   flaps   GAIT-LIE ticks
 *     0.35        8567       573        7,297
 *     0.60        8157       542       10,685
 *     0.90        7770       503       17,381
 *     1.20        7435       450       23,134
 *
 * 21 % fewer flaps for 3.2x the sliding. A clip change blends; a Run cycle
 * played at walking pace does not.
 */
export const ANIM_BAND_HYSTERESIS = 0.35; // m/s
/**
 * Where the SPRINT clip takes over from the run clip. (§3.3, §6.5)
 *
 * Not `MAX_SPEED_RUN`, and that distinction is the whole point. `MAX_SPEED_RUN`
 * is a PHYSICS cap — it is exactly the speed a player reaches holding forward
 * without sprint — so using it as an ANIMATION edge puts the boundary precisely
 * on the speed the player spends most of the match parked at. Measured holding
 * W: speed 6.00..6.00, i.e. sitting on the edge indefinitely. Hysteresis stops
 * that flickering but cannot fix it, because whichever side a floating-point
 * blip lands on is then LATCHED — observed as the sprint cycle playing at a
 * 6.0 m/s jog.
 *
 * So the animation edge belongs BETWEEN the two speeds a player actually holds:
 * 6.0 (forward) and 8.5 (forward + sprint). The midpoint leaves 1.25 m/s of
 * clearance either side — far outside both the noise and the hysteresis band —
 * so each of the two speeds a player parks at sits unambiguously in one clip.
 *
 * Deliberately derived rather than typed as a bare 7.25: if either cap is ever
 * retuned this edge follows it and cannot silently re-collide.
 */
export const ANIM_SPRINT_SPEED = (MAX_SPEED_RUN + MAX_SPEED_SPRINT) / 2; // 7.25 m/s
export const MAX_SPEED_DRIBBLE = 5.5; // with ball at feet
// Holding Sprint WITH the ball lifts the dribble cap toward a knock-and-run pace:
// faster than a controlled dribble, short of a free sprint (8.5). The difference
// is paid in control — the ball is carried further ahead (nearer the reach limit)
// and a turn costs more — not in a teleport. Speed-aware dribbling (2C) keeps the
// lead inside foot reach at this pace so the ball stays catchable. (defect #3)
export const MAX_SPEED_SPRINT_DRIBBLE = 7.0; // m/s — sprinting with the ball
/**
 * ⚠ PACE RAISED (owner: "can you increase the gameplay speed a little bit?").
 *
 * Deliberately raised on ACCELERATION and BRAKING, not on top speed. The top
 * speeds above are already correct football — MAX_SPEED_SPRINT 8.5 m/s is
 * 30.6 km/h, genuine elite pace — so the game did not feel slow because players
 * ran slowly. It felt slow because they took ~1.06 s to reach sprint and a
 * comparable age to stop, and almost every touch of the ball happens during
 * that ramp rather than at the top of it.
 *
 * 8.0 → 9.0 m/s² cuts the stand-to-sprint ramp from ~1.06 s to ~0.94 s (−11%),
 * and 12.0 → 13.0 makes stopping and turning equally crisp. Raising DECELERATION
 * with it matters: acceleration alone makes a player feel eager but floaty,
 * because he still slides past everything he tries to stop at.
 *
 * ⚠ 9.0 IS A MEASURED CEILING, not a taste. 10.0 / 14.0 was tried first and broke
 * two acceptance properties — `shoulder-barge`'s "a dead-even clash unbalances
 * NOBODY" (a symmetric clash started producing a winner, i.e. an array-order
 * tie-break) and `match-metrics`' "11v11 and 5v5 produce watchable, attacking
 * football". Both were proven caused by this change by reverting it in place and
 * watching them go green. Anyone raising this further must re-check those two
 * FIRST: the barge one in particular is a fairness property, and the fact that a
 * pace bump can tip it suggests the dominance test (BARGE_DOMINANCE_RATIO) is
 * closer to its boundary than it looks.
 *
 * WHY NOT RAISE TOP SPEED — this is the load-bearing part. The whole speed
 * ladder is calibrated against these caps and would need re-deriving together:
 *   • the render locomotion ladder's sprint knot IS MAX_SPEED_SPRINT (8.5), and
 *     its clips' stride speeds were MEASURED off the asset — moving the cap
 *     without moving the knot brings back the foot-sliding that was just fixed;
 *   • DRIBBLE_KNOCK_MIN_SPEED (5.8) is the cliff between shepherding the ball
 *     and knock-and-run, and CARRY_TURN_TIGHT_SPEED (5.5) sits just under it —
 *     that pair is what stopped the ball being left behind on a sprint turn;
 *   • TACKLE_CLEAN_FELL_SPEED, BARGE_MIN_CLOSING_SPEED and POKE_VS_SLIDE_SPEED
 *     are all absolute speeds that would silently change meaning.
 * Raising the caps is a coherent re-tune of all of the above, not a one-line
 * edit, and it needs the animation owner's ladder in the same change.
 */
export const ACCELERATION = 9.0; // m/s², modulated by Acceleration stat
/**
 * ⚠ THE BRAKE WAS RE-SHAPED, 2026-08-13. READ THIS BEFORE TOUCHING THE THREE
 * NUMBERS BELOW — and read the block above it first, because that one is what
 * put the game in the state this fixes.
 *
 * Owner: *"add a stop anticipation when player suddenly stop, dont stop player
 * suddenly, show it really how a player stops"*.
 *
 * MEASURED BEFORE (zz-locoprobe, one player, ball parked, full sprint then a
 * released stick, sampled on the SIM TICK):
 *
 *     release 8.500 m/s -> 0 in 40 ticks = 0.667 s
 *     deceleration      13.000 m/s² on tick 1 and 13.000 m/s² on every tick after
 *     stop distance     2.70 m
 *
 * A flat 13.0 m/s² is 1.33 g. A real footballer's hard stop peaks at 5-8 m/s².
 * And a CONSTANT brake from the first tick to the last is not a body at all —
 * it is a rectangle. Both ends of it are wrong in the same way: there is no
 * gather at the top and no settle at the bottom, so the last 2 m/s vanish in
 * 0.15 s and the man arrives at rest as if switched off.
 *
 * It is also what made the animation unfixable rather than merely unfixed. The
 * render gait ladder DOES step Sprint->Run->Jog->Walk->stroll->Idle on the way
 * down, but the whole descent was 0.64 s, so each rung got ~0.15 s — less than
 * one cross-fade (`FADE` in playerAnimator.ts is 0.12-0.22 s). No clip can show
 * a stop the body does not take time to do.
 *
 * ── THE SHAPE ────────────────────────────────────────────────────────────────
 *
 *     brake(v) = SETTLE + (PEAK - SETTLE) x min(1, v / SETTLE_SPEED)
 *
 * i.e. full effort while there is real momentum to kill, easing toward a settle
 * over the last couple of m/s. That taper IS the "stop anticipation" in the
 * physics: the final rung of the ladder gets 0.18 s instead of 0.03 s, which is
 * a settle you can see rather than a switch.
 *
 * ⚠ IT IS A PURE FUNCTION OF SPEED, ON PURPOSE. The physically nicer model is a
 * brake that RAMPS IN over ~0.18 s (the gather-and-plant), and it was rejected:
 * "how long have I been braking" is per-player state that would have to live in
 * `WorldState`, be cloned, be hashed and be replicated, or reconciliation would
 * roll back to a stale ramp (D1). A stateless curve costs one multiply, cannot
 * desync, and — at a peak of 0.82 g rather than 1.33 g — the onset it does have
 * is already inside what a body does.
 *
 * MEASURED AFTER, same probe: 8.500 m/s -> 0 in 1.117 s, peak 8.000 m/s²,
 * stop distance 4.55 m.
 *
 * ── ⚠ IT APPLIES TO A RELEASED STICK ONLY, AND THAT SCOPE IS MEASURED ────────
 *
 * `DECELERATION` still exists, still reads 13.0, and is still what runs when a
 * player is SCRUBBING — turning through a residual angle, or held under a cap
 * that just dropped. Only a FREE STOP (no movement intent at all) takes the
 * curve. That is not tidiness, it is a test result:
 *
 *     dribble-weave.test.ts, sprint carry, curve applied to the scrub too:
 *       crossings 9 -> 0, ball width 0.215 m -> 0.000 (possession lost outright)
 *     ...and the walking carry wandered 0.132 m -> 0.524 m off its line.
 *
 * The carry-recovery model (`carryHasBrokenDown`, CARRY_RECOVER_*) works by
 * DROPPING the carrier's cap below his own ball's speed so the ball stops
 * receding and comes back to his boot. That mechanism is a scrub, and it was
 * measured and tuned against a 13 m/s² one: soften it and the ball never closes,
 * so a knock-and-run simply runs away from him. The owner's report is about the
 * stick being released — "when player suddenly stop" — so the curve is scoped to
 * exactly that and every tuned interaction stays bit-identical.
 *
 * ⚠ THE TWO ACCEPTANCE PROPERTIES THE BLOCK ABOVE NAMES WERE RE-RUN AND ARE
 * GREEN: shoulder-barge's "a dead-even clash unbalances NOBODY" and
 * match-metrics' "11v11 and 5v5 produce watchable, attacking football". They
 * are named there because RAISING pace tipped them; this change moves the same
 * dial the other way, which is the safe direction, but it is still the same
 * dial and the next person to touch it owes those two a re-run.
 *
 * ⚠ NOT A TASTE DIAL. Below ~6.0 the peak leaves the real band and defenders
 * start sailing past the man they are closing down; above ~9.0 the settle is
 * back inside a single cross-fade and the ladder blurs again. If this needs to
 * feel crisper, spend it on `STOP_SETTLE_SPEED` (a shorter settle) before
 * spending it on the peak.
 */
export const DECELERATION = 13.0; // m/s² — the SCRUB. Unchanged; see below.
/** The peak of the FREE STOP curve. */
export const STOP_DECELERATION_PEAK = 8.0; // m/s²
/** What is left of it as he comes to a stand. The settle, not the stop. */
export const STOP_DECELERATION_SETTLE = 4.5; // m/s²
/** Below this the free stop eases from the peak toward the settle. */
export const STOP_SETTLE_SPEED = 2.0; // m/s

/**
 * Momentum is mandatory (§3.3). Turn rate is an INVERSE function of speed —
 * a player at 8.5 m/s cannot turn 180° instantly. The skill layer (positioning,
 * feints, defensive body-shape) still emerges from this being speed-dependent.
 *
 * ⚠ SPRINT TURN RAISED for controllability (owner directive, 2026-07-23): the old
 * 90°/s sprint turn took ~2 s for 180° and read as "unresponsive / hard to control"
 * once movement became camera-relative WASD. Raised to 160°/s (≈1.1 s for 180°).
 * MEASURED, not blindly set to the owner's suggested 360: a fully-flat 600/360 let
 * the AI dribble out of everything and cut a 60s soak from ≥4 kicks to 3 (attacking
 * play collapsed — the exact "deletes the depth" failure the old comment warned of),
 * and even 450/220 tripped the support-passing invariant. 360/160 keeps close
 * control unchanged and the sprint turn 1.8× quicker while EVERY AI-match acceptance
 * soak (anim-integrity kick-rate, support-passing, match-metrics) still passes — so
 * the game keeps its momentum depth and the AI keeps attacking. Applies to human AND
 * AI (shared sim), per the guarantee that the controller can't tell them apart.
 * Do NOT flatten further without re-checking those three soaks.
 */
export const TURN_RATE_MAX_DEG = 360; // deg/s at low speed (close control / stationary)
export const TURN_RATE_MIN_DEG = 160; // deg/s at sprint

/**
 * ── THE BODY LAGS THE AIM (owner, 2026-08-13: "turn him with animations not
 *    like a bot rotating") ──────────────────────────────────────────────────
 *
 * The two numbers above are a CEILING and nothing else, and until now the
 * facing sat on that ceiling for the whole of every turn. Measured before
 * (zz-locoprobe, standing, aim flipped 180 deg):
 *
 *     tick 1   360.0 deg/s      tick 15  360.0 deg/s
 *     tick 2   360.0 deg/s      ...      360.0 deg/s
 *     tick 30  360.0 deg/s      tick 31    0.0 deg/s
 *
 * A rectangle: full rate on the first tick, full rate on the last, nothing
 * after. That is a turret, exactly as reported — it is not that 360 deg/s is
 * unreachable (a footballer can pivot 180 deg in half a second), it is that
 * nothing about the way he gets there is a body.
 *
 * So the rate is now ALSO proportional to how far he still has to go:
 *
 *     rate = clamp(TURN_APPROACH_GAIN x |residual|, floor, turnRateFor(speed))
 *
 * Two things fall out, and the second is the one that matters in play:
 *
 *   1. THE ARRIVAL IS A SETTLE, not a stop. The last ~40 deg is shed on an
 *      exponential with a 1/GAIN = 0.125 s time constant instead of running at
 *      full rate into a wall.
 *   2. THE BODY LAGS A MOVING AIM. This is the whole of the turret look in
 *      real play, and it is not visible in a 180 deg step test at all: a mouse
 *      moves the aim CONTINUOUSLY, the old law tracked it at up to 360 deg/s,
 *      and so the shoulders were welded to the camera with zero lag. With a
 *      gain the shoulders trail the look by residual = yawRate / GAIN — about
 *      21 deg at a brisk 170 deg/s pan — which is what a turning body does and
 *      what gives the renderer something to draw.
 *
 * ⚠ THE CEILING IS DELIBERATELY UNTOUCHED. 360/160 is a MEASURED pair (see the
 * block above: 600/360 and 450/220 both broke AI acceptance soaks), and it is
 * still the cap here — a turn never gets FASTER than it was, only smoother into
 * and out of. Anyone tempted to lower 360 as well should first check whether
 * the gain has already done what they wanted.
 *
 * GAIN = 8 /s: the rate is at the ceiling for any residual over 45 deg, so
 * every large turn is unchanged in its middle and changed only in its last
 * 0.15 s. A smaller gain starts eating into ordinary turning; a larger one
 * shrinks the settle back toward the rectangle.
 *
 * ⚠ THE FLOOR IS WHAT MAKES IT TERMINATE. A rate proportional to the residual
 * approaches the target asymptotically and never reaches it, which would leave
 * every player permanently a hair off his aim and every "is he facing it" test
 * flaky. Below `TURN_RATE_FLOOR_DEG x dt` (0.333 deg) the residual is closed
 * outright on the next tick.
 */
export const TURN_APPROACH_GAIN = 8.0; // 1/s
export const TURN_RATE_FLOOR_DEG = 20; // deg/s

/** Stamina (§3.3) */
export const STAMINA_MAX = 100;
/**
 * ── THE BOOST ECONOMY (owner, 2026-08-13) ──────────────────────────────────
 *
 * Owner's spec, verbatim in shape: the bar lasts a minute of running, key `1`
 * spends it faster for more pace, pressing `1` twice burns faster still, and it
 * only recharges when he is NOT running — two minutes to fill.
 *
 * Every rate below is written as `STAMINA_MAX / seconds` rather than as a fitted
 * decimal, so the DURATION is the thing being specified and the rate is derived.
 * Change the seconds and the arithmetic follows; nobody has to re-divide 100 by
 * anything to know what the number means.
 *
 *     running, no boost   100/60  = 1.667 /s   bar lasts  60 s
 *     boost tier 1        100/120 = 0.833 /s   bar lasts 120 s
 *     boost tier 2        100/60  = 1.667 /s   bar lasts  60 s
 *     recharge             100/120 = 0.833 /s   full in   120 s
 *
 * ⚠ RAISED 2026-08-18 (owner: "with boost 1 player can run for 2 min and with
 * boost 2 for 1 min") FROM 30s/15s. Same 2:1 ratio between the tiers as
 * before — tier 2 still burns twice as fast as tier 1 — just both held 4x
 * longer, because 30s and 15s read as barely-there next to the bar's own 60s
 * unboosted baseline: at the old numbers, activating tier 1 to go FASTER also
 * meant running out THREE TIMES sooner than not boosting at all, which made
 * the boost a burst button, not something to run a possession on.
 *
 * ⚠ THIS REPLACES A MUCH HARSHER MODEL and the difference is the point. The old
 * `STAMINA_DRAIN_SPRINT` of 6.0 emptied the bar in 16.7 s and `STAMINA_REGEN_WALK`
 * of 2.0 refilled it in 50 s, so a player was fatigued almost permanently and had
 * no idea why — it was reported as a bug twice ("he is going slow after some
 * point why?"). Running is now 3.6x more sustainable and the recharge 2.4x
 * slower, which turns a background tax into a decision.
 */
export const STAMINA_DRAIN_RUN = STAMINA_MAX / 60;
export const STAMINA_DRAIN_BOOST_1 = STAMINA_MAX / 120;
export const STAMINA_DRAIN_BOOST_2 = STAMINA_MAX / 60;
/**
 * Recharge, and the pace at or below which it happens.
 *
 * ⚠ THE GATE IS A SPEED, NOT A BUTTON. "Only recharges if he doesn't run" has to
 * be judged on what he is DOING, not on what he is holding — a man pinned
 * against a defender at 0.2 m/s with sprint held is standing still whatever the
 * key says. `WALK_SPEED_THRESHOLD` (1.4 m/s) is the same edge the anim bands and
 * the old regen already used, so idle and walk refill, and a jog does neither:
 * it neither drains nor fills, which gives a player a pace he can hold forever
 * and is the reason the gate is not simply "not sprinting".
 */
export const STAMINA_RECHARGE = STAMINA_MAX / 120;
/**
 * What the boost BUYS — a multiplier on the speed cap, per tier.
 *
 * ⚠ THESE ARE DELIBERATELY MODEST AND THE CEILING IS WHY. `M_Sprint`'s natural
 * speed is 5.794 and the animator's `RATE_MAX` is 1.45, so the sprint clip
 * covers to 8.40 m/s — and `MAX_SPEED_SPRINT` of 8.5 already pins that clamp.
 * A cap past 8.5 with today's clips slides the feet, which is the exact defect
 * removed on 2026-08-13 (the gaits were resampled 21 Hz -> 60 Hz to kill a
 * once-a-stride tick, and the gait selector was moved off a per-frame position
 * difference). Spending that back on a bigger number would be a poor trade.
 *
 * So tier 1 and tier 2 lift the RUN cap toward the sprint cap rather than past
 * it: 6.0 -> 7.8 -> 8.5 un-sprinted (raised 2026-08-13 on the owner's "for
 * Boost 1 and 2 increase the speed"), and with Shift held both still clamp at
 * 8.5. The boost is worth most to a player who is NOT holding sprint, which is
 * the common case, and it costs nothing in foot slide.
 *
 * ⚠ 1.45 IS THE CEILING THESE CLIPS ALLOW, NOT A TASTE DECISION. Tier 2 now
 * takes the un-sprinted cap to exactly MAX_SPEED_SPRINT. Anything past that has
 * no clip behind it: the feet would slide, which the same owner ruled out in
 * the same session ("never fucking never slide only go by foot"). The two
 * instructions meet at 8.5 m/s and the sprint stride is what has to move next.
 *
 * THE FASTER TOP GEAR IS A SEPARATE PIECE OF WORK and is the owner's stated
 * preference: re-author the sprint with a longer stride (the path
 * `tools/anim/measure/build-sprint.mjs` already took once, to a 1.70 m step) so
 * its natural speed rises with the cap, then raise `MAX_SPEED_SPRINT` and
 * re-derive everything calibrated against it — `DRIBBLE_KNOCK_MIN_SPEED` (5.8),
 * `CARRY_TURN_TIGHT_SPEED` (5.5), `ANIM_SPRINT_SPEED`, `TACKLE_CLEAN_FELL_SPEED`,
 * `BARGE_MIN_CLOSING_SPEED`, `POKE_VS_SLIDE_SPEED`. Until that lands these
 * numbers make the mechanic playable without lying about what the clips can do.
 */
export const BOOST_SPEED_MULTIPLIER: readonly number[] = [1.0, 1.3, 1.45];
/** Superseded by the four above; kept only so nothing silently reads a stale
 *  name. Delete once no import remains. */
export const STAMINA_DRAIN_SPRINT = 6.0; // per second
export const STAMINA_DRAIN_JOG = 0.5;
export const STAMINA_REGEN_WALK = 2.0;
/** Below this, MAX_SPEED scales by 0.75 + 0.25·(stamina/30) */
export const STAMINA_FATIGUE_THRESHOLD = 0;
// ^ was 30 - nearly a third of the bar showed the hands-on-knees pose while
// standing, which read as "the idle animation is broken" (owner, 2026-08-14:
// "show fatigue animation only when stamina is under 5 and show idle other
// time", then "dont ever show fatigue animation unless the stamina is 0").
// Zero - the bar must be EMPTY, the same trigger as fatigueCeiling; at any
// other value a standing player is simply standing. Purely visual - the one
// speed penalty in the game still triggers at exactly zero (fatigueCeiling).
export const STAMINA_FATIGUE_FLOOR = 0.75;

/** Soft shoulder-to-shoulder separation. Never hard-lock two capsules. (§3.3) */
export const PLAYER_SEPARATION_IMPULSE = 4.0;

// ---------------------------------------------------------------------------
// Ball-player interaction (§3.4)
// ---------------------------------------------------------------------------

/** Client predicts the ball only inside this radius. (§2.7) */
export const POSSESSION_RADIUS = 1.2;

/**
 * The furthest a player can PHYSICALLY play the ball — centre-to-centre. (§3.4)
 *
 * This exists because POSSESSION_RADIUS was doing two incompatible jobs.
 * Possession is BOOKKEEPING: who nominally owns the ball, which drives the
 * speed cap, tackle bonuses and AI decisions. 1.2m is a fine answer to that.
 * But it was ALSO gating physical contact — the dribble touch and the strike —
 * and 1.2m is a terrible answer to *that*:
 *
 *   player capsule radius  0.30
 * + ball radius            0.11
 * = surfaces touch at      0.41m
 *
 * so at 1.2m the player is ~0.8m of clear air away from the ball. Measured
 * live before this split: 96% of all ball-velocity changes happened with NOBODY
 * touching the ball, including 8 m/s strikes strucĸ from 1.06–1.20m and a
 * carry driven with the nearest player 3.44m from the ball's surface. That is
 * the "ball moves before anyone touches it" report, exactly.
 *
 * 0.7m ≈ contact distance plus a foot's worth of leg extension: close enough to
 * read as a real touch, far enough that you can still stretch for a ball.
 *
 * NOTE: the honest ceiling on how good this can look is ball-contact IK (§6.5 —
 * "IK the foot to the ACTUAL ball position at the contact tick ... the single
 * highest-value animation feature"), which is still a known gap (DECISIONS D9).
 * Until that lands, the leg is wherever the clip put it, so a touch at any
 * distance is approximate. This constant makes the SIM honest; the IK makes the
 * PICTURE honest. Do not raise it to paper over the missing IK.
 */
export const BALL_PLAY_REACH = 0.7;

/** Ball drifting outside this while possessing triggers a corrective touch. (§3.4) */
export const TOUCH_RADIUS = 0.45;
/** Higher speed = larger touches = less control. (§3.4)
 *  SPEED_FACTOR 0.6→0.78 (live feedback "better running with ball"): sprint
 *  touches now knock the ball visibly ahead in a push-and-chase rhythm
 *  instead of shepherding it glued to the boot — probe-verified the carrier
 *  still never loses it through a sprint 90° cut. */
export const DRIBBLE_TOUCH_IMPULSE_BASE = 2.2;
export const DRIBBLE_TOUCH_SPEED_FACTOR = 0.78;
/**
 * Ceiling on how hard one tick of contact may accelerate the ball (m/s²).
 * This is what makes the dribble touch a TOUCH and not a tractor beam: the ball
 * is accelerated toward the carry velocity, not assigned it, so a large
 * correction takes several ticks of real contact to pay out and the ball keeps
 * its own momentum the instant it rolls out of BALL_PLAY_REACH. (§3.4)
 */
export const DRIBBLE_TOUCH_MAX_DV = 45;
/** CCD is mandatory for the ball — at 30 m/s it moves 0.5m/tick and tunnels. (§3.4) */
export const BALL_CCD_ENABLED = true;

// ---------------------------------------------------------------------------
// Ball model additions — owned by ball.ts (§3.2 aero/ground, §3.4 possession +
// dribble touch, §4.2 passing, §4.3 shooting). Appended by the ball agent.
// ---------------------------------------------------------------------------

/**
 * Possession = ball at your feet (§3.4). A ball is possessed only when it is
 * within POSSESSION_RADIUS, below reach height, AND slow RELATIVE to the player
 * — the relative-speed gate is what stops a just-passed or just-shot ball from
 * being instantly re-possessed by the kicker on the very next tick.
 */
export const POSSESSION_MAX_HEIGHT = 1.0; // m — knee/thigh; a header is not control
export const POSSESSION_CAPTURE_REL_SPEED = 5.0; // m/s of ball-vs-player closing speed
/** Hysteresis so possession does not flicker between two near-equidistant players. */
export const POSSESSION_STICKY_MARGIN = 0.25; // m
/**
 * §1 CONTROLLED envelope — the radius/rel-speed a CONTROLLING carrier keeps the
 * ball within, wider than the capture gate a challenger needs. A dribble is
 * repeated touches, not a rigid link: a knock-and-run pushes the ball ahead
 * toward the boundary while it is still clearly his. The GATHER below hard-caps
 * how far it runs (so this never becomes a runaway); this just keeps possessedBy
 * with him through that gather zone. A real KICK clears possessedBy in
 * resolveKicks (no incumbent next tick → genuine release); an opponent still
 * needs the tight challenge radius + a valid steal (§14) to take it. */
export const POSSESSION_CONTROL_RADIUS = 2.35; // m — carrier's keep-radius (> gather cap)
export const POSSESSION_CONTROL_REL_SPEED = 12.0; // m/s — a knock keeps; a real kick (15-25) frees

/**
 * First-touch trap (receiving). A loose ball travelling FASTER than the capture
 * gate (so an on-pace pass is untouched — that already collects) and heading
 * roughly AT a nearby player gets a cushioning touch that bleeds its speed toward
 * a controllable pace and hands that player the gather, so possession transitions
 * on the touch instead of the ball having to roll to a near-stop first. This is
 * the literal first touch — a physical velocity damp over a short contact window,
 * NOT a teleport or a parent. Deliberately gated tight so it does not become a
 * magnet: only above the capture gate, only inside FIRST_TOUCH_RADIUS, only when
 * the ball is genuinely aimed at the player (alignment gate), and never the
 * striker re-trapping his own just-played ball (cooldown). A very hard ball
 * (> FIRST_TOUCH_MAX_SPEED) is only partly cushioned — a difficult touch. (§ receiving)
 */
export const FIRST_TOUCH_RADIUS = 1.7; // m — just outside the tight capture gate
/** Only cushion a ball arriving FAST enough that it would otherwise blow past the
 *  receiver and have to be chased down. A moderate pass (≤ this) already rolls to
 *  the feet and is collected there by the ordinary gate — leave it exactly alone,
 *  so "a pass lands at his feet" keeps its precision. */
export const FIRST_TOUCH_MIN_SPEED = 8.0; // m/s at the receiver
export const FIRST_TOUCH_MAX_SPEED = 17.0; // m/s — a rocket is trapped only partially
export const FIRST_TOUCH_TARGET_REL_SPEED = 3.0; // m/s — cushion to comfortably under the gate
/**
 * Per-tick cushion cap (m/s², scaled by the firstTouch stat) — how HARD a
 * player can take pace off a ball, as opposed to how far he can reach for it.
 *
 * Raised 55 → 110 to close the 5-8 m/s receive dead zone (see applyFirstTouchTrap)
 * WITHOUT widening any reach. At 55 the cushion bled under 1 m/s per tick, and
 * MEASURED on pass-quality's `first-time` cell the ball is inside the 0.7 m boot
 * reach for only one or two ticks — so a 6.9 m/s arrival stayed above the 5 m/s
 * capture gate and was collected 0/3. At 110 the same two ticks take ~3.7 m/s
 * off it and it is collected 3/3.
 *
 * The alternative — reaching FURTHER for the marginal ball — was tried
 * (FIRST_TOUCH_STRETCH_REACH, 1.15 m) and reverted: over eight seeded 120 s
 * AI-vs-AI matches it locked possession (ownership 51-80 % → 84-89 % of live
 * play on two seeds, on-ball decisions 572/559 → 89/55). Reach decides which
 * contests you win and is load-bearing for the whole match; cushion strength
 * only decides how well you take a ball you were already going to reach.
 */
export const FIRST_TOUCH_MAX_DV = 110.0; // m/s², the per-tick cushion cap (× firstTouch stat)
export const FIRST_TOUCH_ALIGN_MIN = 0.72; // cos — ball must head within ~44° of the player
export const FIRST_TOUCH_COOLDOWN_TICKS = 8; // the striker can't re-trap his own pass
/**
 * §1 GATHER touch — while controlling the ball, it must not run past the control
 * envelope. As it approaches DRIBBLE_GATHER_RADIUS the carrier checks its pace (a
 * gather), bleeding the AWAY component of its velocity, scaled from 0 at
 * DRIBBLE_GATHER_START to full at the boundary. This is NOT a tractor beam: only
 * the ball's velocity is nudged (never its position), gently and distance-scaled,
 * so it decelerates into control and the carrier catches up for the next touch —
 * the ball physically cannot exceed the envelope, and never teleports to the foot. */
export const DRIBBLE_GATHER_START = 0.6; // m — begin easing the pace off past here
// (0.8 → 0.6, 2026-07-25.) The gather is a FOOT check on your own knock, so it has
// to begin while the ball is still inside the boot's reach (BALL_PLAY_REACH 0.7
// centre-to-centre) — at 0.8 it only started once the ball was already beyond any
// possible contact, which meant the knock-and-run cycle's whole outer half lived
// out of touch range. Starting it inside reach shortens that untouchable window
// (measured: sprint carry 0.859 → 0.835 m off the boot, touches 7.2 → 9.3 /s) and
// tightens every hard cut (dribble-corridor owned-perp 30° 1.14 → 0.45 m, 90° 1.09
// → 0.83 m; the 180° reversal trails 1.37 → 1.24 m instead of 1.45 m).
export const DRIBBLE_GATHER_RADIUS = 1.9; // m — hard control-envelope boundary
export const DRIBBLE_GATHER_STRENGTH = 1.0; // full recession bled at the boundary (velocity cap, not a snap)
/**
 * The gather keeps acting through the brief blip where a knock-and-run runs the
 * ball just past the tight capture gate — it is keyed on ball.gatherOwner (which
 * survives that blip), NOT on live possession, so it does not die the instant the
 * ball escapes (the chicken-and-egg the naive `possessedBy === id` version had).
 * A KICK clears gatherOwner (applyStrike) so a pass/shot flies free; a rival
 * winning the ball reassigns it. This distinguishes a KNOCK from a KICK by intent
 * (who owns the loose ball), not by an unreliable recession-speed threshold — a
 * hard turn recedes as fast as a soft pass, so speed alone cannot tell them apart.
 */
/** Beyond this player→ball distance the ball is genuinely GONE, not a knock: the
 *  gather stops, so a stale owner can never retrieve a ball from across the pitch.
 *  Sits just past the envelope; a controlled knock is capped well inside it, so
 *  this only excludes balls that were already lost (a heavy touch, a deflection). */
export const DRIBBLE_GATHER_MAX_DIST = 2.9; // m
/**
 * When a knock has escaped and gone LOOSE (owner keeps the gather, but no longer
 * possesses), a bare pace-match would hold the ball at a fixed gap the carrier
 * never closes — he has turned and will not run back onto it. So a loose gather is
 * instead a capped REACHING touch toward a control point a step ahead of him
 * (§1's "capped impulse toward the next control point"): it draws the ball back
 * onto the running line so the next touch re-collects it. Capped per tick, so it
 * is a touch, not glue, and it never sets the ball's position. */
export const DRIBBLE_GATHER_LEAD = 0.62; // m — the control point sits this far ahead of the feet (0.5→0.62: the ball rides ahead of the boot, not under it — kills the clipping-through-shins look)
export const DRIBBLE_GATHER_CLOSE_GAIN = 7.0; // 1/s — spring pulling the loose ball onto the control point
export const DRIBBLE_GATHER_CLOSE_MAX_DV = 45; // m/s² — per-tick cap (a touch, never a snap)
/** A ball moving faster than this RELATIVE to its owner is not being dribbled — it
 *  is a played ball (pass/shot/clearance) or a heavy deflection, so the gather does
 *  not act on it. A KICK already clears gatherOwner (applyStrike / restart release),
 *  so this is a backstop: it sits well above any knock-and-run touch (~pace + 2 m/s,
 *  and even a hard-turn orbit stays ~10) and well below a shot or firm pass (20-30). */
export const DRIBBLE_GATHER_MAX_REL_SPEED = 14; // m/s
/**
 * §14 — the front-arc gate for wresting the ball off a controlling opponent.
 * A defender may only take the ball from a shielding carrier by a valid football
 * interaction: a slide, a standing tackle, or a genuine FRONTAL interception
 * where the defender cuts the ball off from in front. This is the cosine of the
 * half-angle of that front arc measured from the CARRIER's facing toward the
 * challenger: 0.5 ⇒ a 120° frontal cone. A challenger in the rear/side ~240°
 * (a back or shoulder brush) cannot steal by body contact alone — the carrier
 * shields it. Loose balls and tackles are unaffected; only the proximity-capture
 * "walked into him and it was mine" path is gated. */
/**
 * A steal of a controlled ball must come from the carrier's front arc. ⚠ NO
 * LONGER SUFFICIENT ON ITS OWN — see POSSESSION_STEAL_CLOSING_SPEED.
 */
export const POSSESSION_STEAL_FRONTAL_DOT = 0.5;
/**
 * ...and the challenger must actually be MOVING AT THE BALL, m/s along the
 * line to it. This is the half that was missing (Phase-1 Commit 1): a defender
 * standing perfectly still in front of a dribbler satisfies the front-arc
 * test, so possession transferred to a player who pressed nothing — 12 out of
 * 12 on the duel probe. A real interception is a movement; a body in the way
 * is a collision, and collisions belong to the physics, not to this gate.
 *
 * Low enough that a genuine step across the line qualifies, high enough that
 * drift, jostling and a defender being shoved by the carrier do not.
 */
export const POSSESSION_STEAL_CLOSING_SPEED = 1.5;

/**
 * Strike-offset spin (§3.2 "the single highest-value physics decision"). A kick
 * is an impulse applied at a point offset `d` from ball centre; the spin is
 * Δω = (d × impulse)/I, so it emerges from WHERE you hit the ball. The offset is
 * back-solved from the intended spin and clamped to a fraction of the radius, so
 * a weak kick physically cannot be given elite spin — the clamp does that for
 * free rather than a special case.
 */
export const STRIKE_OFFSET_MAX_FRAC = 0.5; // |d| ≤ this × BALL_RADIUS
/** Reach ceiling for striking a ball you don't fully control — enables volleys. (§4.3) */
export const STRIKE_MAX_HEIGHT = 1.3; // m
/** Mouse-wheel lift bias (§4.1) → launch angle for a shot, at full deflection. (§4.3) */
export const SHOT_LIFT_ANGLE_MAX_DEG = 32;
/** Lift bias also sets back/topspin: +lift → backspin float, −lift → topspin dip. (§4.3) */
export const SHOT_LIFT_SPIN = 60; // rad/s at |liftBias| = 1
/** Driven pass is low and fast with minimal spin. (§4.2) */
export const DRIVEN_PASS_BACKSPIN = 6; // rad/s
/** A through-ball leads the runner; cap how far ahead the lead point may sit. (§4.2) */
export const THROUGH_BALL_LEAD_CAP_S = 1.5; // s of the target's own velocity
/**
 * §4 called/to-feet pass — reach the RECEIVER'S CONTROL FOOT, not the ground he
 * has already left. A single dist/speed lead under-shoots (the runner keeps
 * moving through the ball's flight; measured ~2 m short of a 6 m/s runner), so the
 * intercept is iterated to convergence, then biased ONE STEP ahead onto his
 * control foot so he takes it in stride rather than checking back for it.
 */
export const PASS_LEAD_ITERS = 3; // fixed-point iterations converging the intercept
export const PASS_LEAD_CONTROL_FOOT_STEP = 0.8; // m ahead, into the runner's stride
/**
 * Ceiling on the pass LEAD, as a fraction of the straight-line distance to the
 * man — the space cap that sits alongside THROUGH_BALL_LEAD_CAP_S's time cap.
 *
 * Needed once the lead started using the real (decelerating) ball: the
 * fixed-point intercept is only stable while the ball's effective speed over
 * the flight stays above the runner's, and at range it no longer does, so a
 * fast lateral runner walked the solution out to the time cap. MEASURED over 12
 * seeds with only the time cap in force, the launch bearing's deviation from
 * the requested aim went p90 33.6 -> 43.0 deg and p99 61.6 -> 147.9 deg: a pass
 * could leave almost backwards from where it was pointed because the lead point
 * had swung past the passer.
 *
 * 0.6 bounds the bearing swing at asin(0.6) = 37 deg in the worst case and
 * still allows a genuine through ball — a striker 20 m away at 8 m/s gets up to
 * 12 m of lead, which is what a 1.5 s flight actually needs.
 */
export const PASS_LEAD_MAX_FRAC = 0.6;
/** A lofted pass from this far out toward the touchline is treated as a cross. (§4.2) */
export const CROSS_WIDE_FRAC = 0.6; // fraction of the half-width
/**
 * Pass/shot error grows under pressure (§4.2, §4.3). An opponent this close is
 * full pressure; the error multiplier rises by PRESSURE_ERROR_WEIGHT at most.
 */
export const PRESSURE_RADIUS = 4.0; // m
export const PRESSURE_ERROR_WEIGHT = 1.0;
/** Shot error grows with range: distanceFactor = distToGoal / this. (§4.3) */
export const SHOT_DISTANCE_ERROR_REF = 25; // m
/** Shooting off-balance (at pace) widens the cone: +weight × (speed/sprint). (§4.3) */
export const SHOT_OFFBALANCE_ERROR_WEIGHT = 0.5;

/**
 * Ground contact (§3.2 pt 5). Below this vertical speed after a bounce the ball
 * is treated as resting: it stops micro-bouncing, `grounded` latches, and
 * rolling resistance + fast ground spin decay take over.
 */
export const BALL_GROUND_REST_SPEED = 0.6; // m/s
/** Skin width the ball is held off a surface after a CCD sweep, to avoid re-penetration. */
export const BALL_CONTACT_SKIN = 0.001; // m
/**
 * Head-rest destabilizer: a slow ball contacting a player capsule from ABOVE
 * (upward contact normal) is a stable solver equilibrium — observed live: a
 * ball balanced on a defender's head for 100+ seconds while nobody could play
 * it (possession caps at 1.0m; a grounded header tops out at ~1.65m; the AI
 * seek target sat exactly under the ball, so the carrier of the head never
 * moved). Real heads are not shelves — a slow top-contact gets a horizontal
 * shove away from the capsule axis so it rolls off within a few ticks.
 */
export const BALL_HEADREST_NY_MIN = 0.55; // contact normal "from above" gate
export const BALL_HEADREST_MAX_SPEED = 1.5; // m/s — only near-resting balls
export const BALL_HEADREST_SHOVE = 0.8; // m/s per contact tick
/**
 * Each shove is rotated off the radial by this angle, signed by the contact
 * player's slot parity — so a ball wedged in the V between TWO heads gets a
 * common tangential push instead of two cancelling radials (observed live:
 * "ball stuck on 2 players' heads"). NY_MIN dropped 0.7→0.55 with it: wedge
 * contacts arrive shallower than a clean top-rest.
 */
export const BALL_HEADREST_ROT_RAD = 0.6; // ~34°
/**
 * Ball must be at least this high (m) to be a genuine head/chest REST — below
 * it, a slow contact is a boot/shin touch that must not be shoved, or the ball
 * jitters near feet every tick ("ball glitches near players", live feedback).
 * A capsule tops out ~1.8m; knee height ~0.5m; 0.95 cleanly separates a
 * resting-on-top ball from any foot-level contact.
 */
export const BALL_HEADREST_MIN_Y = 0.95;
/** Nets absorb almost all pace — a real verlet net is a graphics-layer job (§6.7). */
export const NET_VELOCITY_DAMPING = 0.35;

// ---------------------------------------------------------------------------
// Passing (§4.2)
// ---------------------------------------------------------------------------

export const PASS_CHARGE_MIN_S = 0.15;
export const PASS_CHARGE_MAX_S = 1.0;
export const GROUND_PASS_MIN_SPEED = 8;
// Retuned 25 → 20 (Phase 3B) against the honest roll (the 2C fix stopped the
// ground eating ~15% of every pass): charge now maps distance — min ≈ 6 m,
// mid ≈ 12 m, max ≈ 20 m arriving trappable (~5 m/s at the receiver). 25 m+
// on the ground is the DRIVEN pass's job (Sprint+pass below); at 25 the old
// max-charge ball reached a 12 m receiver at 18.7 m/s and ran out.
export const GROUND_PASS_MAX_SPEED = 24; // §4: raised so a firm long pass reaches the man (was 20, fell short at 28 m)
export const GROUND_PASS_BACKSPIN = 12; // rad/s, slight
export const DRIVEN_PASS_MIN_SPEED = 20;
export const DRIVEN_PASS_MAX_SPEED = 30;
// ⚠ RAISED 2026-08-15 from 25/40. The owner held Q, clicked, and reported
// "ball is not going in the air" - and he was RIGHT about what he saw even
// though the mechanism worked: measured in the running game, a 25 deg lofted
// pass peaked 1.81 m against a grounded pass's 0.11 m. Over a 10-15 m pass
// that arc is nearly flat on screen; it clears nobody and reads as a normal
// pass with a slight hop. 38-52 deg puts the same pass CLEARLY over a
// defender's head, which is the only reason to press the modifier at all.
export const LOFTED_PASS_MIN_ANGLE_DEG = 38;
export const LOFTED_PASS_MAX_ANGLE_DEG = 52;
export const CROSS_MIN_ANGLE_DEG = 35;
export const CROSS_MAX_ANGLE_DEG = 45;
/** Assisted aim snaps to the nearest teammate within this cone. (§4.2) */
export const ASSIST_CONE_DEG = 25;
// ── Pass-target selection (§4.2, Phase 3B) ─────────────────────────────────
// Every human pass resolves to the most sensible teammate near the aim
// (pass-target.ts): alignment dominates (the human's expressed intent), the
// lane and the receiver's space break ties, offside-positioned receivers are
// excluded. The chosen target also scales the power (soft short, strong long)
// and is what the 3C indicator draws — the same shared function both times.
/** Half-angle of the pass-assist selection cone. 60° ⇒ a ~120° cone in front of
 *  the player, so the receiver system tolerates imperfect aim (the arrow gives
 *  general intent, not a pixel-exact bearing) — directive §10. */
export const PASS_ASSIST_CONE_DEG = 60;
/** Target hysteresis: a NEW candidate must beat the currently-highlighted one by
 *  this score margin to take the highlight, so it does not flicker between two
 *  near-tie teammates as the aim wobbles (directive §13). */
export const PASS_ASSIST_HYSTERESIS = 0.12;
export const PASS_ASSIST_LANE_RADIUS = 1.5; // m clearance for a fully-open lane
export const PASS_ASSIST_ALIGN_WEIGHT = 1.0;
export const PASS_ASSIST_LANE_WEIGHT = 0.35;
export const PASS_ASSIST_FREE_WEIGHT = 0.25;
// Fit of "launch speed that arrives trappable at distance d" against the pass
// matrix on the 3B curve (measured: ~6m→11, 12→14.7, 20→20, 28→25.5 m/s —
// linear fits the drag-crisis band well). Charge swings the result ±15% so the
// human still over/underhits on purpose.
// Base/per-metre kept where a pass ARRIVES CONTROLLABLE (a stationary man can trap
// and latch it — measured; firming these up made a 12 m ball arrive too hot to
// control). The §4 "too slow" complaint was a LONG-ball REACH bug, not short/mid
// pace: a 28 m assist fell short because the ground-pass ceiling clamped it to
// 20 m/s and friction stopped it — fixed by lifting the ceiling (below), which
// only affects the long balls that actually hit the clamp.
export const PASS_ASSIST_SPEED_BASE = 6.5; // m/s at zero distance
export const PASS_ASSIST_SPEED_PER_M = 0.68; // m/s per metre of target distance
/**
 * What the charge bar is worth once the assist has fitted the power to the man:
 * the pass is `fit + (GROUND_PASS_MAX_SPEED − fit) × HEADROOM × t`. A TAP is the
 * fit exactly — the weight tuned to arrive under POSSESSION_CAPTURE_REL_SPEED —
 * and charging spends a fraction of whatever pace is LEFT between that and the
 * hardest ground pass in the game.
 *
 * ── WHY IT IS SHAPED THIS WAY, in three measured steps ─────────────────────
 *
 * 1. The bar was worth almost nothing. Human RMB pass, charge swept 0.15 → 1.00 s
 *    on the old symmetric ±15 % swing (sim/kick-feel.test.ts, section D):
 *
 *      receiver at            8 m        15 m        25 m     no teammate
 *      min charge         10.01       14.05       19.80            7.87 m/s
 *      max charge         13.59       19.03       23.79           23.79 m/s
 *      span                3.58        4.98        3.99           15.93 m/s
 *      dead bar               0 %         0 %        40 %             0 %
 *
 *    With a man in the cone the bar delivered 22-31 % of the range it delivers
 *    without one, and beyond ~18 m the fit already sat on GROUND_PASS_MAX_SPEED
 *    so the top 40 % of the bar did NOTHING.
 *
 * 2. Widening it SYMMETRICALLY was tried and rejected: at ±25 % the most common
 *    input in the game got 15 % weaker, and pass-quality's d12-stationary at
 *    minimum charge went from arriving 1.19 m out and collected 3/3 to dying
 *    1.85 m short and collected 0/3. A tap that no longer reaches the man is a
 *    worse defect than a bar that does nothing. Hence the tap IS the fit.
 *
 * 3. A one-sided MULTIPLIER (`fit × (1 + gain·t)`) was tried and rejected too,
 *    and this is the subtle one. It clips: `fit` is already 23.5 m/s at a 25 m
 *    target against a 24 m/s ceiling, so the multiplier pushed a whole band of
 *    mid-range passes up ONTO that ceiling, where they all left at 24 m/s and
 *    ran ~39 m past everybody. MEASURED over 12 seeds x 240 s, passes that
 *    travelled over 20 m before the next touch:
 *
 *      gain   overall   <10 m   10-20 m   >20 m   >20 m median travel
 *      0.0      62.9 %  73.5 %   81.2 %  44.5 %          33.2 m
 *      0.25     59.6 %  75.2 %   81.0 %  35.4 %          41.1 m
 *      0.40     58.6 %  72.2 %   76.9 %  39.0 %          37.9 m
 *
 *    The long-ball column is the clipping. Spending HEADROOM instead of
 *    multiplying cannot clip by construction, and it puts the pace where a human
 *    can feel it — a short pass has 12 m/s of headroom, a 25 m pass has none,
 *    which is also just true: 24 m/s IS the hardest ground pass, and harder than
 *    that is the DRIVEN pass's job (Sprint + Pass, 20-30 m/s).
 *
 * 4. The fraction itself, measured the same way (12 seeds x 240 s):
 *
 *      headroom   overall   <10 m   10-20 m   >20 m     bar span at 8 m / 15 m
 *      (baseline)  58.9 %  57.6 %   79.3 %  48.5 %       3.58 / 4.98 m/s
 *      0.45        60.5 %  68.6 %   82.6 %  43.2 %       5.40 / 3.26 m/s
 *      0.60        62.6 %  70.7 %   79.2 %  48.3 %       7.20 / 4.35 m/s
 *      0.75        60.1 %  66.7 %   75.0 %  49.5 %       8.99 / 5.44 m/s
 *
 *    0.60 is chosen. It is the best overall completion of the four, it holds
 *    both the 10-20 m and >20 m buckets at the baseline, it doubles the bar at
 *    8 m, and it is the largest fraction pass-quality.test.ts's `first-time`
 *    cell still survives: at 0.75 that pass leaves at 18.0 m/s and arrives at
 *    6.6 m/s, which the receiver stops collecting (3/3 -> 0/3). The bar at 15 m
 *    is 0.6 m/s narrower than the old symmetric swing, but ALL of it now reaches
 *    the man — the old low end was an under-hit that fell short.
 *
 * WHAT THIS DOES NOT FIX, and cannot from here: beyond ~22 m the fit is already
 * on the ceiling, so the bar is flat there whatever fraction is chosen. Raising
 * GROUND_PASS_MAX_SPEED would fix it and is deliberately NOT done — goalkeeper.ts
 * reads it for the roll-out and the throw, and ai/tactical-metrics.ts builds
 * INTERCEPT_PASS_SPEED from it, so that is a much wider change than this defect
 * justifies.
 */
export const PASS_ASSIST_CHARGE_HEADROOM = 0.6;

// ---------------------------------------------------------------------------
// Shooting (§4.3)
// ---------------------------------------------------------------------------

export const SHOT_CHARGE_MIN_S = 0.2;
export const SHOT_CHARGE_MAX_S = 1.2;
export const SHOT_MIN_SPEED = 15;
export const SHOT_MAX_SPEED = 35;
/** Ball height above this at contact = volley animation + reduced accuracy. (§4.3) */
export const VOLLEY_HEIGHT_THRESHOLD = 0.4;
export const VOLLEY_ACCURACY_PENALTY = 0.35;
/** Finesse: lower power, high sidespin. Power shot: max velocity, minimal spin. (§4.3) */
export const FINESSE_POWER_MULTIPLIER = 0.75;
export const FINESSE_SIDESPIN = 90; // rad/s
export const POWER_SHOT_MULTIPLIER = 1.15;
export const POWER_SHOT_SPIN_MULTIPLIER = 0.25;
/**
 * Owner, 2026-08-15: "when the ball is kicked with boost 2 ... the kick is
 * powerful". Applied to a shot/pass's final speed ON TOP OF finesse/power —
 * boost is a separate, orthogonal power source (a player can be mid-boost
 * without holding Sprint at the moment of the strike), never a replacement
 * for the Sprint-held power shot. Same magnitude for both so "a boosted kick
 * is a hard kick" holds regardless of which button sent it.
 */
export const BOOST2_KICK_POWER_MULTIPLIER = 1.3;

/**
 * Mouse-drawn curve on EVERY shot and pass, independent of Finesse — the primary
 * curve mechanic the directive (§4) wants to be VISIBLE, not a token nudge. Sized
 * near FINESSE_SIDESPIN so a full-draw curl bends a flat, driven ball ~1.3 m over
 * a 25 m shot (measured), a clear banana; Finesse still adds a touch more on top.
 * Was 0.22× ("curves, but not much"), which read as no curve at all on a flat
 * strike (only ~0.4 m) — the live "curve doesn't work" report. Still clamped by
 * clampSpin at the strike and the ^0.25 Magnus Cl cap, so it can't spiral.
 */
export const MOUSE_CURL_SIDESPIN = FINESSE_SIDESPIN * 0.7; // rad/s at |curl| = 1
// Pass curl shaping (polish phase §4): the fraction of the gesture's spin a
// zero-charge pass carries (rises linearly to 1 at full charge — power-dependent
// spin), and the technique factor for a DRIVEN pass (laces drive ≈ wrong
// technique for bend; most of the gesture is ignored).
export const PASS_CURL_POWER_FLOOR = 0.5;
export const DRIVEN_PASS_CURL_FACTOR = 0.35;
/** Base error cone (radians) before stat/pressure/distance modifiers. (§4.3)
 *  Cut HARD for "kicks 100% accurate" (live directive): 0.09→0.02 shot,
 *  0.042→0.012 pass. This is the HUMAN's only miss source, so a stationary
 *  aimed shot now lands essentially where pointed (~0.4 m at 20 m at the
 *  distance/pressure-free 1σ). The AI keeps its separate difficulty aim error
 *  (aimWithError), so it stays imperfect and beatable. */
export const SHOT_BASE_ERROR_RAD = 0.02;
export const PASS_BASE_ERROR_RAD = 0.012;
/**
 * Vertical error (degrees at 1σ, before the same stat/distance/pressure/
 * off-balance modifiers as the horizontal cone). Strike error is a 2-D cone
 * (§4.3 "harder to place"): a pressured, off-balance or low-Shooting strike
 * sprays HIGH as well as wide — the ballooned-over-the-bar / scuffed-low
 * texture. Without this, dialled lift always produced exactly that height and
 * misses could only ever be lateral.
 */
// Cut hard for "kicks 100% accurate" (live directive): the HUMAN's only error
// source is this physics cone (the AI adds its OWN difficulty-based aim error
// via aimWithError on top), so shrinking it makes a player's aimed shot land
// where pointed while the AI stays beatable. A whisper of vertical variation
// remains so a strike still reads as struck, not teleported.
export const SHOT_VERTICAL_ERROR_DEG = 0.5;
export const PASS_VERTICAL_ERROR_DEG = 0.2;
export const HEADER_VERTICAL_ERROR_DEG = 1.2;
/** Final launch-angle clamp once vertical error is applied: a scuffed shot may
 *  go slightly into the turf (bobbles), an over-hit one well over the bar. */
export const SHOT_ANGLE_MIN_DEG = -4;
export const SHOT_ANGLE_MAX_DEG = SHOT_LIFT_ANGLE_MAX_DEG + 16;
/**
 * Power shots trade placement for pace (§4.3 "harder to place" — previously
 * unmodelled: the power branch was strictly better than a placed shot, taking
 * +15% speed at zero accuracy cost). Multiplies BOTH error axes.
 */
export const POWER_SHOT_ERROR_MULTIPLIER = 1.45;

// ---------------------------------------------------------------------------
// Defending (§4.4)
// ---------------------------------------------------------------------------

export const STANDING_TACKLE_RANGE = 1.2;
export const SLIDE_TACKLE_RANGE = 2.5;
/**
 * Getting back to your feet after a slide. Owner: "pushes up quickly" — and
 * the reference sheet's own stage 10 says the same.
 *
 * This is the number the ANIMATION is sized against (see anim-author-slide.ts:
 * the clip runs exactly SLIDE_S + this, or a finished once-clip clamps on its
 * last frame and the player stands frozen for the remainder). At 1.2 s the
 * recovery occupied nearly half the clip and read as a man taking his time
 * getting up while play carried on without him. 0.8 s is still a real cost for
 * leaving your feet — three quarters of a second out of the game — without
 * being a spectator.
 */
export const SLIDE_TACKLE_RECOVERY_S = 0.8;
/**
 * §2 fair-stealing — the three tunable bands a defender reads as he closes on a
 * carrier, so a challenge is a deliberate, layered act (never a lunge from
 * anywhere). Winning the ball still requires a VALID interaction (§14 front-arc /
 * tackle-state gate); these only shape WHEN the presser closes, contains, and
 * commits:
 *   AWARENESS  — he is aware of the carrier and shapes goal-side toward him;
 *                beyond it he simply holds his defensive shape.
 *   ENGAGEMENT — he is close enough to actively press and OFFER a challenge.
 *   CHALLENGE  — tackling distance: he eases off the sprint into a controlled
 *                jockey and commits a tackle only when the ball (not the man) is
 *                the nearer thing (§4.4 ball-first) — i.e. a fair steal.
 * All three are single knobs so the feel can be tuned without touching logic.
 */
export const STEAL_AWARENESS_RANGE = 22; // m — outer: aware + shaping goal-side
export const STEAL_ENGAGEMENT_RANGE = 7; // m — actively pressing, may offer a challenge
export const STEAL_CHALLENGE_RANGE = 2.0; // m — tackling distance: jockey + commit
export const JOCKEY_SPEED_MULTIPLIER = 0.55;
export const JOCKEY_LATERAL_MULTIPLIER = 1.35;
/** How far away an attacker can be and still shape the jockey's body toward them. (§4.4) */
export const JOCKEY_TARGET_RANGE = 5.0;
/** Gate interception on a cone of awareness so it isn't a magnet. (§4.4) */
export const INTERCEPT_AWARENESS_CONE_DEG = 120;
export const INTERCEPT_REACTION_MIN_MS = 120;
export const INTERCEPT_REACTION_MAX_MS = 320;

// ---------------------------------------------------------------------------
// Fouls & discipline (§5.3)
//
// Everything the referee decides is tuned from here. It used to be spread
// between player-controller.ts (the sim's verdict) and ai/individual.ts (the
// AI's private copy of the same geometry), and the two SILENTLY DRIFTED — the
// AI committed to challenges on a 0.15 m ball advantage that the sim then
// whistled at 0.49 m, which by itself was 137 fouls per 90 minutes. There is
// now exactly one predicate (sim/tackle-geometry.ts) and it reads these.
// ---------------------------------------------------------------------------

/**
 * How much of a HEAD START on the man the defender is forgiven when he arrives
 * on the ball. (§4.4 / §5.3 — the ball-first test.)
 *
 * The test compares two surface-to-surface gaps from the defender: his gap to
 * the BALL (minus both radii) against his gap to the MAN (minus both capsule
 * radii). Zero here means "the ball must be genuinely the nearer thing";
 * positive means a defender who is fractionally closer to the man is still
 * judged to have played the ball.
 *
 * PHYSICALLY, that forgiveness is a real thing and not a fudge: a defender who
 * gets a genuine toe on the ball HAS played the ball, even though his standing
 * leg, his hip and his shoulder all reach the man at the same instant. A
 * referee does not whistle that; he whistles the challenge that goes through
 * the man on its way to a ball that was never reachable. The gap arithmetic
 * measures capsule centres, and a boot at the end of an extended leg is roughly
 * this far outside the capsule it belongs to.
 *
 * The old sim rule is exactly the value -0.30 (it subtracted the defender's own
 * capsule radius from the man gap but not from the ball gap, which is where the
 * extra 0.30 m of severity came from), so the sweep below spans the historical
 * behaviour rather than starting from an invented baseline.
 *
 * SWEPT against the 12-seed harness (rules/card-rate.test.ts), on the owner's
 * path, at AI_TACKLE_COMMIT_MARGIN 0.12:
 *
 *     -0.30 → fouls 13.1/90, tackles 31.9/90, win 47.1%, foul 29.4%
 *     -0.18 → fouls 18.8     tackles 46.9     win 56.0%  foul 28.0%   ← here
 *     -0.12 → fouls 39.4     tackles 67.5     win 50.0%  foul 33.3%
 *     -0.06 → fouls 24.4     tackles 69.4     win 59.5%  foul 24.3%
 *
 * ⚠ READ THAT COLUMN AS NOISE, NOT AS A CURVE. 48 minutes of sampled football
 * yields only 7-15 actual fouls, so adjacent rows differ by more than the
 * 20-24 target band is WIDE (-0.12 sits above both its neighbours). -0.18 is
 * chosen for the shape of the whole sweep, not the precision of one row: it is
 * the value whose tackle count also lands near real football's 30-40 per match
 * rather than buying a foul rate with volume. Anyone re-tuning this should
 * raise SECONDS_PER_SEED first — the harness cannot currently resolve a
 * difference of 5 fouls per 90, and pretending otherwise is how a tuning pass
 * ends up chasing variance, which is the exact failure that file's header
 * warns about.
 */
export const TACKLE_BALL_FIRST_TOLERANCE = -0.18; // m

/**
 * Extra ball-first advantage an AI defender DEMANDS before he commits, over and
 * above what the referee requires to call it a fair challenge. (§4.4, §10.1)
 *
 * ── Why this is not the bug coming back ─────────────────────────────────────
 *
 * The bug was two COPIES of the ball-first geometry that drifted apart by
 * 0.34 m without anyone deciding they should. This is ONE copy — the AI passes
 * this number into the same `reachesBallBeforeMan` the referee uses — and the
 * gap is a single named quantity that a sweep can move. Retuning
 * TACKLE_BALL_FIRST_TOLERANCE still moves both sides together, which is the
 * property that was missing.
 *
 * ── Why the gap has to exist at all ─────────────────────────────────────────
 *
 * A challenge does not land on the tick it is committed. Under contact timing
 * (`WorldState.contactTiming`, which practice/solo/arena all enable) the
 * challenge is armed and resolves at the swing's CONTACT FRAME:
 *
 *   StandingTackle  18 ticks x contactWindowStart 0.25  ≈  75 ms
 *   SlideTackle     32 ticks x contactWindowStart 0.20  ≈ 107 ms
 *
 * A carrier knocking the ball on at 4-6 m/s moves it 0.3-0.65 m inside that
 * window. A defender who commits on a margin of exactly zero is therefore
 * committing to a challenge that will be a foul by the time it arrives, every
 * time — which is precisely what the measurement showed: ~45% of all AI
 * challenges whistled, at every tolerance in the sweep, because the tolerance
 * is not what that failure is made of.
 *
 * So this is the AI's lead: how far ahead of the referee's line he has to be
 * before he believes he will still be on the right side of it when his foot
 * actually gets there.
 *
 * ── The swept value is well BELOW the physical estimate, on purpose ─────────
 *
 * The arithmetic above argues for 0.3-0.65 m. Measured, on the owner's path:
 *
 *      0.00 → fouls 54.4/90, tackles 76.9/90, win 36.6%, foul 48.8%
 *      0.08 → fouls 37.5     tackles 60.0     win 43.8%  foul 37.5%
 *      0.12 → fouls 18.8     tackles 46.9     win 56.0%  foul 28.0%
 *      0.20 → fouls 13.1     tackles 35.6     win 52.6%  foul 26.3%
 *      0.35 → fouls  0.0     tackles 22.5     win 83.3%  foul  0.0%
 *      0.50 → fouls  0.0     tackles  0.0  ← the AI stops defending entirely
 *
 * The margin has to be not just correct but ACHIEVABLE: a defender is inside
 * STANDING_TACKLE_RANGE (1.2 m) when he decides, and a clear 0.35 m of ball
 * advantage inside that radius is a rare picture. Demanding it does not make
 * him time his tackles better, it makes him never tackle — 0.50 m is a defence
 * that stands and watches, which is the failure mode this file's card-rate
 * harness prints `tackles` to catch. 0.12 buys most of the foul reduction while
 * leaving the tackle count near real football's 30-40 per match.
 *
 * It is deliberately NOT a difficulty knob. An easy AI diving in and a hard AI
 * timing it is a real idea, but it belongs in ai/difficulty.ts alongside the
 * other per-tier params, and mixing the two would make this number untunable.
 */
export const AI_TACKLE_COMMIT_MARGIN = 0.12; // m

/**
 * THE LUNGE (CR-8/CR-27/CR-29, owner: bots "just look at it from far, they
 * dont try to slide tackle"). MEASURED (SESSION_LOG.md, live browser repro):
 * a defender reaches `state: CONTESTED` and sits there, `action: -`, never
 * committing — because AI_TACKLE_COMMIT_MARGIN above is the ONLY gate a
 * challenge is ever launched through, and it is the SAME geometry
 * `rules/fouls.ts resolveTackle` uses to judge foul-or-clean, so a challenge
 * can structurally only ever commit when the AI is already certain it will
 * be judged clean — which, chasing from a losing position, is close to
 * never. Ordinary football has defenders who commit on geometry they have
 * NOT yet confirmed safe: a lunge.
 *
 * A lunging defender still never commits on geometry the REFEREE would call
 * an outright foul RIGHT NOW — `runDuel`'s lunge check reuses
 * `reachesBallBeforeMan` at `requiredExtraMargin=0`, the exact predicate
 * `resolveTackle` itself applies, just without AI_TACKLE_COMMIT_MARGIN's
 * usual travel-time safety buffer. The real risk is the ~75-107ms between
 * the decision and the boot landing (contact-actions.ts): the carrier can
 * still move enough in that window to turn a fair lunge into a foul, which
 * is exactly the real football dynamic this exists to recreate — a fix that
 * raises fouls by making challenges worse across the board is the "stop
 * defending" failure mode, not this.
 */
export const AI_LUNGE_REQUIRED_MARGIN = 0;
/** A lunge under REAL pressure — the last man, the only thing between the
 *  carrier and goal — is desperation, not recklessness; scales
 *  DifficultyParams.tackleLungeChance up for him specifically rather than
 *  raising the base rate for every defender on the pitch. */
export const AI_LUNGE_LAST_DEFENDER_MULT = 1.6;

/** dot(victimForward, dirToDefender) below this ⇒ the defender came from behind. (§5.3) */
export const TACKLE_FROM_BEHIND_DOT = -0.5;

/**
 * Arrival speed (m/s) at or above which a challenge that misses the ball is
 * RECKLESS rather than merely careless — the "he went in at pace" half of a
 * booking. (§5.3)
 *
 * ⚠ It must stay WELL BELOW MAX_SPEED_RUN (6.0). A previous attempt gated on
 * `contactSpeed > MAX_SPEED_RUN` and produced ZERO yellows in 48 minutes,
 * because un-sprinted top speed IS 6.0 and the comparison is strict — nothing
 * running on the ground could ever exceed it. That is the mistake this constant
 * exists to make impossible to repeat. Sprinting reaches MAX_SPEED_SPRINT, so a
 * threshold between jog and run pace catches a genuine lunge and lets an
 * ordinary mistimed challenge stay a free kick.
 */
export const FOUL_RECKLESS_ARRIVAL_SPEED = 4.6; // m/s

/**
 * How far the ball has to be (m) for a challenge to read as NO ATTEMPT TO PLAY
 * IT. (§5.3)
 *
 * This is the second half of every bookable branch, and it is the whole defence
 * against cheap yellows. A defender who came in fast but had the ball inside
 * this radius made a genuine, mistimed attempt — free kick, no card. Beyond it
 * he took the man with no prospect of the ball, which is the disregard for the
 * opponent that Law 12 calls reckless. Set above STANDING_TACKLE_RANGE (1.2) so
 * anything inside a standing tackle's own reach is always forgiven.
 *
 * ⚠ MEASURED, AND THE RESULT IS A NEGATIVE ONE WORTH KNOWING: sweeping this
 * 1.6 → 1.3 → 1.0 changed the 12-seed harness output NOT AT ALL — byte
 * identical, including the tackle counts. Once the AI commits on a real margin
 * (AI_TACKLE_COMMIT_MARGIN) its remaining fouls all have the ball far closer
 * than a metre: they are "mistimed by a fraction with the ball right there",
 * which is Careless by design and by the owner's directive. So this constant
 * does not control the AI yellow rate and lowering it cannot buy one — it only
 * governs the genuinely late challenge, which in AI-vs-AI barely happens and in
 * a human's hands happens all the time. Do not lower it hunting for yellows;
 * you will get none, and you will have quietly made honest challenges bookable
 * for every human player.
 */
export const FOUL_RECKLESS_BALL_DISTANCE = 1.6; // m

/** Outside this radius a foul is not denying an OBVIOUS goal-scoring chance. (§5.3) */
export const DOGSO_MAX_GOAL_DISTANCE = 30;

/**
 * Law 12's fourth DOGSO criterion — GENERAL DIRECTION OF PLAY — as a speed
 * floor (m/s) and a heading test.
 *
 * The other three criteria (distance to goal, no covering defender, control of
 * the ball) were implemented; this one was not, so a man dawdling sideways in
 * his own half's final third, or shielding the ball away from goal, was a
 * sending-off waiting to happen. An attacker who is not going anywhere near the
 * goal has no obvious goal-scoring opportunity to deny, whatever the other
 * three say.
 *
 * Two knobs because both failure modes are real: DOT alone would count a
 * stationary player whose residual drift happens to point goalwards (velocity
 * direction is meaningless at ~0 m/s), and SPEED alone would count a man
 * sprinting flat across the box.
 */
export const DOGSO_MIN_GOALWARD_SPEED = 2.0; // m/s — a real run, not a shuffle
/** cos(60°): the victim's run must be within 60° of the line to goal. */
export const DOGSO_MIN_GOALWARD_DOT = 0.5;

// ---------------------------------------------------------------------------
// Goalkeeper (§4.5)
// ---------------------------------------------------------------------------

/** Reaction delay is what makes keepers beatable. f(Reflexes) → 80..220ms. (§4.5) */
// ⚠ HALVED 2026-08-15 from 80/220. Owner: "goal keeper is acting dumb he is
// waiting for player to kick and defend ... because of animations delay the
// goal keeper is missing the ball make the goal keeper accurate". The gate is
// measured from the tick the ball was STRUCK, and a 20 m/s shot covers 4.4 m
// in the old 220 ms - a keeper who has not moved by then is beaten before he
// starts. 40-110 ms is a real keeper's reaction (published human floor is
// ~120 ms visual, but he is reading the STRIKER's shape long before contact,
// which this model has no other way to express).
// ⚠ HALVED AGAIN 2026-08-16 (owner: "make the goal keeper very smart and
// street smart and please dont deleay his actions"). A keeper reads the
// STRIKER's shape, not the ball's first frames, and this model has no other
// place to express that - so the gate stands for the whole anticipation the
// sim cannot see. 20-55 ms is effectively "he was already moving", which is
// what a good keeper looks like.
export const GK_REACTION_MIN_MS = 20;
// 110, not 55: the REFLEX STAT still has to be worth something. At 20-55 the
// whole band is 35 ms - two ticks - and gk-behavior's acceptance ('a sharp
// keeper commits earlier than a slow one') measured a 2-tick gap against its
// 3-tick bar. The best keeper is still effectively already moving; the worst
// is a tenth of a second behind him, which is what the stat is for.
export const GK_REACTION_MAX_MS = 110;
export const GK_DIVE_REACH = 4.8;
export const GK_DIVE_SPEED = 6.5;
/**
 * How far ahead the keeper simulates the ball when asking "is this going in?".
 * (§4.5)
 *
 * ⚠ This is NOT the same horizon as "how far ahead could I act", and conflating
 * the two is what made both keepers inert. The act horizon is a dive's duration
 * plus reaction — 28-37 ticks, 0.47-0.62 s — and `predictThreat` only reports a
 * shot as ON TARGET when its forward integration actually CROSSES THE GOAL
 * PLANE. A 22 m/s strike from the edge of the box is in flight for 0.75 s, so
 * scanning to the act horizon ended the loop before the ball arrived, on target
 * was never set, and the dive branch could not fire. MEASURED: 0 dives, 0
 * catches, 0 distributions in a 120 s 11v11 while facing shots, every seed.
 *
 * 1.6 s covers a ball struck from the halfway line at a shot's pace and every
 * shot inside it, which is every shot a keeper is expected to react to. It buys
 * nothing to go further: a slower ball from further out gets re-evaluated every
 * tick, and it costs `crossHorizonTicks` iterations of the integrator per
 * keeper per tick — 96 at 60 Hz, scalar and allocation-free (§15.7).
 */
export const GK_THREAT_HORIZON_S = 1.6; // s

/**
 * Above this pace the keeper is RUNNING, and `setStanceAnim` stops stamping his
 * standing GK pose over the locomotion clip stage 4 picked from his velocity.
 * (§4.5, §6.5)
 *
 * Deliberately the same number as player-controller's WALK_SPEED_THRESHOLD
 * (1.4), which is where AnimState.Idle gives way to AnimState.Walk for every
 * outfield player — a keeper is not a different kind of body and the two bands
 * must not disagree, or he flickers between his stance and a walk in the seam
 * between them. It is duplicated rather than imported because goalkeeper.ts
 * deliberately does not depend on player-controller.ts.
 */
export const GK_STANCE_MAX_SPEED = 1.4; // m/s

/**
 * THE SMOTHER — "they sleep on the ball". (§4.5)
 *
 * Owner, verbatim: *"like how real goal keeper do when they get the ball they
 * sleep on the ball"*. A commitment at a ball below this height and inside this
 * range is a keeper DROPPING ONTO it, not a lateral dive, and it stamps
 * AnimState.GkSmother instead of GkDiveLeft/Right.
 *
 * Height sits just above BALL_RADIUS doubled — a ball rolling or skidding on
 * the deck, which is the only thing you can actually fall on. Range is a body
 * length: further out and he is stretching for it, which is a dive by
 * definition and looks like one.
 */
export const GK_SMOTHER_MAX_HEIGHT = 0.45; // m
export const GK_SMOTHER_MAX_RANGE = 1.8; // m
/** Parries bias away from the centre of the pitch — never back to the striker. (§4.5) */
export const GK_PARRY_OUTWARD_BIAS = 0.75;
/**
 * Law 12 (IFAB 2026/27): keeper may not hand-hold the ball longer than 8
 * seconds — the offence now awards a CORNER to the opponents, superseding the
 * old 6-second indirect free kick. The internal event keeps its historical
 * 'SIX_SECOND' name (wire/client stability); the timing and restart follow the
 * current law. See rules/fouls.ts handleSixSecond.
 */
export const GK_HOLD_LIMIT_S = 8;
export const GK_SWEEPER_TRIGGER_DISTANCE = 12;
/**
 * Horizontal speed (m/s) above which a keeper whose action clock is running is
 * read as MID-DIVE rather than recovering. (§4.5)
 *
 * The keeper's committed state used to be read from `p.anim`, but step.ts runs
 * player-controller's stepAnim() over the keeper at stage 4 and it overwrites
 * `p.anim` from velocity every tick — so by stage 5 a GkDive pose is already
 * gone and stepGoalkeeper could never tell it was mid-dive. It now identifies a
 * dive by its MOMENTUM instead: a lunging keeper carries dive pace while his
 * clock runs; a punch/fallen keeper is stationary. Any value comfortably below
 * GK_DIVE_SPEED×(min diving mult) and above zero separates the two. See
 * goalkeeper.ts and gk-save-rate.test.ts.
 */
export const GK_DIVE_ACTIVE_MIN_SPEED = 2.0;

// ── GK own-touch refractory (added for the "keeper cannot kick the ball" bug) ──
/**
 * How long (s) after a keeper's OWN touch he may not get his hands on the ball
 * again. (§4.5 distribution, Law 12)
 *
 * WHY THIS EXISTS — the live-match report was *"goal keeper unable to kick the
 * ball, bugs out"*, and this is the whole of it. `releaseBall()` puts the ball
 * PLAYER_CAPSULE_RADIUS (0.3 m) in front of the keeper's hands, while
 * `resolveSave()` claims anything within POSSESSION_RADIUS (1.2 m). So on the
 * tick after he punts it, his own clearance is still inside his own claim
 * envelope and nothing declined it — the back-pass guard deliberately exempts
 * his own last touch. The catch roll then decided which flavour of broken you
 * got:
 *   - caught  → the hold re-armed, he surveyed, punted, caught it again…
 *               forever, and the 8-second law never fired because
 *               gkHoldStartTick reset on every cycle;
 *   - parried → parryBall aims at the corner FLAG, i.e. through goalX, so he
 *               palmed his own punt back toward his own goal line and then lay
 *               in Fallen recovery while it rolled goalwards.
 *
 * Law 12 is the legal shape of the same thing: a keeper who releases the ball
 * may not touch it with the hands again before another player has. And it is
 * also just biology — nobody re-gathers a ball 16 ms after kicking it, which is
 * exactly the argument behind GK_REACTION_MIN_MS.
 *
 * SIZING, not a feel knob: the ball must clear the claim envelope before he may
 * touch it again. The slowest legal release is a short rolled-out throw at
 * GROUND_PASS_MIN_SPEED (8 m/s), which needs
 * (POSSESSION_RADIUS − PLAYER_CAPSULE_RADIUS)/8 = 0.113 s to get clear. 0.2 s is
 * ~2× that for margin (the keeper may still be closing on it) and stays far
 * below the flight time of any real rebound, so gathering his own spill after a
 * genuine save is untouched.
 *
 * Derived from ball.lastTouchedBy/lastTouchTick, which already clone and hash —
 * no new WorldState field, exactly like the reaction gate (see goalkeeper.ts's
 * header note on where keeper memory lives).
 */
export const GK_OWN_TOUCH_REFRACTORY_S = 0.2;

// ---------------------------------------------------------------------------
// Skill moves (§4.6) — each has a windup during which the player is committed
// and vulnerable to a well-timed tackle. Reward the defender for reading it.
// ---------------------------------------------------------------------------

export const SKILL_DURATIONS_S = {
  BODY_FEINT: 0.3,
  BALL_ROLL: 0.4,
  DRAG_BACK: 0.5,
  STEP_OVER: 0.6,
  ELASTICO: 0.8,
  KNOCK_AND_RUN: 0.5,
  RABONA_FAKE_SHOT: 0.7,
} as const;
/** Fraction of the move during which the player is committed/vulnerable. */
export const SKILL_WINDUP_FRACTION = 0.45;

// ---------------------------------------------------------------------------
// Rules (§5)
// ---------------------------------------------------------------------------

/** Law 13: defenders must be 9.15m (10 yards) from the ball. (§5.3) */
export const FREE_KICK_WALL_DISTANCE = 9.15;
/** Defending team gets a wall-positioning phase. (§5.3) */
export const FREE_KICK_SETUP_S = 3;
/**
 * Free-kick WALL (Law 13 staging, §5.3): how many defenders line up on the
 * ball→goal line at exactly FREE_KICK_WALL_DISTANCE, by threat distance from
 * the defended goal. Beyond the far band no wall forms (just the 9.15m clamp).
 */
export const FREE_KICK_WALL_NEAR_DIST = 22; // ≤ this → 4-man wall
export const FREE_KICK_WALL_MID_DIST = 30; // ≤ this → 3-man wall
export const FREE_KICK_WALL_FAR_DIST = 38; // ≤ this → 2-man wall
/** Shoulder-to-shoulder spacing between wall members (m). */
export const FREE_KICK_WALL_SPACING = 0.6;
/**
 * Penalty ceremony (Law 14, §5.3): everyone except the taker and the defending
 * keeper stays outside the penalty area, outside the arc (9.15m from the
 * spot), and no closer to the goal line than the spot. The keeper is pinned to
 * his line until the kick.
 */
export const PENALTY_ARC_RADIUS = 9.15;
/** How far outside the area's edge the waiting players are staged (m). */
export const PENALTY_WAITER_MARGIN = 1.0;
/** The taker's run-up start, behind the spot toward midfield (m). */
export const PENALTY_TAKER_RUNUP = 1.4;
/**
 * Kickoff shape staging (Law 8 + presentation): bots are returned to their
 * POSITION_ANCHORS home spots (sim/formation-anchors.ts) compressed into their
 * own half, so the restart after a goal/half reads as a real formation, not
 * the ragged halfway-line scatter open play left behind.
 */
export const KICKOFF_SHAPE_DEPTH = 0.92; // anchor fx compression factor
export const KICKOFF_FORWARD_PULLBACK_FX = -0.06; // forwards wait just inside own half
export const KICKOFF_DUP_SPREAD_FZ = 0.26; // lateral split between same-position players
/** Taker stands this far behind the ball — must be comfortably INSIDE
 *  BALL_PLAY_REACH (0.7) or the staged taker can never actually kick it and
 *  every kickoff stalls to the ready-window timeout. */
export const KICKOFF_TAKER_INSET = 0.45;
/** Advantage: play on for 3s; if the attack breaks down, pull it back. (§5.3) */
export const ADVANTAGE_WINDOW_S = 3;
/** Referee reaction delay for realism. (§5.6) */
export const REFEREE_REACTION_MIN_MS = 200;
export const REFEREE_REACTION_MAX_MS = 400;
/** VAR check duration in competitive modes. (§5.6) */
export const VAR_CHECK_S = 3;
/** Two yellows → red. (§5.3) */
export const YELLOWS_FOR_RED = 2;
export const SUBSTITUTIONS_PER_TEAM = 5;
export const SUBSTITUTION_WINDOWS = 3;
/** Default for online friends: long enough to feel like a match, short enough
 *  to keep queues moving. (§5.5) */
export const DEFAULT_HALF_LENGTH_S = 6 * 60;
export const PENALTY_SPOT_DISTANCE = 11.0;
export const CENTRE_CIRCLE_RADIUS = 9.15;
export const KICKOFF_OPPONENT_DISTANCE = 9.15;

// ---------------------------------------------------------------------------
// Rules engine — restart state machine timings. (§5.1)
// Every restart is setup (positioning + min-distance) → ready (taker may play)
// → live (back to InPlay). These are the setup/ready window lengths.
// ---------------------------------------------------------------------------

/** Positioning window before the ball may be played at a kickoff. (§5.1) */
export const KICKOFF_SETUP_S = 2.0;
/** Setup window for throw-ins, corners, goal kicks and drop balls. (§5.1) */
export const RESTART_SETUP_S = 1.5;
/** Setup window for a penalty — area cleared, GK on the line. (§5.3) */
export const PENALTY_SETUP_S = 3.0;
/**
 * Ready window: once positioned, the taker has this long before it is auto-played. (§5.1)
 *
 * ── 2.0 → 1.3 (2026-08-09, dwell) ──────────────────────────────────────────
 *
 * The owner reports the game feels slow, and restart dwell was measured as the
 * largest single cause — up to a third of a two-minute match. This window is
 * pure standing-still: the taker has the ball in his hands and is waiting out a
 * timer. 1.3 s still covers the whole pickup (THROW_PICKUP_S 0.45) plus a beat
 * of hold plus the release wind-up (THROW_RELEASE_CONTACT_S 0.31), so nothing
 * is clipped and the throw still reads as deliberate — it just stops loitering.
 * It remains the BOUNDED FALLBACK that guarantees a restart can never deadlock.
 */
/**
 * -- FLOOR: 1.3 -> 2.1 (2026-08-09) ------------------------------------------
 *
 * Raised again once THROW_RELEASE_CONTACT_S was corrected to the throw clip's
 * real release beat. The window must cover THROW_PICKUP_S (0.45) + the wind-up
 * (1.52) = 1.97 s, or the bounded fallback fires mid-throw and the ball leaves
 * his hands early all over again.
 *
 * This is NOT the dwell cut being given back. At 2.0 s the window was 2.0 s of
 * a man standing still holding a ball; at 2.1 s it is 1.97 s of him visibly
 * picking the ball up and throwing it, plus a beat. The dwell win was in the
 * SETUP window, which is where the dead air actually was (far throw-in:
 * 13.80 s -> 5.72 s). Time spent on a motion you can see is not dwell.
 */
export const RESTART_READY_S = 2.1;
/** Half-time break before the second-half kickoff. (§5.5) */
export const HALF_TIME_BREAK_S = 3.0;

// ---------------------------------------------------------------------------
// Throw-in delivery (§5.2 / Law 15) — the ball is THROWN, two hands, from
// behind and over the head. It was previously "taken" through the ordinary
// kick path, i.e. kicked off the touchline turf, which is not football.
// ---------------------------------------------------------------------------

/** Release height. Law 15: delivered from behind and over the head. */
export const THROW_RELEASE_HEIGHT = 2.05;
/** Speed range, charge-scaled. 13 m/s is a genuine long throw. */
export const THROW_MIN_SPEED = 6;
export const THROW_MAX_SPEED = 13;
/** Base launch angle above horizontal; liftBias nudges it. */
export const THROW_ANGLE_DEG = 14;
export const THROW_ANGLE_LIFT_DEG = 10;
/**
 * Total release plus follow-through window (drives AnimState.Throw).
 *
 * -- 0.5 -> 2.77 (2026-08-09), and it is a HARD FLOOR, not a taste knob -------
 *
 * `runThrowInReady`'s armed branch is gated on `actionTimer > 0`. If this is
 * shorter than THROW_RELEASE_CONTACT_S the timer runs out BEFORE the detach
 * tick, the armed branch stops matching, and the throw-in never completes at
 * all -- the phase sits in ThrowIn/Ready forever. That is what happened the
 * moment THROW_RELEASE_CONTACT_S was corrected from 0.31 to 1.52 and this was
 * left at 0.5: seven suites went red with "the throw never happened".
 *
 * So: THROW_ANIM_HOLD_S >= THROW_RELEASE_CONTACT_S, always. 2.77 s is the full
 * duration of `setpiece_throw_in` (83f), which makes the pose cover the release
 * AND its follow-through -- the ball is already in flight for the last 1.25 s,
 * which is correct; a thrower does not snap back to idle the instant the ball
 * leaves.
 */
export const THROW_ANIM_HOLD_S = 2.77;
/**
 * Wind-up from entering the throw clip to its visible two-hand release pose.
 *
 * -- 0.31 -> 1.52 (2026-08-09): THE BALL WAS LEAVING BEFORE HIS HANDS DID -----
 *
 * This is the sim's half of a two-sided contract, and the two sides disagreed
 * by 1.2 seconds. The renderer's authored beat for the throw is
 * `animationCommand.ts ACTION_BEAT[AnimCommand.THROW_IN] = 0.55` -- 55% of the
 * way through `setpiece_throw_in`, which `clipAvailability.ts` records as
 * 83f/2.77s. That is 0.55 x 2.77 = 1.52 s from the first frame of the clip to
 * the frame where the hands actually let go.
 *
 * The sim was detaching the ball at 0.31 s: ELEVEN PERCENT into the clip, while
 * the taker's arms are still down by his sides. MEASURED on the side-on proof
 * render (sheet 954): `anim = Throw(17)`, ball already at y = 1.37 m and
 * climbing, arms hanging. Head-on you cannot see it. From the side it is the
 * whole shot -- exactly the defect the owner named: "the ball being released
 * before the hands reach it".
 *
 * COUPLED CONSTANT. If `setpiece_throw_in` is re-authored or its 0.55 beat
 * moves, this must move with it: THROW_RELEASE_CONTACT_S = beat x clipDuration.
 * The sim cannot import the client's manifest to derive it, so the number is
 * carried here with its arithmetic written down rather than left to be
 * rediscovered from a screenshot a second time.
 *
 * This sets the floor for RESTART_READY_S -- see the note there.
 */
export const THROW_RELEASE_CONTACT_S = 1.52;
/** Ball centre sits just behind the head while both hands support it. */
export const THROW_HANDS_BACK_OFFSET = 0.08;

// ---------------------------------------------------------------------------
// THE FETCH (owner, 2026-08-09): *"when throw in happens make sure the player go
// and pick up the ball and throw it not glitches"*.
//
// The walk-in already existed — the taker really does travel to the touchline.
// What did NOT exist was the moment in between: the ball lay on the turf for the
// whole Setup window and then TELEPORTED to 2.05 m the instant the whistle blew,
// whether or not the taker had arrived. Two visible lies in one frame — a ball
// that jumps 1.94 m in a tick, and a ball held over the head of a man who is
// still jogging up the line.
//
// The choreography below closes both, and it does it with NO new state: every
// beat is derived from `stageTimer`, which is already hashed, cloned and pinned
// by the desync fingerprint. The pickup lands exactly ON the whistle because it
// is timed off the same countdown the whistle is.
//
//   ball on turf  ──arrived, last THROW_PICKUP_S of Setup──▶  carried at chest
//   carried       ──release armed, over THROW_RELEASE_CONTACT_S──▶  overhead
//   overhead      ──contact──▶  released
//
// ⚠ CORRECTION (ANIM-AUDIT, 2026-08-09): the paragraph below described a state
// that no longer holds. `clipAvailability.ts` THROW_IN_HOLD is now `V`
// (verified) — the RIG74 merge (@278a2fa) bound it to a real recovered clip
// ('ThrowInHold', 72f/2.40s, sustained, looping) that IS in the loaded cast
// pack. "There is no ThrowInHold clip" is false today; leaving this note here,
// struck through, so nobody re-derives the old (now wrong) conclusion.
//
// What is still true, MEASURED live on the running cast rather than assumed
// (`scripts/measure-throwinhold-hands.mjs`, `boneWorldPosition` debug hook):
// the clip is a genuine cyclic SWAY, not a static hold — hand_l/hand_r cycle
// through roughly 0.86–1.32 m over its 2.4 s loop, both hands, both directions,
// no single "correct" height. THROW_CARRY_HEIGHT (1.12) already sits inside
// that live-measured range, i.e. it is a reasonable static approximation of a
// moving target, not the "ball at 2.05 m over arms-at-sides" defect this
// comment used to warn about — that specific failure mode was actually a
// SEPARATE bug (the wind-up's AnimState thrashing every tick and restarting
// the RELEASE clip before it could move — see player-controller.ts stepAnim's
// `prev === AnimState.Throw` branch), now fixed, and it never touched this
// constant.
//
// A ball that visibly tracks the sway (rather than a fixed compromise height)
// needs the ball socket-attached to the animated hand bone, not a taller
// constant — the exact seam `fix/ball-in-hand`'s `render/handAttach.ts` built
// for the keeper's held ball (computeSocket's offsets are all in the player's
// UPRIGHT frame because the sim has no skeleton, D1, so they go blind the
// moment a pose leaves upright; handAttach is the render-side correction).
// That branch was NOT on this one as of this note — do not duplicate it here;
// wire the throw-in carry onto it once it lands instead of hand-rolling a
// second bone-follow system.
// ---------------------------------------------------------------------------

/**
 * Turf-to-hands lift, seconds. Runs at the START OF READY — i.e. AFTER the
 * referee's whistle, never before it.
 *
 * ⚠ That timing is a rule, not a preference. `restart-whistle.test.ts` asserts
 * the ball "stays DEAD on its spot for the whole ceremony, and moves only after
 * the whistle" to 5 cm, which is the owner's own *"the ball is in game only
 * after refree whisely"*. An earlier draft of this ran the pickup in the last
 * 0.45 s of Setup so it would land exactly on the whistle; it moved the ball
 * 0.35 m before it and correctly failed that test. Setup is now untouched: he
 * walks up and stands over it, and picks it up when he is told he may.
 */
export const THROW_PICKUP_S = 0.45;
/** Two-handed carry height while he waits — chest, not overhead. */
export const THROW_CARRY_HEIGHT = 1.12;
/**
 * How far in FRONT of his chest the carried ball sits.
 *
 * ⚠ Load-bearing floor: PLAYER_CAPSULE_RADIUS (0.30) + BALL_RADIUS (0.11) =
 * 0.41 m is where the ball starts overlapping his own collision capsule. At 0.30
 * the physics step resolved that overlap by SHOVING the dead ball — which showed
 * up as a throw-in whose ball had horizontal velocity before anyone had thrown
 * it, and as a ball drifting a metre out of his hands. 0.48 clears the capsule
 * with 7 cm to spare. Do not lower this without re-reading placeThrowInBall.
 */
export const THROW_CARRY_FRONT_OFFSET = 0.48;
/**
 * Fraction of the wind-up spent raising the ball STRAIGHT UP before it swings
 * back over the head.
 *
 * Same capsule problem, one leg later: interpolating straight from the chest
 * carry to the behind-the-head release point takes the ball diagonally THROUGH
 * his own torso. Going up first, then back — which is also what a thrower's
 * hands actually do — keeps it either in front of the capsule or above it
 * (THROW_RELEASE_HEIGHT 2.05 clears PLAYER_CAPSULE_HEIGHT 1.8 by more than a
 * ball radius) for the whole motion.
 */
export const THROW_WINDUP_RAISE_FRAC = 0.6;

// ---------------------------------------------------------------------------
// Dead-ball out-of-play handling (Phase-1 Step 6). The pitch boundary is a
// RULES boundary, not a physical wall: the world-edge walls were removed, and a
// ball that leaves the pitch during a phase where the rules never collect it
// (Goal dwell, HalfTime, FullTime — a live ball is collected the same tick it
// fully crosses) is instead damped to rest by stepBall once it is clearly out.
// ---------------------------------------------------------------------------

/**
 * How far the WHOLE ball must be beyond the line before dead-ball damping
 * starts. Big enough that the ball visibly continues its natural flight past
 * the line (nothing yanks it the moment it crosses); small enough that damping
 * has room to stop it inside the touchline apron (the z headroom between the
 * line at ±34 and the encodable edge at ±40 is only ~5 m on an 11v11 pitch).
 */
export const DEAD_BALL_OUT_MARGIN = 2.0; // m beyond the line

/**
 * Exponential horizontal damping rate (1/s) applied to a dead out-of-play ball:
 * v *= (1 − rate·dt) per tick. Roll-out distance ≈ v/rate: a ~27 m/s clearance
 * entering the damped zone dies in ~2.3 m, comfortably before the §2.8
 * encoding-edge clamp (the z apron past the margin is only ~2.8 m on 11v11 —
 * measured: 8/s let a 30 m/s FullTime ball coast to the clamp at 38.89 with
 * 0.3 m/s left; 12/s stops it clear of the clamp). Horizontal only: gravity
 * still lands a lofted ball naturally and the bounce/roll model settles it.
 */
export const DEAD_BALL_DAMP_PER_S = 12.0;

/**
 * How long the match dwells on a GOAL before staging the kickoff. (§5.1, §5.5)
 *
 * This did not exist: a goal called beginKickoff() on the same tick, so play
 * restarted KICKOFF_SETUP_S (2.0s) after the ball crossed the line — while the
 * GOAL banner was still on screen (it holds 2.8s). The single most exciting
 * event in football got less pause than a throw-in, and MatchPhase.Goal was
 * never actually entered by the FSM at all, so online clients (whose banners
 * ride the phase feed, not the event feed) could never show it.
 *
 * 5s is a game pause, not a real one — a televised celebration runs 30s+, but
 * this is a 6-minute half (DEFAULT_HALF_LENGTH_S) and the clock is stopped
 * anyway (STOPPAGE_GOAL_S already adds 8s of added time, which is the LAW's
 * accounting and is unrelated to how long we dwell here). Long enough to see
 * the banner, watch the scorer celebrate and reset; short enough not to bore.
 */
export const GOAL_CELEBRATION_S = 5.0;

// ---------------------------------------------------------------------------
// Rules engine — added (stoppage) time accrual. (§5.5)
// "Accumulate stoppage time from injuries, subs, goals, VAR" — cards used to
// add their own extra stoppage here too, but cards are disabled (owner,
// 2026-08-17: "give only fouls") and rules/fouls.ts no longer calls a
// card-stoppage constant at all, so there is nothing left to declare.
// ---------------------------------------------------------------------------

/** Stoppage added for a goal (celebration + restart). (§5.5) */
export const STOPPAGE_GOAL_S = 8;
/** Stoppage added for a foul / free-kick setup / offside. (§5.5) */
export const STOPPAGE_FOUL_S = 2;

// ---------------------------------------------------------------------------
// Pitch geometry — goals are fixed by law regardless of pitch size. (§9.2)
// ---------------------------------------------------------------------------

export const GOAL_WIDTH = 7.32; // 8 yards
export const GOAL_HEIGHT = 2.44; // 8 feet
export const GOAL_DEPTH = 2.0;
export const GOAL_POST_RADIUS = 0.06;
export const PENALTY_AREA_LENGTH = 16.5;
export const PENALTY_AREA_WIDTH = 40.32;
export const GOAL_AREA_LENGTH = 5.5;
export const GOAL_AREA_WIDTH = 18.32;
export const LINE_WIDTH = 0.12;
/** Subtle real-world undulation on the pitch surface. (§6.3) */
export const PITCH_UNDULATION_M = 0.03;

// ---------------------------------------------------------------------------
// AI (§10.2)
// ---------------------------------------------------------------------------

/** AI with 0ms reactions is the #1 cause of "the AI is cheating". (§10.2) */
export const AI_REACTION_MIN_MS = 150;
export const AI_REACTION_MAX_MS = 350;
/** AI shouldn't react to a player behind it. (§10.2) */
export const AI_VISION_CONE_DEG = 140;
export const AI_AWARENESS_RADIUS = 35;
export const AI_TEAM_TICK_RATE = 5; // Hz, Layer 3
export const AI_INDIVIDUAL_TICK_RATE = 10; // Hz, Layer 2
export const AI_STEERING_TICK_RATE = 60; // Hz, Layer 1
/** Influence-map resolution over the pitch. (§10.1) */
export const AI_INFLUENCE_GRID_X = 32;
export const AI_INFLUENCE_GRID_Z = 20;
export const AI_CONTEXT_DIRECTIONS = 16;

// ---------------------------------------------------------------------------
// Attributes (§8.1)
// ---------------------------------------------------------------------------

export const ATTRIBUTE_MIN = 0;
export const ATTRIBUTE_MAX = 100;
/**
 * Balance constraint (§8.1): cap the effect of any stat at ±15% of base.
 * A great player with a bad avatar must still beat a bad player with a great
 * avatar. This is essential — if stats dominate, friends who play less quit.
 * Do NOT raise this to make progression feel "rewarding".
 */
export const STAT_EFFECT_RANGE = 0.15;
/** Attribute 50 is the neutral midpoint — 1.0× multiplier. */
export const ATTRIBUTE_NEUTRAL = 50;

// ---------------------------------------------------------------------------
// Camera (§7)
// ---------------------------------------------------------------------------

export const CAMERA_BROADCAST_HEIGHT = 18;
export const CAMERA_BROADCAST_DISTANCE = 25;
export const CAMERA_BROADCAST_FOV = 55;
export const CAMERA_BROADCAST_TILT_DEG = 15;
/**
 * Broadcast follows a weighted centroid. §7.1 suggested ball 70/player 30, but
 * the first live playtest read as "the camera abandons my player" (user report
 * 2026-07-17: "keep the camera angle on that player"). Player-primary keeps YOUR
 * player framed while anticipation (below) still pulls toward where the ball is
 * going; the §7.3 off-ball blend covers the far-from-ball case. Both weights
 * must sum to 1.
 */
export const CAMERA_BALL_WEIGHT = 0.42;
export const CAMERA_PLAYER_WEIGHT = 0.58;
/** Look where the play is going, not where it is. (§7.2) */
export const CAMERA_ANTICIPATION_S = 0.25;
export const CAMERA_SMOOTH_TIME = 0.28;
export const CAMERA_DEAD_ZONE = 1.5;
/** +5° when sprinting. Sells speed for free. (§7.2) */
export const CAMERA_SPRINT_FOV_KICK = 5;
/** Beyond this distance from the ball you get your own camera + ball arrow. (§7.3) */
export const CAMERA_OFFBALL_BLEND_START = 15;
export const CAMERA_OFFBALL_BLEND_FULL = 40;

// ---------------------------------------------------------------------------
// Player switching (§4.8)
// ---------------------------------------------------------------------------

/** Respect intentional off-ball positioning — don't yank the camera away. (§4.8) */
export const AUTO_SWITCH_INPUT_GRACE_MS = 500;
export const MANUAL_SWITCH_CONE_DEG = 60;

// ---------------------------------------------------------------------------
// Performance budgets — hard limits, asserted in CI. (§6.8)
// ---------------------------------------------------------------------------

export const BUDGET_DRAW_CALLS = 150;
export const BUDGET_TRIANGLES = 2_500_000;
export const BUDGET_TEXTURE_MB = 512;
export const BUDGET_FRAME_MS = 16.6;
export const BUDGET_RENDER_MS = 10;
export const BUDGET_SIM_MS = 3;
export const BUDGET_NET_MS = 1;
export const BUDGET_INITIAL_LOAD_MB = 25;

// ---------------------------------------------------------------------------
// Live-tunable subset for the dev overlay (§15.5)
// ---------------------------------------------------------------------------

/**
 * Bound to leva in the dev overlay. Mutating these at runtime changes feel
 * immediately. NOTE: mutating a tunable mid-match desyncs from the server —
 * the overlay is dev-build only and forces an offline/local session.
 */
export const TUNABLES = {
  ball: {
    restitution: BALL_RESTITUTION_GRASS,
    frictionGrass: BALL_FRICTION_GRASS,
    rollingResistance: BALL_ROLLING_RESISTANCE,
    spinDecayAir: BALL_SPIN_DECAY_AIR,
    maxAngularVelocity: BALL_MAX_ANGULAR_VELOCITY,
    magnusCoeff: MAGNUS_CL_COEFF,
    magnusMax: MAGNUS_CL_MAX,
    airDensity: AIR_DENSITY_SEA_LEVEL,
  },
  player: {
    sprintSpeed: MAX_SPEED_SPRINT,
    dribbleSpeed: MAX_SPEED_DRIBBLE,
    acceleration: ACCELERATION,
    deceleration: DECELERATION,
    turnRateMax: TURN_RATE_MAX_DEG,
    turnRateMin: TURN_RATE_MIN_DEG,
    touchRadius: TOUCH_RADIUS,
    dribbleTouchImpulse: DRIBBLE_TOUCH_IMPULSE_BASE,
  },
  shooting: {
    minSpeed: SHOT_MIN_SPEED,
    maxSpeed: SHOT_MAX_SPEED,
    baseError: SHOT_BASE_ERROR_RAD,
    finessePower: FINESSE_POWER_MULTIPLIER,
    finesseSpin: FINESSE_SIDESPIN,
  },
  camera: {
    height: CAMERA_BROADCAST_HEIGHT,
    distance: CAMERA_BROADCAST_DISTANCE,
    fov: CAMERA_BROADCAST_FOV,
    smoothTime: CAMERA_SMOOTH_TIME,
    anticipation: CAMERA_ANTICIPATION_S,
  },
};

export type Tunables = typeof TUNABLES;

// ---------------------------------------------------------------------------
// House rules — an arcade "funny addon", NOT a Law of the Game. (user, 2026-07-17)
//
// Pressing P PUNCHES / K KICKS a nearby OPPONENT (the man, never the ball).
// Contact is ALWAYS a foul — there is no clean version, unlike a tackle. Same
// house-rule spirit as MOUSE_CURL_SIDESPIN above: gameplay the brief never asked
// for, with real, deterministic consequences. See player-controller.ts
// (startViolentConduct), rules/fouls.ts (handleViolentConduct) and DECISIONS.md D3.
// ---------------------------------------------------------------------------

/** A punch has short reach — you must be right on top of your victim. (§house) */
export const PUNCH_RANGE = 1.1; // m
/** A kick reaches a little further — a swung leg. (§house) */
export const KICK_RANGE = 1.8; // m
/** The swing commits the attacker (cannot act again) for this long. Punch is quicker. */
export const PUNCH_RECOVERY_S = 0.4;
export const KICK_RECOVERY_S = 0.7;
// How long a footed strike's swing animation is held (Pass/Shoot/Volley/Header):
// a short follow-through so the renderer reliably plays the kick clip. It reuses
// actionTimer, so it also gives a small, realistic post-kick recovery — but is
// deliberately shorter than a tackle/skill recovery. (§1 kick-animation fix)
export const KICK_ANIM_HOLD_S = 0.22;
/** The victim is decked — planted and unsteerable — for this long. A kick decks them longer. */
export const PUNCH_KNOCKDOWN_S = 0.9;
export const KICK_KNOCKDOWN_S = 1.4;
/** Speed the victim is knocked back along the attacker's facing, then slides to a stop (m/s). */
export const PUNCH_KNOCKBACK_SPEED = 3.0;
export const KICK_KNOCKBACK_SPEED = 5.0;

// ── Tackled-player reaction (§5.3) ─────────────────────────────────────────
// Review, verbatim: "in tackle if a player makes another player fall forward
// make that happen … enable collision and make other players fall or get hurt".
//
// Before this, a foul tackle raised a FOUL event and the referee stopped play —
// but the man who was fouled kept running, untouched. Play stopping was the
// ONLY evidence a challenge had landed, which read as the tackle passing
// straight through him. He now goes down.
//
// This reuses knockdownTimer / AnimState.Fallen (the violent-conduct path
// above) rather than adding a parallel mechanism: that field is already
// cloned, hashed, serialised, excluded from pass targeting and honoured by the
// locomotion lock, so a trip needs no new state. Durations are shorter than a
// punch — a trip is not a decking.
/** A player fouled by a tackle stays down this long, by how bad the challenge was. */
export const TACKLE_TRIP_KNOCKDOWN_S = 0.75;
/** A reckless/violent challenge puts them down longer. */
export const TACKLE_TRIP_RECKLESS_KNOCKDOWN_S = 1.15;
/**
 * A CLEAN slide that wins the ball still takes the man down when it arrives
 * this fast (m/s) — a legitimate, heavy, ball-first slide. Below it the carrier
 * just loses the ball on his feet. Set above jogging pace so a gentle poke
 * never floors anyone.
 */
export const TACKLE_CLEAN_FELL_SPEED = 4.6;
/** Clean-slide trips are the briefest of the three — he is up quickly. */
export const TACKLE_CLEAN_KNOCKDOWN_S = 0.55;
/**
 * Speed the tripped player is carried along his OWN momentum as he goes down
 * (m/s), i.e. he falls FORWARD over the tackle rather than being punched
 * backwards. Combined with the tackler's line-of-travel in resolveTackle.
 */
export const TACKLE_TRIP_CARRY_SPEED = 2.6;
/**
 * Below this speed (m/s) a tripped player has no momentum of his own to fall
 * over, so he is shoved along the TACKLER's line instead of his own. Small — any
 * genuine movement should carry him forwards.
 */
export const TRIP_MIN_OWN_MOMENTUM = 0.35;
/**
 * The final slice of ANY knockdown is spent GETTING UP rather than lying still.
 *
 * Review: *"when he falls let him stand up, not directly stand up — same with
 * every animation"*. `anim` used to read Fallen for the whole knockdown and then
 * become a locomotion state on the tick the timer expired, so the body went from
 * flat on the grass to fully upright in a single frame. A render cross-fade
 * cannot fix that — blending a lying pose into a standing one just snaps the
 * torso vertical.
 *
 * 0.4 s is the shortest window that reads as pushing off the floor rather than a
 * flicker, and it must stay BELOW the shortest knockdown that can occur
 * (TACKLE_CLEAN_KNOCKDOWN_S = 0.55) or a light trip would be nothing but
 * get-up, with no moment on the ground at all.
 */
export const KNOCKDOWN_GETUP_S = 0.4;
/**
 * "More than 3 times → penalty" (the user's rule). A player's punch+kick offence
 * count climbs by one each strike; offences 1..3 are a direct free kick at the
 * spot, and the 4th (count EXCEEDS this limit) awards a PENALTY to the victim's
 * team regardless of where it happened, then resets the counter to 0. This is
 * §5.3's "track per-player foul count; escalate" pushed to a house extreme.
 */
export const PUNCH_KICK_OFFENSE_LIMIT = 3;

// ── Shoulder barge + stumble (§3.3, §4.4) ──────────────────────────────────
// Review, verbatim: "enable collision and make other players fall or get hurt
// use their hands to push like same like fifa".
//
// The tackle half of that is TACKLE_TRIP_* above. This is the other half: the
// FIFA jostle for position. Two opponents who genuinely CLASH while contesting
// a ball resolve a barge — the loser is shoved off his line, stumbles, and if
// the charge arrives hard enough and he had the ball, it comes off him.
//
// ── Why it is automatic contact, and NOT an input button ───────────────────
// InputFlags is at bit 17 (Jump/BicycleKick already pushed the wire field past
// 16 bits, types.ts). A new flag costs a PROTOCOL_VERSION bump, an input-codec
// widening and a client keybind — for a mechanic that in FIFA has no dedicated
// button either: you barge by running your shoulder into someone. Contact-based
// costs nothing on the wire and reads identically in play.
//
// ── Why every barge is LEGAL (no FOUL raised) ─────────────────────────────
// Law 12 permits a fair charge: shoulder-to-shoulder, contesting a ball within
// playing distance, without excessive force. BARGE_CONTEST_RADIUS is exactly
// that "within playing distance" clause, so a barge cannot fire as an off-ball
// body-check in midfield. Whistling ordinary jostling would stop play several
// times a minute and the deliberate-foul tools (tackle / P / K) already exist.
//
// ── Why a STUMBLE is not a knockdown ──────────────────────────────────────
// knockdownTimer plants a player and takes steering away entirely (`sliding` in
// stepPlayerController). Doing that on a shoulder charge would be both wrong
// (nobody hits the deck from a fair charge) and dangerous: two overlapping
// capsules that both stop steering is how a match deadlocks. stumbleTimer keeps
// full steering and only scrubs the speed cap and the ability to START an
// action, so a stumbling player always keeps walking out of the contact.

/**
 * The pair's closing speed along the contact normal (m/s) needed for an overlap
 * to count as a CLASH rather than a lean. This is the SUM of both men's speed
 * into the contact, so two players walking into each other (1.4 + 1.4 = 2.8,
 * WALK_SPEED_THRESHOLD) stay on their feet, while one man arriving at a jog
 * (MAX_SPEED_JOG 3.5) or better resolves a barge. Sitting between those two is
 * the whole justification for the value.
 */
export const BARGE_MIN_CLOSING_SPEED = 3.0;
/**
 * The ball must be within this distance (m) of the contact MIDPOINT for the
 * clash to be a challenge FOR the ball — Law 12's fair-charge condition, and
 * the reason a barge is never a foul. Just outside POSSESSION_CONTROL_RADIUS
 * (2.35), the carrier's own keep-radius, so a ball either man could plausibly
 * reach counts and a ball 10 m away does not. This gate is also what keeps 22
 * players from permanently jostling each other across the pitch.
 */
export const BARGE_CONTEST_RADIUS = 2.5;
/**
 * How much more strength-weighted momentum into the contact the winner needs
 * before anyone is judged to have LOST the clash. Without a margin, a dead-even
 * collision would need an arbitrary tie-break to pick a loser — which the
 * contact geometry does not actually tell us, and which would make who stumbles
 * depend on player-array order. At 1.25 an even clatter is just the soft
 * separation doing its job and both men stay up.
 */
export const BARGE_DOMINANCE_RATIO = 1.25;
/**
 * Instant Δv (m/s) the loser is shoved along the contact normal — the shoulder/
 * forearm push. Deliberately well under TACKLE_TRIP_CARRY_SPEED (2.6): losing a
 * barge costs you a yard, being tripped puts you on the floor. Divided by the
 * loser's Balance multiplier (§8.1), the stat whose whole job is staying on your
 * line under contact.
 */
export const BARGE_SHOVE_SPEED = 1.6;
/**
 * Seconds the loser is off balance. Shorter than TACKLE_CLEAN_KNOCKDOWN_S
 * (0.55), the briefest knockdown in the game, so a stumble can never read as
 * "he went down" — it is a lost stride, not a fall. Divided by Balance.
 */
export const BARGE_STUMBLE_S = 0.45;
/**
 * Top-speed multiplier while stumbling. Below JOCKEY_SPEED_MULTIPLIER (0.55) —
 * a jockey is a controlled defensive stance, a stumble is not controlled at all
 * — and below SKILL_SPEED_MULTIPLIER (0.6). Never near zero: a stumbling player
 * must keep moving, or a pair in sustained contact could stall the match.
 */
export const STUMBLE_SPEED_MULTIPLIER = 0.45;
/**
 * The winner's OWN speed into the contact (m/s) needed to knock the ball off a
 * barged carrier. Above TACKLE_CLEAN_FELL_SPEED (4.6, the slide that floors a
 * man) on purpose: a shoulder must never be a cheaper way to win the ball than
 * a properly-timed challenge, or the tackle stops being the defensive tool
 * §4.4 designs it as. 5.0 is a committed run, short of a full sprint (8.5).
 */
export const BARGE_BALL_LOOSE_SPEED_GATE = 5.0;
/**
 * Exit speed (m/s) of a ball knocked loose by a barge, thrown OPPOSITE the shove
 * so the man and the ball leave the contact on opposing vectors. Under
 * STANDING_TACKLE_POKE_SPEED (4.5) because a shoulder is not a foot — a barge
 * dislodges the ball, a poke clears it — and above DRIBBLE_MAX_REL_SPEED (2.5)
 * so the touch model cannot treat it as still under anyone's control. It then
 * resolves through the ordinary §14 loose-ball contest.
 */
export const BARGE_BALL_LOOSE_EXIT_SPEED = 3.4;
/**
 * The WINNER'S own drive (m/s) above which a barge takes the loser all the way
 * down, not just off balance. Owner, 2026-08-23: "if the intensity of the fall
 * is heavy make sure player falls and rolls and dont give foul for every
 * fall". Same shape as `TACKLE_CLEAN_FELL_SPEED` — a legitimate, heavy,
 * ball-first challenge that happens to floor a man is not a foul, because Law
 * 12's fair-charge test (BARGE_CONTEST_RADIUS) already gated this contact to a
 * genuine challenge for the ball. `resolveShoulderBarge` calls `tripVictim`
 * here instead of the ordinary shove — the same primitive `resolveTackle`'s
 * clean-slide branch uses, so a heavy barge and a heavy clean slide floor and
 * recover identically (momentum-carried fall, then the shared
 * SlideRecover/GetUpFront get-up — never a new animation path).
 *
 * Set above BARGE_BALL_LOOSE_SPEED_GATE (5.0) — flooring a man is a more
 * severe outcome than merely dislodging the ball off him, so it should never
 * be the CHEAPER threshold to reach — and below MAX_SPEED_SPRINT (8.5), so a
 * genuine committed sprint-charge can still reach it. No live telemetry yet
 * (this is a new mechanic); see barge-knockdown-diagnostic.test.ts for a
 * first measurement of how often it actually fires.
 */
export const BARGE_KNOCKDOWN_SPEED_GATE = 6.8;
/**
 * Seconds the loser stays down after a heavy barge. Matches
 * TACKLE_CLEAN_KNOCKDOWN_S (0.55) deliberately — both are the SAME picture, a
 * clean, legal, ball-first challenge that happened to floor someone, and both
 * get the shortest, "he is up quickly" recovery of every knockdown tier in the
 * game (contrast TACKLE_TRIP_KNOCKDOWN_S 0.75 and the punch/kick tiers, which
 * are foul knockdowns and stay down longer on purpose).
 */
export const BARGE_KNOCKDOWN_S = 0.55;

// ---------------------------------------------------------------------------
// Player switching — feel (§4.8). APPENDED by the client agent; no existing
// value was changed. §4.8's own knobs (AUTO_SWITCH_INPUT_GRACE_MS,
// MANUAL_SWITCH_CONE_DEG) live in the "Player switching" block above; these are
// the ones the switch UX bar needs and the brief left as an exercise.
//
// They are consumed by client/src/game/playerSwitch.ts. They live here anyway
// because CLAUDE.md rule 5 is unconditional — every gameplay tunable is in this
// file — and because switching becomes a shared concern the moment the server
// starts honouring InputFlags.SwitchPlayer (which today it does not; see that
// module's header).
// ---------------------------------------------------------------------------

/**
 * Camera/indicator blend when control moves to another player (§4.8 "a clear,
 * animated ring"; user bar 2026-07-17: "smooth like FIFA — not an instant
 * snap"). Below ~150ms the eye reads it as a cut; above ~250ms you are steering
 * a player you cannot see yet. 200ms sits in the middle of that window.
 */
export const SWITCH_BLEND_MS = 200;

/**
 * Manual cone-selection scoring (§4.8 "weighted by distance and relevance to
 * the ball"). Angle within the cone always scores 1.0 at dead centre; these
 * weight the two tie-breakers against it. Both are < 1 on purpose — a candidate
 * the player did not point at must never beat one they did.
 */
export const SWITCH_DISTANCE_WEIGHT = 0.6;
export const SWITCH_BALL_RELEVANCE_WEIGHT = 0.8;
/** Metres at which the distance/ball-relevance scores fall to half. */
export const SWITCH_DISTANCE_FALLOFF = 10;

// ---------------------------------------------------------------------------
// AI implementation tunables — Layers 3/2/1. (§10.1, §10.2)
// Appended by the AI agent; nothing above this block was changed.
//
// The §10 knobs further up (reaction band, vision cone, tick rates, grid size)
// are the brief's own numbers. Everything below is the implementation's, and is
// here rather than in ai/ because §15.5 has exactly one home for a tunable.
//
// ⚠ THE RULE THAT GOVERNS THIS WHOLE BLOCK (§10.2): not one of these scales a
// physical attribute. They are geometry, weights and decision thresholds. The
// only per-difficulty knobs live in ai/difficulty.ts, and they are cognitive
// too. "Never give AI extra speed, perfect knowledge, or stat boosts" is not a
// guideline — it is the reason players trust the AI at all. If the AI is too
// easy, a number in here is the wrong fix; a better decision is the right one.
// ---------------------------------------------------------------------------

/** Ticks the team FSM stays in TRANSITION after the ball changes hands. (§10.1) */
export const AI_TRANSITION_TICKS = 45; // 0.75s — the counter-press window

/** Thirds of the pitch, in normalized fx. Derived, so they stay exact thirds. */
export const AI_FINAL_THIRD_FX = 1 / 3;
export const AI_BUILD_UP_FX = -1 / 3;

/**
 * How much further than the rest of the block a forward pushes when the team
 * is going forward, and a defender drops when it is not. Forwards damp their
 * drop instead of tracking all the way back — they are the outlet, and a team
 * whose strikers defend their own box has nobody to pass to when it wins the
 * ball. These are the numbers behind formations.ts's isForwardPosition /
 * isDefenderPosition.
 */
export const AI_FORWARD_PHASE_GAIN = 1.4;
export const AI_DEFENDER_PHASE_GAIN = 1.25;
export const AI_FORWARD_DROP_DAMP = 0.45;

/**
 * Metres of handicap a player carries for not having eyes on the ball when
 * Layer 3 picks who chases or presses. Not a veto: a defender with his back
 * turned is a worse choice than a teammate two metres further away who can see
 * it, but he is still a better choice than nobody. A hard veto would leave a
 * ball that broke behind a defensive line uncontested forever (§10.2).
 */
export const AI_BLIND_DISTANCE_PENALTY = 12; // m

/**
 * Where each team phase pushes the whole block along its own attack axis, in
 * normalized fx units (see ai/formations.ts). These six numbers ARE the tactical
 * identity of the AI: a defending block that drops 0.24 of a half-pitch and a
 * final-third block that pushes 0.18 is what makes the shape read as football
 * rather than as 10 players orbiting the ball.
 */
export const AI_PHASE_SHIFT_BUILD_UP = -0.06;
export const AI_PHASE_SHIFT_ATTACK = 0.06;
export const AI_PHASE_SHIFT_FINAL_THIRD = 0.18;
export const AI_PHASE_SHIFT_TRANSITION = 0.0;
export const AI_PHASE_SHIFT_PRESS = 0.14;
export const AI_PHASE_SHIFT_DEFEND_BLOCK = -0.24;

/** The block slides with the ball, as a fraction of the ball's normalized pos. */
export const AI_BLOCK_BALL_SHIFT_X = 0.28;
export const AI_BLOCK_BALL_SHIFT_Z = 0.35;
/**
 * THE SHAPE LEADS THE BALL (owner, 2026-08-18: "they dont... take positions
 * to defend"). MEASURED (`defensive-shape-diagnostic.test.ts`, 5 seeds x
 * 90s, sustained forward carries): the shape target itself is fine and
 * players sprint correctly toward it (93% of HoldShape players >6m from
 * target ARE sprinting, mean 6.57 m/s) — the gap (10.9m early in a carry,
 * shrinking smoothly to 0.6m by 5s) is realistic recovery time, not a bug.
 * But `solveFormation`'s block-shift always reacts to where the ball WAS at
 * the last 5Hz (200ms) sample, never where it is heading — every recovering
 * defender starts each leg of the chase a beat behind. Same trick
 * `AI_INFLUENCE_VELOCITY_LEAD_S` already uses for influence-map coverage
 * (leading a player along his OWN velocity so a covering run registers
 * before it arrives): lead the BALL's position by its own velocity before
 * computing the block's ball-shift terms, so a fast, sustained carry pulls
 * the whole defensive anchor toward where the ball is GOING, not just where
 * it already was. Deliberately NOT applied to the discipline `chase` lerp a
 * few lines below — that term represents a WEAK player's undisciplined
 * instinct to chase the ball's CURRENT position, and anticipating it well
 * would make low discipline read as smart, not sloppy.
 *
 * ⚠ THE LEAD SPEED IS CAPPED, and MEASURED shows why: an uncapped first cut
 * broke `situational-shape.test.ts`'s "open play keeps the ball in the
 * middle third" — team 0 spent the whole sample in its own third (att-third
 * visits: 0). A shot or a long ball moves the ball at 20-30 m/s, nothing
 * like the pace of a developing attack, and leading THAT velocity for 0.4s
 * projects the block's anchor 8-12m in a direction the ball is about to
 * stop moving in (it's airborne/decelerating, not carried). Capped at
 * AI_SHAPE_BALL_LEAD_MAX_SPEED — a fast dribble, not a strike — so the lead
 * only ever anticipates a CARRY, the situation this fix is actually for.
 */
export const AI_SHAPE_BALL_LEAD_S = 0.4;
/** Ball speed used for the lead is capped here (m/s) — see the warning
 *  above. Matches MAX_SPEED_SPRINT_DRIBBLE: faster than this is a kick, not
 *  a run, and should not steer the block. */
export const AI_SHAPE_BALL_LEAD_MAX_SPEED = 7.0;
/** Lateral spread when several players share one Position (two CBs, two STs). */
export const AI_SHAPE_SPREAD_FZ = 0.3;
/** Positioning discipline (§10.3): low discipline drifts toward the ball. */
export const AI_INDISCIPLINE_BALL_CHASE = 0.4;

// Influence map — §10.1's "space analysis" over AI_INFLUENCE_GRID_X × _Z cells.
/** A player controls space out to here; falloff is (1-d/R)² — no exp(), so the
 *  map is bit-identical on every engine (only + - * / and sqrt). (D2) */
export const AI_INFLUENCE_PLAYER_RADIUS = 11; // m
/** Influence is projected along velocity: a sprinting player owns the space he
 *  is arriving at, not the space he is leaving. */
export const AI_INFLUENCE_VELOCITY_LEAD_S = 0.5;
/** Prefer space that is also forward — the emptiest cell is often behind you. */
export const AI_SPACE_FORWARD_BIAS = 0.5;
/** ...but do not send a runner 40m across the pitch for a marginally better cell. */
export const AI_SPACE_DISTANCE_PENALTY = 0.35;
/** How far from his anchor a supporting player will look for space. */
export const AI_SUPPORT_SEARCH_RADIUS = 14; // m
/** Score bonus a Support runner gives the cell it chose LAST cycle, so its
 *  target holds steady instead of hopping between adjacent ~3.3m cells every
 *  5Hz rebuild — the main source of "players roam like idiots". Sized just
 *  above the typical inter-cell score wobble; a genuinely better cell still
 *  wins. Control terms are ~[0,1], so 0.25 is a meaningful but beatable edge. */
export const AI_SUPPORT_CELL_KEEP_BONUS = 0.25;

// MEASURED AND REJECTED — biasing Support toward the CARRIER'S CURRENT FACING
// CONE. Do not re-land without re-reading this.
//
// Owner, 2026-08-17 (live-prod screenshot): a carrier held up in a 1v1 duel
// near the halfway line, teammates standing apart from play — "he wont pass
// the ball at all... other players are not actively involved." MEASURED
// (`stuck-carrier-diagnostic.test.ts`, 5 seeds x 90s): 21 HOLD streaks
// >=0.6s, mean 2.9s, one running 9.5s, and inside every stuck streak 82% of
// the carrier's teammates were rejected for exactly one reason — OUTSIDE THE
// FACING CONE. Matches an OLDER, independent measurement in SESSION_LOG.md's
// "STILL WEAK" list (Session 41+): "the next lever is off-ball movement that
// puts support in the carrier's field of view — NOT further relaxation of
// the cone."
//
// So that lever was tried: `InfluenceMap.bestCellNear` gained an optional
// view-cone bias (viewX/viewZ/viewYaw/viewHalfRad/viewBonus, default 0 =
// unchanged for every caller), wired to Assignment.Support only — when my
// team has a carrier beyond AI_PASS_LAYOFF_RADIUS, bias the search toward a
// cell inside HIS current facing cone (the same `withinCone` test the pass
// scorer itself uses, AI_VISION_CONE_DEG half-angle, bonus 0.4 — comparable
// to AI_SPACE_FORWARD_BIAS).
//
// ISOLATED on/off, SAME seeds, SAME commit, only the bonus toggled 0.0/0.4:
//
//   |                    | OFF (control) | ON     |
//   |--------------------|---------------|--------|
//   | HOLD               | 34%           | 52%    |
//   | DRIBBLE            | 29%           | 4%     |
//   | HOLD streaks >=0.6s| 21            | 34     |
//   | cone-rejected %    | 82%           | 80%    |
//
// WORSE across the board, and the cone-rejection rate — the thing this was
// supposed to fix — barely moved. WORKING THEORY, not yet independently
// confirmed: the carrier's FACING YAW is not a proxy for his attacking shape
// mid-duel — shielding or engaged 1v1, he is very often facing the
// presser/ball-side, sideways or even backward relative to goal. Biasing
// support toward "wherever he happens to be looking right now" pulled
// teammates toward the wrong side of the pitch and away from the forward
// space the dribble scorer (`AI_SCORE_DRIBBLE_FORWARD_BONUS`) rewards —
// which is the likely reason DRIBBLE collapsed 29% → 4%, a much larger
// swing than the cone fix could plausibly cause on its own.
//
// A future attempt should bias toward something STABLE under pressure — the
// carrier's ATTACKING axis (attackSign), not his transient facing — and
// re-run this exact isolated harness before landing anything.
//
// v2-v6, 2026-08-17, ALSO REJECTED — full detail in SESSION_LOG.md's top
// entry, short version here so nobody re-walks the same six steps:
//   v2  stable attackSign axis instead of live yaw, still ALWAYS-ON.  WORSE.
//   v2b same, bonus 0.4->0.15 (a gentler weight).  WORSE STILL — the streak
//       count went UP as the bonus went DOWN, i.e. NON-MONOTONIC.
//   v3  gated on the carrier being CONFIRMED stuck (>=6 consecutive
//       decideOnBall hold decisions, a real clock) instead of always-on.
//       Still WORSE — because assignAttack puts SEVERAL players in Support
//       at once and ALL of them chased the same cone: the exact "converges
//       on the carrier... teammates end up in a huddle" this branch's own
//       comment already warns about, self-inflicted.
//   v4  v3 + only the CLOSEST Support-assigned teammate responds.  STABLE at
//       last (streaks identical to off, 21 = 21) but not an improvement —
//       cone-rejection barely moved (82% -> 85%).
//   v5  v4 + a wider search radius (14m -> 22m) so the one responder could
//       actually REACH a cell inside the cone.  PASS rose 35% -> 50% but
//       streaks rose too (21 -> 29) and DRIBBLE collapsed (29% -> 5%).
//   v6  same idea, a gentler radius (18m, between the two).  WORSE THAN
//       BOTH ENDPOINTS (streaks 31) — again non-monotonic.
// CONCLUSION: the non-monotonicity is the finding, not any one number. Every
// parameter tried (bonus magnitude, search radius, trigger scope) produced a
// non-smooth response from the 22-agent system across a 90s x 5-seed sample
// — this is not "hasn't found the right constant yet", it is evidence this
// specific mechanism (biasing bestCellNear's SUPPORT search toward a
// visibility cone, however scoped) is the wrong lever, not just untuned. A
// different mechanism is owed a fresh session, not another sweep of this one.

// Context steering — §10.1 Layer 1.
export const AI_STEER_SEEK_WEIGHT = 1.0;
export const AI_STEER_SHAPE_WEIGHT = 0.55;
export const AI_STEER_TEAMMATE_AVOID_WEIGHT = 0.85;
export const AI_STEER_OPPONENT_AVOID_WEIGHT = 0.3;
/** Getting back onside outranks every other interest — it is a hard constraint. */
export const AI_STEER_ONSIDE_RECOVER_WEIGHT = 2.5;
export const AI_TEAMMATE_AVOID_RADIUS = 3.5; // m — don't stand on a teammate
export const AI_OPPONENT_AVOID_RADIUS = 1.5; // m — small: you DO contest opponents
/** Below this net score no direction is worth taking; stand still. */
export const AI_STEER_MIN_SCORE = 0.02;
/** Ease off inside this radius so an AI does not vibrate around its target. */
export const AI_SEEK_ARRIVE_RADIUS = 2.5; // m
/** Chasing and pressing do not ease off — you arrive at full pace or not at all. */
export const AI_SEEK_ARRIVE_RADIUS_URGENT = 0.6; // m
/** Hold the line this far behind it, so a tick of lag does not stray offside. */
export const AI_ONSIDE_MARGIN = 0.6; // m

// Decision thresholds — §10.1 Layer 2.
/**
 * How hard to hit a pass, and this is the number the whole AI turned on.
 *
 * A pass is only worth playing if the receiver can CONTROL it, and §3.4 defines
 * control as ball-vs-player relative speed under POSSESSION_CAPTURE_REL_SPEED
 * (5 m/s). A ball still doing 20 m/s when it arrives is not a pass, it is a
 * turnover with extra steps — and because BALL_ROLLING_RESISTANCE is only 0.02
 * (§3.2), a hard pass stays fast for a very long time. The first version of this
 * used a linear "speed = 1.1 × distance", which sent every 20m pass at 22 m/s;
 * measured over 90 seconds of AI-vs-AI, the ball was loose 91% of the time and
 * the two teams combined for one shot. Nobody could trap anything.
 *
 * So the AI aims for an ARRIVAL speed instead and solves backwards for the
 * launch speed with v₀ = √(v_arrive² + 2·a·d). The deceleration estimate is
 * deliberately a single rough number rather than an inversion of the real drag
 * crisis + rolling model: the AI is allowed to misjudge the weight of a pass,
 * and §10.2 would rather it did. GROUND_PASS_MIN_SPEED (8) is the floor, so
 * short passes are firmer than ideal and have to be run onto — which is correct
 * football, and is what makes a short ball to a stationary man a bad idea here.
 */
export const AI_PASS_TARGET_ARRIVAL_SPEED = 3.0; // m/s — comfortably trappable
export const AI_PASS_DECEL_ESTIMATE = 2.0; // m/s², the AI's rough model of §3.2
/** Nobody passes to a teammate standing on their shoulder. */
export const AI_PASS_MIN_DISTANCE = 3.0;
/** Beyond this a pass is a hopeful one; the scorer starts docking it. */
export const AI_PASS_MAX_COMFORTABLE_DISTANCE = 30; // m
/** An opponent within this of the pass line is in the way. */
export const AI_PASS_LANE_BLOCK_RADIUS = 1.1; // m
/** Beyond this, a forward pass is played as a through ball (lofted + led). */
export const AI_THROUGH_BALL_MIN_DISTANCE = 14; // m
export const AI_SHOT_LANE_BLOCK_RADIUS = 0.9; // m
/** Lift bias (§4.1) the AI puts on a shot: keep it down close, lift it from range. */
export const AI_SHOT_LIFT_NEAR = -0.1;
export const AI_SHOT_LIFT_FAR = 0.22;
/** How far ahead a dribbler checks for space before backing himself. */
export const AI_DRIBBLE_LOOKAHEAD = 6.0; // m
/** Ticks after a kick/tackle before the AI may throw another. Stops a decision
 *  held across a 10Hz window from firing the same flag six ticks running. */
export const AI_KICK_COOLDOWN_TICKS = 18;
export const AI_TACKLE_COOLDOWN_TICKS = 24;
/**
 * The AI's ball prediction is DELIBERATELY naive: straight line, damped. It does
 * not integrate the drag crisis and does not know about Magnus — exactly the
 * approximation goalkeeper.ts makes on purpose (§4.5). An AI that solved the
 * real trajectory would read every curling ball perfectly, and that reads as
 * cheating (§10.2). The wrongness is the design.
 */
export const AI_BALL_PREDICT_DAMP = 0.72;
export const AI_INTERCEPT_ITERATIONS = 4;
/**
 * §3 receiver commitment — once a teammate is chosen to collect a loose ball /
 * receive a pass, he keeps the job unless a rival teammate's intercept beats his
 * by MORE than this many ticks. Stops the primary chaser flip-flopping every 5Hz
 * decision as the ball rolls, which read as "nobody is interested".
 */
export const AI_RECEIVE_HYSTERESIS_TICKS = 15; // ~0.25 s
/**
 * Past this the ball is somebody else's problem; hold your shape.
 *
 * ⚠ NO LONGER GOVERNS BEHAVIOUR (2026-07-25). ai/individual.ts's looseBall node
 * used this to let the team's DESIGNATED chaser refuse the job on distance, and
 * he then ran to his formation anchor instead. That was measured to be a big part
 * of "the bots ignore the ball": the chaser stood off on 14-17 % of all
 * loose-ball ticks and, because assignLoose had already committed to him, nobody
 * was nominated in his place — so on those ticks not one player on that team was
 * going for the ball. See AI_LOOSE_CLAIM_RADIUS's block at the end of this file.
 *
 * It is kept because ai/ball-engagement.test.ts still reports the stood-off
 * fraction against it, which is the counter that has to stay honest if anyone
 * reintroduces a distance gate. It is NOT a live tunable — changing it changes a
 * diagnostic threshold and nothing else.
 */
export const AI_CONTEST_MAX_DISTANCE = 30; // m
/** A marker sits this far goal-side of his man. */
export const AI_MARK_GOALSIDE_DISTANCE = 1.6; // m
/** Marking hysteresis: a defender keeps last cycle's man unless a different
 *  opponent is at least this much closer (m). Stops markers swapping men every
 *  5Hz cycle — the robotic-shimmer that reads as "roaming like idiots". */
export const AI_MARK_SWAP_MARGIN = 3.0; // m
/** Inside this the presser jockeys instead of diving in. (§4.4) */
export const AI_JOCKEY_RANGE = 2.6; // m
/** A slide is only on from at least this far out — closer than this, poke. */
export const AI_SLIDE_MIN_RANGE = 1.4; // m
/** The AI holds a sprint reserve. Same stamina model as a human (§3.3). */
export const AI_STAMINA_SPRINT_FLOOR = 20;
/** Target further away than this is worth sprinting for. */
export const AI_SPRINT_URGENCY_DISTANCE = 6; // m

// Option scoring — §10.1 Layer 2 "Evaluate options".
export const AI_SCORE_SHOOT_BASE = 1.0;
export const AI_SCORE_PASS_BASE = 0.35;
export const AI_SCORE_PASS_PROGRESS_WEIGHT = 0.6;
export const AI_SCORE_PASS_SPACE_WEIGHT = 0.5;
/** Long balls are worse balls. Without this the progress term alone makes every
 *  AI hoof it to the furthest forward man it can see. */
export const AI_SCORE_PASS_DISTANCE_PENALTY = 0.5;
export const AI_SCORE_DRIBBLE_BASE = 0.3;
export const AI_SCORE_DRIBBLE_SPACE_WEIGHT = 0.55;
export const AI_SCORE_PRESSURE_PENALTY = 0.5;
/** No option scores above this ⇒ keep the ball and wait for one. */
export const AI_OPTION_MIN_SCORE = 0.15;
/**
 * Switch of play (§4.7 "the mark of a stronger player"): a pass that travels
 * far ACROSS the pitch to a receiver in real space escapes the distance
 * penalty that otherwise suppresses every long diagonal — without it the AI
 * recycles short on the crowded side forever and the defence never has to
 * shift. Scored, not scripted: the bonus scales with lateral travel × the
 * receiver's space, so a covered far man is still ignored.
 */
/**
 * How far an AI can "see" a TEAMMATE as a pass option. Opponent awareness
 * stays at AI_AWARENESS_RADIUS (35) — imperfect information about the other
 * side is §10.2's trust rule — but your own team's shape is knowledge, not
 * perception: a real player switches to the far winger he KNOWS is there
 * without seeing him clearly. At 35 every cross-pitch diagonal (40-60m) was
 * structurally invisible, so the switch of play could never exist. The vision
 * CONE still applies (nobody passes to a man behind his head), and easy/
 * normal tiers still cap candidates nearest-first (passOptions), so long
 * switches stay mostly a hard-tier read — the honest §10.3 knob.
 */
export const AI_TEAMMATE_AWARENESS_RADIUS = 60;
export const AI_SWITCH_MIN_LATERAL = 18; // m of cross-pitch travel to count
/** Tuned against the caged-receiver scenario (attack-variety.test.ts): at 0.5
 *  the short ball into a THREE-man cage still beat the free far-side man —
 *  the exact recycling-into-traffic the term exists to break. Soak-checked:
 *  the lateral+space gates keep it from turning every ball into a hoof. */
export const AI_SCORE_PASS_SWITCH_WEIGHT = 0.85;
/**
 * Crossing (§4.7): a wide, advanced carrier with a teammate attacking the box
 * scores a CROSS — a deliberately lofted ball to the box target (the sim's
 * buildPass already shapes a wide+advanced loft as a cross; the AI simply
 * never chose to loft). Geometry mirrors ball.ts isCrossPosition.
 */
export const AI_SCORE_CROSS_BASE = 0.55;
/** Bonus per unit of box-target quality (space at the target's cell). */
export const AI_SCORE_CROSS_TARGET_SPACE_WEIGHT = 0.45;
/** The cross is played with full loft. */
export const AI_CROSS_LIFT = 1.0;
/**
 * Near/far-post box-run split: in the final third the team's forwards no
 * longer converge on ONE cutback cell (the two STs shared an anchor and a
 * small search radius — measured box clustering). Forward rank 0 attacks the
 * near post, rank 1 the far post, rank 2+ the edge-of-box cell; the lateral
 * post target sits this far from the goal's centre line.
 */
export const AI_BOX_RUN_POST_Z = 4.6; // m
/** Influence control → a roughly [-1,1] scoring term. One lone player ≈ 1.0. */
export const AI_SPACE_CONTROL_SCALE = 0.5;
/** Aim this far inside the post, never at it — the AI is not a robot arm. */
export const AI_SHOT_POST_FRAC = 0.72;
/** Range at which the AI stops adding power to a shot and starts placing it. */
export const AI_SHOT_CHARGE_NEAR_DIST = 4; // m
/** Charge fraction at AI_SHOT_CHARGE_NEAR_DIST — even a tap-in is struck firmly. */
export const AI_SHOT_CHARGE_NEAR_T = 0.35;

// ---------------------------------------------------------------------------
// Attack-minded tuning (§10.1 Layers 2/3) — appended by the ai-tuning agent.
// Evidence: ai/match-metrics.test.ts (seeded AI-vs-AI, real step()+physics).
//
// The stock AI reached the box (10-12 box entries / 3min at 11v11) but almost
// NEVER SHOT (0-2 shots/team/3min) and scored ZERO goals across a 6-match sample
// — "they are just running like idk". Root causes the harness exposed:
//   1. the shoot score was zeroed by any traffic in the lane and fell to 0 at
//      the edge of range, so a chance in a busy box was never taken;
//   2. progression stalled — one team snowballed possession while the other sat;
//   3. finishing lacked pace/placement to beat the keeper.
//
// ⚠ THE RULE STILL HOLDS (§10.2): not one number below scales a physical
// attribute. They are decision weights, geometry and thresholds — the SAME ones
// for both teams. Difficulty still lives only in ai/difficulty.ts. If the AI is
// too strong or too weak, the fix is a better decision, never a faster player.
// ---------------------------------------------------------------------------

// -- Shooting appetite (§10.1 Layer 2 "Evaluate options → Shoot") -------------
/**
 * A blocked shot lane no longer ZEROES the shot — it floors it. A striker in a
 * crowded box shoots through the crowd like a real player rather than endlessly
 * looking for a cleaner pass that never comes. laneFactor becomes
 * FLOOR + (1-FLOOR)·clamp01(clearance/BLOCK_RADIUS).
 */
export const AI_SHOT_LANE_FLOOR = 0.5;
/**
 * How fast the shoot score falls off with range. Was an implicit 1.0 — a shot
 * at the edge of shootRange scored exactly 0, so nothing beyond ~half range was
 * ever attempted. At the edge the shot is now still worth (1-this) of full.
 */
export const AI_SHOT_RANGE_FALLOFF = 0.62;
/** Flat bonus added to a shot's score once inside the penalty box: in the box
 *  you SHOOT, you do not walk it in. Applied on top of range·lane shaping. */
export const AI_SCORE_SHOOT_BOX_BONUS = 0.45;
/** Extra shooting reach (m) added to difficulty.shootRange when the carrier is
 *  already inside the box — a half-chance in the six-yard area is always on. */
export const AI_SHOT_BOX_RANGE_BONUS = 6;

// -- Finishing (§4.3 charge/placement the AI feeds the shot model) ------------
/** Close-range shots are struck firmer than the old 0.35 tap so the keeper
 *  cannot smother them — still charge-scaled, still through the same shot model. */
export const AI_SHOT_CHARGE_NEAR_T2 = 0.55;
/** Aim fraction of the half-goal-width the AI targets inside the far post when it
 *  can read the keeper — wider than the old 0.72 so finishes find the corner. */
export const AI_SHOT_POST_FRAC2 = 0.82;

// -- Progression: pass appetite toward goal (§10.1 Layer 2 "Pass") ------------
/**
 * A dedicated progressive-pass bonus. The stock scorer rewarded progress and
 * receiver space but the two together rarely beat the safety of a square ball,
 * so the AI recycled possession sideways. This adds an explicit, non-linear
 * reward for a pass that genuinely advances the ball toward the opponent goal.
 */
export const AI_SCORE_PASS_PROGRESS_BONUS = 0.5;
/** A completed forward pass into the attacking third is worth chasing — extra
 *  reward when the RECEIVER would be in the final third. */
export const AI_SCORE_PASS_FINAL_THIRD_BONUS = 0.3;
// Co-op (§13): a MODERATE preference for feeding the human teammate — a
// tie-breaker, not a magnet. Sized well below the progression/space terms (0.5-
// 0.6) so a clearly-better AI outlet still wins, and applied only AFTER the
// lane-block and offside/see gates, so it can never force a pass through a
// covered lane or to an offside/unseen man. It just means: all else close, give
// it to the player. (Bots ignoring the human is the "co-op is broken" complaint;
// bots ONLY feeding the human is equally wrong — this threads the needle.)
export const AI_SCORE_PASS_HUMAN_BONUS = 0.12;
/**
 * §8 call-for-pass. When a human teammate presses Call-for-Pass (X), the AI
 * carrier should STRONGLY prefer feeding them — far more than the passive co-op
 * nudge above. Sized to beat a typical progression/space edge so the caller wins
 * a normal decision, but added AFTER the lane-block and see/offside gates (like
 * the human bonus), so it can never force a pass through a covered lane or to an
 * offside/unseen man — an invalid call is simply not obeyed, never illegally
 * forced. The call stays "active" for AI_CALL_FOR_PASS_DURATION_TICKS after the
 * last press so the ~10Hz decision loop has cycles to respond within ~0.25–0.75s. */
export const AI_SCORE_PASS_CALL_BONUS = 0.8;
export const AI_CALL_FOR_PASS_DURATION_TICKS = 45; // 0.75s at 60Hz
/** §8 team CIRCULATE (single middle-click): while it is active a BOT carrier gets
 *  this strong bonus toward passing to another BOT (never the human), so it plays
 *  a safe ball now instead of holding or dribbling on. Applied after the same
 *  lane-block/see/offside gates as a normal pass, so an unsafe circulate is simply
 *  not obeyed. Held for CIRCULATE_DURATION_TICKS so the 10Hz loop can respond. */
export const AI_SCORE_PASS_CIRCULATE_BONUS = 1.6;
export const AI_CIRCULATE_DURATION_TICKS = 45; // 0.75s at 60Hz
/** Square/backward balls are gently discouraged so the AI looks forward first.
 *  Multiplies the (negative) progress when progress < 0. */
export const AI_SCORE_BACKWARD_PASS_PENALTY = 0.35;
/** §8: prefer the SAFER of two otherwise-similar passes. AI_PASS_LANE_BLOCK_RADIUS
 *  is a hard gate (a lane with an opponent inside 1.1 m is rejected), but a lane
 *  that barely clears at 1.2 m is nearly as interceptable — an opponent one stride
 *  away steps into the ball's ~1 s flight. Measured on the clean stack, AI pass
 *  completion was ~31 %: the carrier picked barely-open lanes because the scorer
 *  was blind to how open they were. This term rewards breathing room in the lane,
 *  ramping from 0 at the block radius to full at AI_PASS_LANE_SAFE_RADIUS, so a
 *  clean 4 m corridor beats a 1.2 m needle of equal progression. Adding it lifted
 *  passing share 28 %→49 % and completion into the 40s across a 4-seed audit. */
export const AI_SCORE_PASS_LANE_SAFETY_WEIGHT = 0.35;
export const AI_PASS_LANE_SAFE_RADIUS = 4.0; // m — lane is "fully safe" past this

// -- Dribble at goal when unpressed (§10.1 Layer 2 "Dribble") -----------------
/** With space ahead and nobody pressing, carry the ball at the goal rather than
 *  hold — this is what turns "reaches the final third" into "gets to the box". */
export const AI_SCORE_DRIBBLE_FORWARD_BONUS = 0.35;
/** Below this pressure the dribble bonus applies in full (unpressed carry). */
export const AI_DRIBBLE_UNPRESSED_MAX = 0.35;

// -- Support runs into the box (§10.1 Layer 3 assignments / Layer 2 support) ---
/**
 * When the team is in the final third, a supporting forward drives into the box
 * to attack a cutback/cross instead of holding a midfield shape. This is the
 * push-fraction (normalized fx) added to a forward's support search centre so it
 * sits inside the penalty area rather than on the D.
 */
export const AI_SUPPORT_BOX_PUSH_FX = 0.14;
/** Radius (m) a forward searches for space when making a box run — wider than the
 *  standard support radius so he can peel to the back post. */
export const AI_BOX_RUN_SEARCH_RADIUS = 18;

// -- Pressing line height (§10.1 Layer 3) — compress play in their half --------
/**
 * How much higher the whole block sits when pressing high, added to the PRESS
 * phase shift via the ball-slide. A higher line compresses the game into the
 * opponent half so turnovers happen near their goal, not ours — more shots for
 * both sides, which is the watchable outcome the user is asking for.
 */
export const AI_PRESS_LINE_PUSH = 0.06;

// -- Get out of our own third: push the block up IN POSSESSION (§10.1 Layer 3) --
/**
 * When we have the ball, the whole block sits this much higher (normalized fx),
 * added on top of the phase shift. The stock anchors + build-up shift (-0.06)
 * left a team camped in its own third with no forward outlet: the deep carrier
 * saw only square/back options and recycled, so play never left the defensive
 * half (measured: whole matches at ~85-90% own-third possession, 0 shots). A
 * standing offer ahead of the ball is what lets a team actually build.
 */
export const AI_POSSESSION_PUSH = 0.14;
/** ...but a defender does not abandon his post to join the push — his share of
 *  the possession push is damped so the back line stays a back line. */
export const AI_POSSESSION_PUSH_DEFENDER_DAMP = 0.4;

// -- Don't camp: progress the ball when pinned deep (§10.1 Layer 2 on-ball) ----
/**
 * When a carrier is pinned in his OWN defensive third under this much pressure
 * and no scored option cleared AI_OPTION_MIN_SCORE, he progresses the ball
 * forward (clearance to a forward outlet / carry into space / last-resort ball
 * upfield) INSTEAD of shielding backward forever. The stock hold-and-shield made
 * a pressed team keep the ball at its own feet for whole matches while the other
 * team chased and hacked (measured: 79-90% own-third possession, 12-19 fouls
 * against, 0 shots either way — the "they are just running like idk" match). A
 * midfield turnover keeps football happening; camping does not. 0 = closest
 * opponent at PRESSURE_RADIUS, 1 = on top of you. */
export const AI_STUCK_PRESSURE = 0.25;
/** A clearance/progression is "forward enough" to take only if the outlet is at
 *  least this far up the attack axis (m) — otherwise just shield. */
export const AI_CLEARANCE_MIN_FORWARD = 1.0;

// -- Contest discipline (§10.1 Layer 3, §10.3) — the anti-swarm cap ------------
/**
 * The MOST players one team may send to converge on the ball carrier at once:
 * one designated presser plus AT MOST one second presser. This is the headline
 * fix for the user's "some players are copying what other players are doing" —
 * measured live, ~9 of 20 moving players had velocity pointed at the ball and one
 * team ran 6-7 at it simultaneously. Real football contests with 1, occasionally
 * 2; the rest hold shape. The defence used to send `pressingCount` (up to 3)
 * bodies at the carrier, and every one that ran in was one more player doing the
 * identical thing. Difficulty (§10.3) scales HOW EAGERLY the second man joins
 * (AI_SECOND_PRESS_RADIUS_*), never this cap — even the hard tier never sends a
 * third body at the ball. A "designated ball-contester chosen by the team AI"
 * (Layer 3), not every player independently deciding "I can see it, I'll run at
 * it", is exactly what §10.1 asks of Layer 3.
 */
export const AI_MAX_CONCURRENT_PRESSERS = 2;
/**
 * The second presser commits only if he is within this distance (m) of the
 * carrier — a genuinely close support press, not a second man sprinting the width
 * of the pitch to join a swarm. The radius grows with the tier's pressingCount
 * (AI_SECOND_PRESS_RADIUS_PER_COUNT) so a more aggressive tier lets the second
 * man join from farther; easy (pressingCount 1) never sends a second at all.
 */
export const AI_SECOND_PRESS_RADIUS = 6; // m base, at pressingCount 2
/** Metres added to the second-press radius per pressingCount above 1 (§10.3). */
export const AI_SECOND_PRESS_RADIUS_PER_COUNT = 3;

// -- Shape stability: damp the block's ballward LEAN (§10.1 Layer 3) -----------
/**
 * A multiplier on the formation block's LATERAL ball-slide (AI_BLOCK_BALL_SHIFT_*)
 * and on the low-discipline ball-chase drift. The block still shifts toward the
 * ball's side, but LESS. At the full shift, when the ball moved every anchor moved
 * the same direction, so ~8 shape-holders translated ballward in unison — which
 * reads to a viewer as "half the team copying each other" even though each was
 * only holding his (ball-following) shape. Measured: 28-32% of moving players fell
 * inside the ball-direction cone against a 20.5% geometric chance floor; that
 * ~10-point excess IS this coherent lean. Trimming it keeps the shape varied so
 * players move in different directions, which is what a held shape looks like.
 * NOT zero: a block that ignores the ball entirely stops defending as a unit.
 */
export const AI_BLOCK_BALL_SHIFT_SCALE = 0.6;
/** Same idea, applied to the indiscipline ball-chase lerp (low-discipline tiers
 *  drift toward the ball). Damped so a weak team looks loose, not swarming. */
export const AI_INDISCIPLINE_CHASE_SCALE = 0.55;

// -- Verticality: actually attack (§10.1 Layer 3, §4.7 off-ball runs) ----------
/**
 * In the ATTACK phase (we hold the ball around midfield), a supporting forward
 * pushes his space-search centre this far up the attack axis (normalized fx)
 * BEYOND his formation anchor — a run that breaks ahead of the ball to give the
 * carrier a forward outlet. He stays onside: steering.ts's hard onside mask holds
 * him at the line, so this is a run TO the line, not past it. Without a standing
 * option ahead of the ball the carrier only ever sees square/back passes and play
 * never leaves midfield (session 3's logged gap: attacking third only ~2-10% of
 * live time). This is the run that turns possession into territory.
 */
export const AI_SUPPORT_ATTACK_PUSH_FX = 0.16;
/**
 * Fullbacks (LB/RB/LWB/RWB) push up their wing by this much (normalized fx, added
 * on top of the damped possession push) in the ATTACK and FINAL_THIRD phases —
 * the overlapping run that gives width and a crossing option high up the pitch.
 * Centre-backs get only the damped possession push: a back line without its
 * centre is still a back line (§10.1), but a modern fullback attacks, and a team
 * whose only forward runners are its two forwards is trivial to defend against.
 */
export const AI_FULLBACK_ATTACK_PUSH = 0.16;

// ---------------------------------------------------------------------------
// Jump, aerial headers, bicycle kick. (§3.3 jumping attr, §4.3 strike envelope;
// house rule 2026-07-17 — user asked for Space=jump, real headers, and a
// B+Space+Shoot bicycle kick.)
//
// Nothing here is inlined at a call site (§15.5). The jump reuses the vertical
// integration that moveViaPhysics already runs for every player (py += vy·dt,
// vy += GRAVITY·dt, land on `grounded`); it just seeds vy. The header raises the
// loose-ball strike envelope so an airborne player reaches a higher ball, and
// the bicycle kick is a charged shot from an overhead-to-behind position with
// more power, far less accuracy, and a committing floored recovery.
// ---------------------------------------------------------------------------

/**
 * Jump take-off speed (§3.3, Attributes.jumping). 3.4 m/s → apex v²/2g ≈ 0.59 m
 * analytically (~0.55 m through the kinematic controller's snap-to-ground). That
 * is a header reach for an aerial duel, NOT a platformer hop — do not raise it to
 * make jumping "feel springy". Scaled ±15% by the jumping stat (§8.1).
 */
export const JUMP_SPEED = 3.4; // m/s
/** Stamina paid at take-off. A jump is an effort, not free. (§3.3) */
export const JUMP_STAMINA_COST = 4.0;
/** A short gather on landing so you cannot chain hops. Applied via actionTimer. */
export const JUMP_LAND_RECOVERY_S = 0.15;
/**
 * Feet height at/below which a player counts as GROUNDED (jump legal) and above
 * which they are AIRBORNE (Jump anim, no re-jump). A settled outfield capsule
 * rests at py ≈ −0.006 m through the character controller and the first airborne
 * tick clears +0.048 m, so this 0.03 m band separates the two cleanly with room
 * to spare. Used as a small height tolerance only; the falling case is excluded
 * by a vy sign check, not by this number. (house rule 2026-07-17)
 */
export const JUMP_GROUND_EPSILON = 0.03; // m

/**
 * Ball height (absolute, m) above which a strike is a HEADER rather than a foot
 * strike: reduced power, accuracy scaled by Heading, AnimState.Header. A standing
 * player can still head up to the grounded reach below; a 2 m ball needs a jump.
 */
export const HEADER_CONTACT_HEIGHT = 1.6; // m
/**
 * Extra strike reach a player gets above the foot-volley envelope (STRIKE_MAX_HEIGHT)
 * for a header/leap. Grounded reach becomes ~0 + 1.3 + 0.5 = 1.8 m (a standing
 * header — bumped 0.35→0.5 on live "head hits" feedback: the old 1.65 m band was
 * so narrow that a chest-high cross fell between "trap" and "header" and connected
 * with neither). Still well under 2 m so a grounded player CANNOT head a 2 m ball —
 * that is what a jump is for. An airborne player adds their feet height on top. (§4.3)
 */
export const HEADER_EXTRA_REACH = 0.5; // m
/** A header redirects: base power plus a fraction of the incoming ball's pace. */
export const HEADER_BASE_SPEED = 6.0; // m/s
export const HEADER_REDIRECT_FRACTION = 0.35; // of the incoming ball speed
/** Hard cap — a header is meaningfully weaker than a footed shot (15–35 m/s). */
export const HEADER_MAX_SPEED = 14.0; // m/s
/** Headers stay flat-ish: liftBias buys only a shallow launch angle. (§4.3) */
export const HEADER_LIFT_ANGLE_MAX_DEG = 20;
/**
 * Downward header (the planted attacking header toward the bottom corner —
 * previously impossible: negative lift was clamped to 0, so every header was
 * level-to-lofted). Negative liftBias buys a downward angle, but ONLY when the
 * contact is genuinely high — heading DOWN needs the ball above the forehead,
 * not at chest height (that contact just goes flat).
 */
export const HEADER_DOWN_ANGLE_MAX_DEG = 24;
/** Must sit ABOVE HEADER_CONTACT_HEIGHT (1.6, the header trigger) — a contact
 *  in the 1.6-1.75 band is forehead-level and can only go flat, not down. */
export const HEADER_DOWN_MIN_CONTACT_Y = 1.75; // m — ball height at contact
/** Base error cone — wider than a footed shot (SHOT_BASE_ERROR_RAD 0.09); scaled
 *  down by Heading through statMultiplier (§8.1). Headers are imprecise. */
export const HEADER_BASE_ERROR_RAD = 0.14;

/**
 * Bicycle kick (InputFlags.BicycleKick — composed client-side from B+Space+Shoot;
 * the sim is the legality authority). Legal only when the ball is airborne and
 * roughly overhead-to-behind, within horizontal reach, and the player has a real
 * shot charge. Otherwise the request is ignored (a normal Shoot may still fire).
 */
/** Loosened 1.2→1.05 / 1.6→2.0 (live playtest: "bicycle kick not working" —
 *  lining up a ≥1.2m ball inside 1.6m mid-jump was nearly impossible; the
 *  overhead-band and charge gates below still keep it an earned strike). */
export const BICYCLE_MIN_BALL_HEIGHT = 1.05; // m — ball must be up
export const BICYCLE_REACH = 2.0; // m — horizontal distance to the ball
/**
 * Overhead-to-behind geometry: dot(facing, dirToBall) must be ≤ this (≈ cos 60°),
 * i.e. the ball is more than 60° off the forward axis — over the shoulder or
 * behind, never a ball in front (that is an ordinary volley). A ball within
 * BICYCLE_OVERHEAD_RADIUS horizontally counts as directly overhead regardless.
 */
export const BICYCLE_MAX_FORWARD_DOT = 0.5;
export const BICYCLE_OVERHEAD_RADIUS = 0.4; // m
/** More power than a plain shot (charge-scaled), but only a little. */
export const BICYCLE_POWER_MULTIPLIER = 1.12;
/**
 * ...bought at a steep accuracy cost — the error cone is multiplied by this, the
 * same *pattern* as VOLLEY_ACCURACY_PENALTY but far riskier. Spectacular and
 * unreliable is the whole point. (§4.3)
 */
export const BICYCLE_ERROR_MULTIPLIER = 2.2;
/** An overhead scissor imparts backspin (float), like a lifted shot. (§3.2) */
export const BICYCLE_BACKSPIN = 40; // rad/s
/** Base launch angle — a bicycle kick goes up and over before liftBias adds more. */
export const BICYCLE_BASE_ANGLE_DEG = 18;
/**
 * After the strike the player is on the floor, committed and unsteerable, for
 * this long (Fallen anim). Applied through knockdownTimer — the same floored
 * state a punch/kick victim gets — because that field, not a bare actionTimer,
 * yields the on-the-ground Fallen recovery the mechanic calls for while still
 * gating any follow-up action. Spectacular but committing.
 */
export const BICYCLE_RECOVERY_S = 1.1;

// ---------------------------------------------------------------------------
// Dribble-follow redesign (§3.4) — appended by the feel agent 2026-07-18.
// Nothing above this block was changed (constants.ts is append-only during the
// parallel build-out).
//
// User playtest (2026-07-18): "when i am with the ball and moving the ball is
// not following the taps ... it doesn't stay with the moving player." Measured
// with sim/dribble-follow.test.ts on the OLD model (carry = pspeed + touch):
// a straight sprint held possession 5.7% of ticks with the ball a MEDIAN 5.6 m
// off the feet (max 17.8 m). Root cause: the touch's velocity target always
// exceeded the carrier's own pace by the full touch impulse (2.2–3.5 m/s), so
// every touch knocked the ball away FASTER than the carrier could ever follow,
// and — rolling resistance being only 0.02 (§3.2) — it then rolled clear and
// possession dropped. A velocity target alone cannot hold a POSITION; that is
// the whole bug.
//
// The redesign keeps §3.4's model (a capped impulse toward a want-velocity — no
// glue, the ball still rolls/bounces freely) but retargets it: the want-velocity
// now carries the ball WITH the player (his pace) plus a spring that pulls it
// back onto a lead point a short way ahead of the feet. Steady state is the ball
// sitting ~a lead-distance ahead moving at the carrier's speed — i.e. AT the
// feet — instead of being flung away. See ball.ts applyDribbleTouch.
// ---------------------------------------------------------------------------

/**
 * Where the ball wants to sit while being carried: this far AHEAD of the feet in
 * the movement direction, at a standstill/walk. MUST clear the player capsule
 * with margin: the capsule radius (0.30) + ball radius (0.11) = 0.41 m surface
 * contact, and a ball parked at ~0.45 gets caught and bounced by the dribbler's
 * OWN capsule the instant a turn closes the gap — measured, that reversed the
 * ball and spiked its relative speed past the capture gate, losing possession on
 * every turn. 0.55 keeps ~0.14 m of clear air at a walk so a turn can compress
 * the gap without a self-collision. Scaled up with pace by
 * DRIBBLE_TOUCH_SPEED_FACTOR (a faster carry pushes the ball further out = the
 * "larger touches = less control" of §3.4) and tightened by Dribbling
 * (÷ statMultiplier), so a better dribbler keeps a shorter leash.
 */
export const DRIBBLE_LEAD_DISTANCE = 0.55; // m — must exceed capsule+ball (0.41)
// The lead point is clamped here so the ball a carrier is shepherding never sits
// beyond the boot's reach (BALL_PLAY_REACH 0.7m). At a sprint-dribble the ball
// rides near this limit (a heavier carry, harder to turn with); at a jog it sits
// closer. Keeping it inside reach is what makes every dribble touch a real foot
// contact (contact-truth) AND holds the ball through a turn — a lead out at ~0.9m
// was both an out-of-reach touch and a ball that swung loose off the first turn.
export const DRIBBLE_LEAD_MAX = 0.6; // m — lead point ceiling, inside foot reach

/**
 * Spring rate (1/s) pulling the ball back onto the lead point. This is what makes
 * the touch keep the ball at the feet rather than merely matching its pace: when
 * the ball falls behind or swings wide the spring adds closing speed ON TOP of
 * the carrier's pace; at the lead point it contributes nothing and the ball just
 * travels along. Bounded per tick by DRIBBLE_TOUCH_MAX_DV, so a big correction is
 * still paid out over several ticks of contact — an impulse, not a tractor beam.
 */
export const DRIBBLE_RECAPTURE_GAIN = 7.0; // 1/s
// Derivative term of the recapture PD: damps the ball's velocity RELATIVE to the
// carrier so the recapture settles the ball onto the lead point instead of
// overshooting past it and out of reach. Without it a sprint-dribble flings the
// ball ~2.5 m/s over the carrier and loses it. (§3.4, defect #4)
export const DRIBBLE_RECAPTURE_DAMP = 1.4; // dimensionless (per unit rel-velocity)
// "Under control" needs BOTH position and velocity: a ball crossing the lead
// point at +2.4 m/s relative to the carrier is NOT settled — it is blowing
// through. Without this the settle check was position-only, so a hot ball got no
// cushioning touch precisely while it was escaping (measured: sprint-dribble
// flung to 9.4 m/s, possession 43%). This is the literal first-touch: a ball
// arriving fast at the feet gets a touch that kills the relative velocity.
export const DRIBBLE_SETTLE_REL_SPEED = 0.6; // m/s rel-to-carrier that still counts as controlled
// ── Knock-and-run (§3.4, defects #3/#4) ─────────────────────────────────────
// Above this carrier speed the continuous shepherding spring cannot track the
// carrier (measured: an accelerating sprinter overruns the tracked ball, the
// capsule swallows it and the spring flings it — ball at 9.4 m/s pinned behind
// his heels). So at pace the model switches to what footballers actually do:
// KNOCK the ball a touch ahead and chase it. Between knocks the ball is pure
// physics — untouched, decelerating under rolling resistance — and the next
// knock fires only when the boot genuinely reaches it again. Discrete touches,
// zero glue, and every contact inside foot reach (contact truth).
export const DRIBBLE_KNOCK_MIN_SPEED = 5.8; // m/s carrier speed where knocks replace the spring
export const DRIBBLE_KNOCK_REL_SPEED = 1.1; // m/s the knock sends the ball ahead of the carrier
// (1.5 → 1.1, 2026-07-25.) The knock fires at the very edge of foot reach
// (0.695 m centre-to-centre), so the ball's excursion past reach IS the length of
// the window in which nothing can touch it: at 1.5 the ball ran 0.14 m clear and
// only drag brought it back — 23 ticks untouched at sprint, long enough on a turn
// for the ball to ride wide, leave reach for good and end up towed behind him.
// At 1.1 the excursion is ~0.09 m and the cycle ~11 ticks, so a curve is
// re-touched (and re-aimed at the new line) before it can escape. Still a real
// knock-and-run: the ball is pushed ahead of the boot and chased.
export const DRIBBLE_KNOCK_TRIGGER_REL = 0.35; // m/s — ball still running ahead faster than this ⇒ no touch yet
// Proximity trigger: knock whenever the ball is this close ahead of the carrier,
// whatever its nominal velocity reads. A ball pinned against the capsule by the
// sweep has a huge velocity and no displacement — a velocity-only trigger never
// fires and the carrier swallows the ball (measured). Geometry does not lie.
export const DRIBBLE_KNOCK_PROX = 0.46; // m — capsule+ball surfaces (0.41) + margin

// ╔══ CARRY-SEPARATION FIX (2026-07-25) ═══════════════════════════════════════╗
// User playtest, verbatim: "ball glitches out like when players run the ball
// dont stay at their feet its leaves and run behind them".
//
// MEASURED cause (dribble-carry-separation.test.ts): the knock-and-run corrected
// the ball's PACE but never its BEARING. Each knock assigned the ball a velocity
// straight down the carrier's line from wherever the ball happened to be, so any
// lateral error it picked up while untouched was preserved forever. At sprint the
// ball is out of foot reach for ~22 of every 23 ticks between knocks (the knock
// puts it 0.14 m past the 0.7 m reach and only drag brings it back), and on a
// sustained turn that window is long enough for the ball to ride wide. Once the
// total offset stayed above foot reach no touch could ever fire again, and the
// gather's pace-match then dragged the ball along BEHIND the carrier at his own
// speed, indefinitely, at 100 % "possession" — measured 1.49 m behind him for
// 211 consecutive ticks on a steady 35°/s arc.
//
// The three constants below close that loop. They do NOT glue the ball: every
// touch still requires real foot contact (inFootReach), and the ball is still
// integrated as a free body between touches.

/** The knock now carries an ACROSS component that returns the ball to the
 *  carrier's running line, sized to null the offset over roughly ONE knock cycle.
 *  It must not be faster than that: between touches the ball is a free body, so a
 *  correction that would null the error in less than a cycle overshoots the line
 *  and the next touch over-corrects the other way — measured, that diverged to
 *  0.9 m off-line on a dead-straight sprint. The knock cycle at sprint measures
 *  ~0.2 s, so 0.35 s is deliberately on the slow side of dead-beat: it converges
 *  in two or three touches and can never ring. */
export const DRIBBLE_KNOCK_LINE_RECOVER_S = 0.35; // s
/** Ceiling on that across component. A dribbler can sweep the ball back across
 *  his body, but not at any speed — beyond this it stops reading as a touch and
 *  starts reading as a pass to himself. Also the hard stability bound for a very
 *  wide ball, where the proportional term alone would be large. */
export const DRIBBLE_KNOCK_LINE_RECOVER_MAX = 1.2; // m/s
/** Lateral offset from the running line that alone justifies a touch, even while
 *  the ball is still running ahead of the carrier. Without this the pace triggers
 *  (relFwd / proximity) skipped every touch on a curve — the ball was "still
 *  running ahead nicely", just not where he was going. Set well inside foot reach
 *  (0.7) so the correction happens while the boot can still reach the ball. */
export const DRIBBLE_KNOCK_LINE_TOL = 0.12; // m
/** ── The carry speed governor (real football, not a leash) ──
 *  A carrier may not hold sprint pace while the ball is NOT at his feet and no
 *  longer ahead of him: he has to check his run and get back on it. Without this
 *  the carrier held 7.0 m/s with the ball 1.5 m behind him forever — a stable
 *  fixed point, because the gather pace-matches the ball to whatever speed he
 *  holds, so the gap could never close. It also closes the long-documented
 *  cap-jump (§6): the instant a knock ran the ball past the capture gate the cap
 *  jumped 7.0 → 8.5 (free sprint) and the carrier accelerated AWAY from his own
 *  ball. Gated on the ball being BEHIND-ish (below the along-offset threshold)
 *  and out of foot reach, so a normal knock-and-run — ball out in front, being
 *  chased — is completely unaffected and still runs at the full 7.0. */
export const CARRY_RECOVER_ALONG_MIN = 0.2; // m — below this the ball is no longer "ahead of him"
/** How much SLOWER than his own off-the-boot ball the carrier must run, so it
 *  actually comes back to him. A fixed recovery pace does not work: the gather
 *  pace-matches the ball to whatever speed he holds, so any cap at or above the
 *  ball's speed leaves the gap frozen (measured: capped to 4.6 m/s with the ball
 *  towed at 4.6, the ball never returned). Expressing it as a MARGIN below the
 *  ball is self-tuning and reads correctly in every case: ball still rolling at
 *  7 m/s ⇒ he may hold 5.5 (close control, so he collects it in ~a second); ball
 *  already dead ⇒ the floor below applies and he jogs back onto it. */
export const CARRY_RECOVER_CLOSE_MARGIN = 1.5; // m/s
/** Floor for that cap — he can always jog back onto a stopped ball rather than
 *  being pinned to its speed. Sits between a walk and a jog: quick enough to
 *  close on a dead ball, slow enough that running off your own ball at pace is
 *  a real cost. */
export const CARRY_RECOVER_MIN_SPEED = 2.5; // m/s
/**
 * How long the ball must stay behind his running line before he LOSES it.
 *
 * ⚠ THE CAP THESE CONSTANTS DESCRIBE NO LONGER EXISTS. Owner: "players should
 * never slow down unless the player stops or stamina is over". Running off your
 * own ball used to drop the speed cap to CARRY_RECOVER_MIN_SPEED, which is a
 * slowdown with no visible cause; now it costs POSSESSION instead, which is
 * both self-explanatory on screen and what football does.
 *
 * 15 ticks = 0.25 s at 60 Hz. It exists because the geometric test chatters on
 * every turn — see `releaseBrokenCarry`. Long enough that turning with the ball
 * is free, short enough that knocking it past yourself at a sprint is not.
 */
export const CARRY_BREAK_GRACE_TICKS = 15;
// ╚════════════════════════════════════════════════════════════════════════════╝

// Predictive steer: how much of a dribble touch's direction comes from the
// carrier's REQUESTED heading vs his lagging velocity. Velocity trails a turn
// (the §3.3 turn-rate curve), and a touch along raw velocity knocks the ball up
// the old running line — the live "ball escapes the path on a turn" defect.
// 1 would be a ball that ignores momentum entirely (glue); 0 was the defect.
export const DRIBBLE_STEER_WEIGHT = 0.6;

/**
 * The furthest the ball may be and still get a dribble touch. Wider than
 * BALL_PLAY_REACH (0.7, the clean-STRIKE reach) because shepherding a ball along
 * with body/knee while running reaches a little further than a clean foot-strike
 * does, and — critically — it keeps a ball that swings wide through a turn
 * recoverable before it crosses POSSESSION_RADIUS (1.2) and possession is lost.
 * Past this the ball is genuinely getting away: no touch, chase it. Stays under
 * POSSESSION_RADIUS so there is no window where you "own" a ball you can touch
 * from any distance.
 *
 * Sized with headroom over the lead distance (0.55, up to ~0.88 at pace): a turn
 * momentarily surges the ball ahead as the recapture spring chases the swinging
 * lead point, and the touch must keep firing through that surge rather than
 * cutting out and stranding the ball just when it needs recapturing most.
 */
export const DRIBBLE_REACH = 1.1; // m

/**
 * The touch may never accelerate the ball to more than this speed RELATIVE to the
 * carrier. This is the constraint the old model broke and the single most
 * important number here: updatePossession (§3.4) drops possession the instant the
 * ball moves faster than POSSESSION_CAPTURE_REL_SPEED (5.0) relative to the
 * player, and the dribble touch only fires WHILE possessed (stepBall gates it on
 * holder ≥ 0). So a touch that flings the ball past the capture gate loses
 * possession, which stops the touch, which strands the ball — the exact runaway
 * measured on the first redesign (spring alone reached ~6 m/s relative and held
 * possession only ~12% of ticks). Kept comfortably below the 5.0 gate so the
 * recapture spring can pull hard WITHOUT ever tripping it: the ball stays owned
 * all the way back to the feet.
 */
export const DRIBBLE_MAX_REL_SPEED = 2.5; // m/s, < POSSESSION_CAPTURE_REL_SPEED

/**
 * Free-roll pocket around the lead point: while the ball is within this of where
 * it wants to sit (the lead point ahead of the feet), it is under control and
 * rolls free — no touch. Past it, the touch fires to bring it back. This is the
 * §3.4 "touch → roll → recapture" cycle, but measured from the MOVING lead point
 * rather than from the player: the old model gated on player-to-ball distance,
 * which meant a ball at the feet but NOT keeping pace (e.g. a dead ball you start
 * to run with) was judged "under control" and never knocked into stride — so you
 * ran onto it and past it and lost it (measured: possession lost on every
 * standing start). Small, so the ball is re-touched before it can fall behind the
 * carrier; the ball therefore lives AHEAD of the feet, never trailing.
 */
export const DRIBBLE_SETTLE_RADIUS = 0.15; // m from the lead point

/**
 * The settle band ALONG the running line, for a carrier whose ball is still ON
 * that line. This is what turns the carry from a spring into touches.
 *
 * THE DEFECT. With one tight radius the ball was re-corrected the instant it
 * drifted anywhere, so it sat at a fixed offset from the boot and stayed there.
 * Measured in the running game: a full second of jogging with the ball, sampled
 * every 75 ms, gave 0.54, 0.55, 0.55, 0.55, 0.54 m — one centimetre of variation
 * across the whole run. There were no touches to see because there were none;
 * the ball was being carried, not kicked, and on screen it reads as welded to
 * the foot. A real carry is touch → roll → catch up → touch, and the ball has to
 * be allowed to MOVE relative to the player for any of that to be visible.
 *
 * WHY IT IS CONDITIONAL ON BEING ON THE LINE, and not simply a bigger radius:
 * the lead point rotates with the running direction, so slack measured along
 * that line becomes LATERAL error the moment the line swings. Tried as a plain
 * wider radius, this put the ball 1.5 m BEHIND the carrier on a sustained
 * 70 deg/s arc — it drifted wide, left the boot's reach, and no touch can fire
 * backwards through his own body, so it never came back.
 *
 * Coupling the two closes that: the extra slack exists only while the ball is
 * genuinely on the running line, and any lateral error at all collapses the band
 * back to DRIBBLE_SETTLE_RADIUS and hands the ball to the spring. A carrier who
 * starts to curve therefore tightens up automatically, BEFORE the ball can
 * escape — which is also what a footballer does, and what the carry-corner limit
 * in player-controller.ts already assumes he does.
 */
export const DRIBBLE_SETTLE_RADIUS_RUN = 0.3; // m along the line, ball on-line

/**
 * How far OFF the running line the ball may sit and still count as on it. Held
 * near the knock path's DRIBBLE_KNOCK_LINE_TOL, which was tuned against the same
 * failure from the other side.
 */
export const DRIBBLE_SETTLE_LINE_TOL = 0.13; // m across the running line

/**
 * How far past the lead point a touch sends the ball, when he is running
 * straight enough to earn the wider band. The ball lands near the far edge, then
 * rolls back through it under no force at all while the carrier closes, and is
 * touched again when it drops out of the near end. Kept inside
 * DRIBBLE_SETTLE_RADIUS_RUN: a touch that landed the ball outside the band would
 * immediately qualify for another one and the cadence would collapse straight
 * back into a per-tick spring.
 */
export const DRIBBLE_TOUCH_PUSH = 0.22; // m beyond the lead point

// ---------------------------------------------------------------------------
// Phase 4B/4C — legal kick-off staging + throw-in execution (§5.1, §5.2,
// Law 8, Law 15; playtest DEFECTS #7/#8/#9). Added at the END of the file —
// another agent owns the rest of constants.ts this phase, so new tunables
// from rules/fsm.ts land in this one contiguous block, never interleaved.
// ---------------------------------------------------------------------------

/**
 * Law 15: opponents must retreat at least this far from the point at which a
 * throw-in is taken, until the ball is released. Sim-enforced every Setup/
 * Ready tick the same way KICKOFF_OPPONENT_DISTANCE is for a kick-off — see
 * rules/fsm.ts enforceThrowInDistance.
 */
export const THROW_OPPONENT_DISTANCE = 2.0; // m

/**
 * How close the designated thrower must be to the restart spot before the FSM
 * treats a Pass/Shoot input as "release the throw" (rules/fsm.ts
 * runThrowInReady). Wider than BALL_PLAY_REACH (0.7, a foot-strike reach)
 * because a throw is picked up by hand, which reaches further than a boot.
 */
export const THROW_TAKE_REACH = 1.0; // m

/** Furthest a teammate may be and still be considered a throw-in target at all. */
export const THROW_TARGET_MAX_RANGE = 30; // m

/**
 * A candidate receiver within this of the nearest opponent is "tightly
 * marked" and is skipped unless nobody else qualifies (rules/fsm.ts
 * selectThrowTarget — simple deterministic heuristic, no possession scoring).
 */
export const THROW_MARK_TIGHT_DIST = 3.0; // m

/**
 * How far behind the thrower (against the attack direction) a candidate may
 * still count as "roughly forward" before that preference is relaxed.
 */
export const THROW_FORWARD_SLACK = 5.0; // m

/**
 * Distance range the throw speed (and, riding the same fraction, the launch
 * angle between THROW_ANGLE_DEG and THROW_ANGLE_DEG+THROW_ANGLE_LIFT_DEG)
 * interpolates over: at/under MIN it is a short, flat lob at THROW_MIN_SPEED;
 * at/over MAX it is a full-effort long throw at THROW_MAX_SPEED.
 */
export const THROW_SPEED_RANGE_MIN_DIST = 3.0; // m
export const THROW_SPEED_RANGE_MAX_DIST = 25.0; // m

// ---------------------------------------------------------------------------
// Contextual corner/throw-in choreography + goal→kickoff polish (§5.1, §5.2,
// Law 8/15/17; playtest directive §8/§9). Corners and throw-ins used to be
// "generic restarts": the ball staged correctly (4B/4C) but every player
// other than the taker/opponent-distance rule stood wherever open play had
// left them — no roles, no adaptation to which side the ball went out. This
// block adds the tunables for rules/fsm.ts's role-assignment choreography.
// Added at the END of the file, after 4B/4C's own end block, for the same
// reason that block gives: another agent may append to the rest of this file
// concurrently, so new tunables land in one contiguous block, never
// interleaved.
// ---------------------------------------------------------------------------

/**
 * Law 17: opponents must retreat at least this far from the corner arc until
 * the ball is in play — the same real-world "10 yards" as
 * KICKOFF_OPPONENT_DISTANCE (Law 8) and FREE_KICK_WALL_DISTANCE (Law 13).
 * Kept as its own named constant because it is enforced from a different call
 * site (rules/fsm.ts enforceCornerFormation) citing its own Law, matching how
 * the other two 9.15m rules are already three separate constants above.
 */
export const CORNER_OPPONENT_DISTANCE = 9.15; // m

/**
 * Shared role-assignment scoring weights — corners AND throw-ins use the same
 * greedy selector (rules/fsm.ts scoreForRestartRole/fillRestartRoles): score
 * every eligible player against every open role in priority order, take the
 * best, repeat. Aerial roles (attacking a cross) are scored on attributes; the
 * rest lean on outfield position. Distance-to-target is only a tie-breaker,
 * kept small relative to the other two so — since attributes and position
 * never change tick to tick — the winning assignment is a stable fixed point
 * across the whole Setup/Ready window instead of flickering between two
 * similarly-placed candidates.
 */
export const RESTART_ROLE_AERIAL_WEIGHT = 1.0; // × (heading + jumping), range 0..200
export const RESTART_ROLE_POSITION_WEIGHT = 6.0; // × outfield-position attack-mindedness, 0..1
export const RESTART_ROLE_DISTANCE_WEIGHT = 0.5; // × metres from the role's target (a penalty)

/**
 * Players not needed forward pull back toward their own half instead of
 * standing wherever open play left them ("rest defend" / "rest defence").
 * Own-half inset from halfway; spread separates successive rest-defend
 * players so they don't stack on the same spot.
 */
export const RESTART_REST_DEFEND_OWN_HALF_INSET = 8.0; // m into own half from halfway
export const RESTART_REST_DEFEND_SPREAD_Z = 6.0; // m between successive rest-defend players

// ── Corners (Law 17) — attacking roles: taker, short option, near-post
// runner, central target, far-post runner, edge-of-box option, rebound
// player, rest defend. Depths are measured INFIELD from the goal line the
// corner is being taken at; Z offsets are measured from the goal's centre
// line, signed by which side ("near"/"far") the corner was won on. ─────────
export const CORNER_TAKER_INSET = 0.4; // m infield from the arc — within kick reach
export const CORNER_SHORT_OPTION_DEPTH = 6.0; // m infield along the goal line
export const CORNER_SHORT_OPTION_WIDTH_INSET = 2.5; // m in from the touchline
export const CORNER_NEAR_POST_DEPTH = 4.0; // m infield — six-yard-box area
export const CORNER_NEAR_POST_Z = 2.8; // m from goal centre, corner (near-post) side
export const CORNER_CENTRAL_TARGET_DEPTH = 8.5; // m infield — six-yard box to penalty spot
export const CORNER_FAR_POST_DEPTH = 5.5; // m infield
export const CORNER_FAR_POST_Z = 2.8; // m from goal centre, opposite (far-post) side
export const CORNER_REBOUND_DEPTH = 20.0; // m infield — just past the box, loose-ball zone
export const CORNER_REBOUND_Z = 7.0; // m from goal centre, corner side

// ── Corners — defending roles: near-post defender, central markers,
// far-post defender, GK positioned, edge-of-box defender, counter outlet. ──
export const CORNER_GK_LINE_INSET = 0.3; // m off the goal line
export const CORNER_GK_NEAR_POST_BIAS = 1.0; // m toward the near post
export const CORNER_POST_DEFENDER_INSET = 0.5; // m off the goal line, post-cover defenders
export const CORNER_COUNTER_OUTLET_X = 12.0; // m beyond halfway, into the defending team's own attack
export const CORNER_MARKER_COVER_DEPTH = 9.0; // m infield — overflow central markers' generic cover
export const CORNER_MARKER_COVER_SPREAD_Z = 4.0; // m between successive overflow markers

/**
 * Score+time context (§10.2-style: a decision weight, not a stat boost):
 * losing AND inside this fraction of the current half's remaining time (added
 * time included) sends an extra man forward for the corner instead of resting
 * one at the back. Only considered in the second half — a team down at
 * minute 3 of a 45-minute half is not yet "leaving it all out there".
 */
export const CORNER_LATE_GAME_REMAINING_FRAC = 0.15;

// ── Throw-ins (Law 15) — attacking roles: taker, short receiver, line
// runner, inside support, backward safety, rest defence. Depths are measured
// along the touchline from the throw spot (forward = attack direction,
// backward = the safety out-ball); width insets bring a role in off the
// touchline toward the centre of the pitch. ────────────────────────────────
export const THROW_TAKER_INSET = 0.5; // m off the exact spot — clear of the resting ball's collision footprint
export const THROW_SHORT_RECEIVER_DEPTH = 6.0; // m up the touchline
export const THROW_SHORT_RECEIVER_WIDTH_INSET = 3.0; // m in from the touchline
export const THROW_LINE_RUNNER_DEPTH = 14.0; // m up the touchline — the furthest option
export const THROW_INSIDE_SUPPORT_DEPTH = 5.0; // m up the touchline
export const THROW_INSIDE_SUPPORT_WIDTH_INSET = 9.0; // m in — cuts inside
export const THROW_BACKWARD_SAFETY_DEPTH = 6.0; // m BEHIND the thrower — a recycling out-ball
export const THROW_BACKWARD_SAFETY_WIDTH_INSET = 4.0; // m in from the touchline

// ---------------------------------------------------------------------------
// Restart WALK-IN staging (§5.1–§5.3; Laws 13/14/15/17). Appended at the END
// of the file for the same reason the two blocks above give: other agents
// append here concurrently, so new tunables land in one contiguous block.
//
// Set-piece staging used to write every staged player's role target straight
// into px/pz on the first Setup tick. The SHAPE that produced was right (and
// is unchanged) — the way players GOT there was not football: a one-frame
// teleport of up to ~80 m, the corner taker materialising on the flag, the
// wall appearing on the 9.15 m mark, everyone in the box vanishing to its edge
// for a penalty. These three tunables turn staging into a MOVE at a human
// speed, and size the Setup window to the longest walk so the ceremony is
// still SETTLED before the taker is allowed to play the ball.
// ---------------------------------------------------------------------------

/**
 * How fast a player walks/runs into his staged set-piece position (m/s).
 *
 * A set piece is a purposeful run, not a stroll and not a flat-out sprint, and
 * it has to cover real distance (a corner taker can be 60-80 m from the flag at
 * the moment the ball goes out) inside a ceremony a player is willing to sit
 * through. 5.5 m/s is 19.8 km/h.
 *
 * This is the TOP of the gait ladder — the pace of the long haul. See the
 * RESTART_STAGE_JOG_SPEED block at the end of this file for the rest of it, and
 * for why the velocity a staged player carries must MATCH the step actually
 * applied instead of being zeroed.
 *
 * ⚠ It sits MID-BAND in AnimState.Run (MAX_SPEED_JOG 3.5 < 5.5 < MAX_SPEED_RUN
 * 6.0), same rule as the other two rungs, and it was 6.0 for exactly one release
 * of this code — which is precisely ON locomotionAnim's Run/Sprint boundary.
 * MEASURED: the authored velocity is |Δp|/FIXED_DT, so it lands at
 * 6.0 ± ~2e-13, and every staged player flickered between AnimState.Run and
 * AnimState.Sprint tick by tick as the sign of that last-bit error changed. Do
 * not raise it back to a band edge.
 */
export const RESTART_STAGE_SPEED = 5.5; // m/s

/**
 * Slack added to the computed walk-in time when a restart's Setup window is
 * sized, so the last man is genuinely STOOD on his mark rather than still a
 * stride short of it when the restart becomes takeable. Physics settling and
 * the soft player-player push (step.ts stage 8) both nibble at the final few
 * centimetres, and the whole point of the window is that the shape has
 * SETTLED before play may resume.
 */
export const RESTART_STAGE_SETTLE_PAD_S = 1.0; // s

/**
 * Hard cap on how far a restart's Setup window may be extended beyond its base
 * ceremony length to wait for the walk-in.
 *
 * A restart must NEVER be able to hang the match. The window is computed ONCE,
 * from real distances, at the moment the restart is awarded (rules/fsm.ts
 * sizeSetupWindowForWalkIn) and clamped here — so the FSM's countdown always
 * expires and Setup always ends, however pathological the positions are. It is
 * deliberately NOT a per-tick "hold until everyone has settled" gate: that has
 * no upper bound, and one player the AI keeps dragging off his mark would
 * freeze the match forever.
 *
 * Sized to cover the LONGEST walk-in the pitch can pose plus the gait ladder's
 * arrival, because a window that clamps below the walk it has to cover ends the
 * ceremony with players still in transit. The furthest a taker's mark has been
 * measured from him is 80.57 m (both squads in their own halves, a corner at the
 * far flag — restart-walk-in.test.ts's fixture), which under the ladder is
 * (80.57 − 6)/5.5 + 1.5 + 1.25 = 16.31 s, + RESTART_STAGE_SETTLE_PAD_S = 17.31 s.
 * With RESTART_SETUP_S = 1.5 that needs 15.81; 16.0 gives a little slack.
 *
 * ⚠ MEASURED, not guessed. At 12.0 the cap and the walk it had to cover were
 * 0.07 s apart: that same 80.57 m walk took 80.57/6 = 13.43 s against a
 * 1.5 + 12.0 = 13.50 s window — four ticks of margin, entirely by luck. The
 * ladder's arrival pushed the walk past the cap and the corner went Ready with
 * its taker 2.42 m short of the flag (restart-walk-in.test.ts's settle assertion
 * caught it).
 *
 * Only a pathological restart ever reaches this cap: beginRestart hands the take
 * to the NEAREST teammate, so a real 11v11 corner taker is never 80 m out, and
 * rest-defence marks are excluded from the sizing outright (stageTargetCritical).
 * The 80.57 m figure above is that deliberately-extreme fixture (both squads
 * parked in their own halves, corner at the far flag), where the ceremony runs
 * 17.2 s; a goal kick from the same fixture is 1.5 s, i.e. the base ceremony with
 * no extension at all, because Law 16 only stages opponents already in the box.
 */
export const RESTART_STAGE_EXTRA_MAX_S = 16.0; // s

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║ GOALKEEPER DISTRIBUTION (§4.5 "throw (short/long) / drop-kick / punt")     ║
// ║ Appended as ONE contiguous block at the END of the file, for the reason    ║
// ║ every block above gives: several agents append here concurrently.          ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
//
// Owner report, verbatim: *"the goal keeper not only kicks he can also throw the
// ball"*. A real keeper has three distributions, not one, and picks between them
// on football grounds:
//   ROLL-OUT  — bowled underarm along the deck to a near defender who is FREE.
//   THROW     — overarm, mid-range, to a full-back / wide midfielder.
//   PUNT      — the boot, for distance or when he is being closed down.
// The first five constants below are PROMOTIONS: they were locally-derived
// `const`s at the top of goalkeeper.ts, flagged there ("⚠ PROMOTE THESE TO
// constants.ts WHEN THE FREEZE LIFTS") because constants.ts was frozen for a
// concurrent agent at the time. The freeze has lifted; §15.5 wants them here.

/**
 * Time on the ground after a dive before the keeper is back up. (§4.5)
 * Promoted from goalkeeper.ts unchanged (0.9).
 */
export const GK_DIVE_RECOVERY_S = 0.9; // s

/**
 * Fraction of pace a parried ball keeps off the gloves. (§4.5)
 * Promoted from goalkeeper.ts unchanged (0.55). Below 0.5 the spill dies at the
 * keeper's feet and every save becomes a re-gather; above ~0.7 the rebound flies
 * to the edge of the box and the striker is never in it.
 */
export const GK_PARRY_SPEED_RETENTION = 0.55;

/**
 * How long an AI keeper surveys before distributing, mapped from Vision
 * (MAX at Vision 0, MIN at Vision 100).
 *
 * ⚠ MAX must stay well inside GK_HOLD_LIMIT_S (8 s) or the AI concedes a corner
 * to its own dithering under the IFAB 2026/27 8-second law (§5.5).
 *
 * ── 2026-07-25: 3.6/1.2 → 2.0/0.6 (Session 36, "bots are reacting late to kick
 *    or throw the ball") ──────────────────────────────────────────────────────
 *
 * MEASURED on the 11v11 soak (seeds 7/42/424242 × 3 sim-min): every keeper hold
 * in the sample lasted EXACTLY 144 ticks = 2.40 s, because a procedural roster's
 * Vision sits at the ±15 % band around 50 (§8.1) and the remap lands there. Two
 * and a half seconds of a keeper standing motionless with the ball in his hands
 * is precisely the "late to throw the ball" the owner reported, and it is the one
 * dwell in the game a viewer is guaranteed to see from the front, because play
 * has stopped and the camera is on him.
 *
 * The old pair was a straight promotion of goalkeeper.ts's original locals and
 * had never been measured against the ball. It is also not what keepers do: the
 * modern keeper is looking for the out-ball as the save is completed, and the
 * IFAB 8-second law exists precisely because holding it longer than ~2 s is
 * considered time-wasting. Real released-in times from a catch cluster around
 * 1-2 s.
 *
 * So: a poor-Vision keeper now dithers 2.0 s (still visibly a survey, still an
 * age in football terms), and a top-Vision keeper releases in 0.6 s — a
 * deliberate beat, never zero, so he does not look like a ball-return machine.
 * A neutral 50-Vision keeper lands at 1.30 s, a 45 % cut on the 2.40 s measured.
 * MIN stays comfortably above GK_REACTION_MAX_MS (0.22 s) + the
 * GK_OWN_TOUCH_REFRACTORY_S (0.2 s) that stops him re-gathering his own release,
 * so the release path is unchanged mechanically — only its timing moves.
 */
export const GK_AI_SURVEY_MIN_S = 0.6; // s
export const GK_AI_SURVEY_MAX_S = 2.0; // s

/**
 * Furthest a keeper will THROW before it becomes a KICK. (§4.5 "throw
 * (short/long)"). Promoted from goalkeeper.ts unchanged (32 m).
 *
 * A real overarm throw reaches roughly the halfway line from the six-yard box —
 * ~32 m of carry is the top of the human range and matches the keepers who are
 * known for it. Past this the arm cannot beat the boot and he punts.
 */
export const GK_THROW_MAX_RANGE_M = 32; // m

/**
 * ONE radius, used for the SAME question about two different players:
 * "is anybody close enough to be ON him before the ball is gone?"
 *
 *   - applied to the KEEPER   ⇒ he is PRESSED, so he does not put his hands near
 *     the floor to bowl a ball out; he punts it and the danger goes with it.
 *   - applied to a TEAMMATE   ⇒ that man is FREE, so rolling it to him is
 *     building from the back rather than gifting a chance.
 *
 * Sized, not tasted. The slowest legal release is the rolled-out throw at
 * GROUND_PASS_MIN_SPEED (8 m/s). It is untouchable for GK_OWN_TOUCH_REFRACTORY_S
 * (0.2 s), and the keeper's own worst-case biology before he can even begin is
 * GK_REACTION_MAX_MS (0.22 s). An opponent closing at MAX_SPEED_SPRINT covers
 * 8.5 × 0.42 = 3.57 m in that time; add POSSESSION_RADIUS (1.2 m), inside which
 * he is already on the ball, and the answer is 4.77 m. Rounded to 5.
 *
 * Deliberately NOT reusing GK_DIVE_REACH (2.4 m): that is how far his hands go,
 * which is the right envelope for "can he claim it" (resolveSave uses it for
 * exactly that) and much too tight for "has he time to distribute calmly".
 */
export const GK_PRESSURE_RADIUS = 5.0; // m

// ---------------------------------------------------------------------------
// Restart walk-in GAIT LADDER (§5.1–§5.3). Appended at the END of the file for
// the same reason every block above it is: several agents append here
// concurrently, so new tunables land in one contiguous, clearly delimited block.
//
// THE DEFECT THIS FIXES, verbatim: "when corner or throw in happens bots are
// getting dragged they are not running / please make sure each and every player
// runs or walks or sprint / no forced dragging".
//
// Two separate causes, both here:
//
//   1. The walk-in ZEROED a staged player's velocity (so step.ts stage 4 could
//      not integrate it a second time). But EVERY downstream consumer reads
//      velocity: player-controller.ts stepAnim picks the locomotion clip from
//      |v| alone, so a man being stepped 0.1 m per tick with v = 0 was rendered
//      IDLE while his position translated. That is precisely "dragged" — a
//      player sliding across the grass with no gait under him. The double-move
//      is now prevented by GATING his locomotion (rules/stage-lock.ts) instead
//      of by lying about his velocity, so |v| genuinely equals |Δp|/dt.
//
//   2. Everyone moved at ONE speed whatever he had to cover, so a 1.5 m
//      adjustment travelled at the pace of a 60 m cross-pitch haul: 21 players
//      gliding at a uniform rate. Real footballers RUN the length of the
//      walk-in, ease to a JOG as they arrive, and take the last stride or two
//      at a WALK.
//
// The three speeds are deliberately centred INSIDE the three locomotion anim
// bands (player-controller.ts locomotionAnim: Walk ≤ 1.4 < Jog ≤ 3.5 < Run ≤
// 6.0 < Sprint), never near a boundary — so each band resolves to one
// unambiguous clip and a decelerating player cannot flicker between two.
// ---------------------------------------------------------------------------

/**
 * Staging pace once a player is inside RESTART_STAGE_JOG_DIST of his mark (m/s).
 *
 * Sits mid-band in AnimState.Jog (WALK_SPEED_THRESHOLD 1.4 < 3.0 < MAX_SPEED_JOG
 * 3.5) — 0.5 m/s clear of the Run boundary above and 1.6 m/s clear of the Walk
 * boundary below, so the arrival reads as an unambiguous jog for its whole
 * duration.
 */
export const RESTART_STAGE_JOG_SPEED = 3.0; // m/s

/**
 * Staging pace for the last stride or two into the mark (m/s).
 *
 * Sits mid-band in AnimState.Walk: comfortably above ANIM_IDLE_SPEED (0.15 —
 * below that he would read as standing still while still translating, i.e. the
 * original bug in miniature) and comfortably below WALK_SPEED_THRESHOLD (1.4).
 * It is also the pace the client's own locomotion ladder was retuned to call a
 * walk (Session 30: walk knot 1.6 → 1.2).
 */
export const RESTART_STAGE_WALK_SPEED = 1.2; // m/s

/**
 * Remaining distance to the mark, in metres, at which a staged player eases from
 * the RESTART_STAGE_SPEED run down to RESTART_STAGE_JOG_SPEED.
 *
 * 6 m is about a second and a half of jogging — the distance over which a
 * footballer arriving at a set-piece mark actually stops driving and starts
 * placing his feet. Cheap in ceremony time: the whole jog band costs
 * (6 − 1.5)/3.0 = 1.5 s regardless of how far he came from.
 */
export const RESTART_STAGE_JOG_DIST = 6.0; // m

/**
 * Remaining distance to the mark, in metres, inside which the step becomes a
 * WALK. Roughly one stride: below this a player is adjusting his position, not
 * travelling, and a jog would visibly overshoot-and-brake.
 *
 * Costs 1.5/1.2 = 1.25 s of the ceremony, which sizeSetupWindowForWalkIn
 * accounts for exactly (stageTravelTime) — the Setup window must always be sized
 * with the SAME ladder the mover applies, or the shape will not have settled
 * when the referee's whistle goes.
 */
export const RESTART_STAGE_WALK_DIST = 1.5; // m

/**
 * How fast a staged player swings his body around during a walk-in (deg/s).
 *
 * Between TURN_RATE_MIN_DEG (160, at a sprint) and TURN_RATE_MAX_DEG (360, close
 * control / stationary), because a walk-in is a jog: he turns freely but not
 * instantly. It has to be a REAL rotation and not a snap for the same reason the
 * velocity has to be real — locomotionAnim compares facing against the direction
 * of travel and shows a STRAFE clip beyond STRAFE_ANGLE_COS (60°), so a player
 * whose yaw never caught up with his walk-in would shuffle sideways to his mark
 * for its whole duration.
 */
export const RESTART_STAGE_TURN_RATE_DEG = 270; // deg/s

/**
 * How close to his mark (metres) counts as ON it for the mover.
 *
 * Distinct from the FSM's own RESTART_STAGE_SETTLED_EPSILON (1e-6, pure float
 * noise): this one is the threshold at which the mover stops authoring a
 * velocity and lets the player stand genuinely still, waiting for the whistle.
 * A millimetre is far too small to hide a player who is really still a stride
 * out, and large enough that the soft player-player separation push (step.ts
 * stage 8) nudging him 0.1 mm off his mark does not re-arm a 0.1 mm "walk" with
 * a 0.006 m/s velocity on it every tick.
 */
export const RESTART_STAGE_ON_MARK_DIST = 1e-3; // m

// ===========================================================================
// AI TEAM CONNECTION (Session 35, ai/ agent) — A HYPOTHESIS THAT WAS MEASURED
// AND REJECTED. Recorded so nobody re-derives it and re-lands it.
//
// The shape numbers looked damning: over the tactical soak the two STs stationed
// themselves ~32-35 m clear of their own midfield line (gapMidFwd), the team
// spanned ~64 m of a 105 m pitch (teamLength), and of the carrier's FORWARD
// teammates only 15 % survived the pass gates — with HALF of those survivors
// beyond AI_PASS_MAX_COMFORTABLE_DISTANCE (30 m), median 30.3 m, i.e. sitting
// exactly on the distance-penalty cliff. The obvious read: the forwards are
// unreachable, so cap how far ahead of the BALL a support run may station itself
// (AI_SUPPORT_MAX_BALL_LEAD = 22 m, ~3/4 of the comfortable pass distance) and
// the carrier will suddenly have a forward option.
//
// It was implemented and A/B'd against the same sim snapshot, 4 seeds × 5
// sim-minutes each. IT MADE FOOTBALL WORSE, and it did not even fix the number it
// was aimed at:
//
//                          no cap    with cap
//   median survivor dist    29.3 m    29.4 m   ← the thing it was FOR: unmoved
//   forward opts / decision   0.47      0.43
//   shots                       30        25
//   box entries                 45        40
//   ball under control      29.4 %    26.3 %
//   carrier w/ no reachable
//     forward support       32.9 %    39.7 %   ← it made this WORSE
//
// WHY (the useful part): pulling the forwards back does not create a forward
// option, it just moves the crowd. Their markers come back with them, so the
// lanes around the ball get MORE congested (lane-blocked forward teammates rose
// 23.5 % → 24.1 %) while the stretch that was pinning the opposition back line
// disappears. The influence-map search (AI_SUPPORT_SEARCH_RADIUS) and the shape
// steering then drag the station around anyway, which is why the survivor
// distance did not budge.
//
// The real cause of the starvation was not geometry at all — it was that the
// carrier was FACING THE WRONG WAY and so never scored the forward passes that
// were already there (70 % of his forward teammates sat outside his §10.2 facing
// cone). That is fixed in ai/individual.ts, costs no constant, and is what
// actually moved these numbers. If you are tempted by a compaction cap again,
// re-read this block and go measure the facing gate first.
// ===========================================================================

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║ AI STRIKE BUFFERING (Session 36) — "bots are reacting late to kick or      ║
// ║ throw the ball". Appended as ONE contiguous block at the END of the file,  ║
// ║ per the reason every block above gives: several agents append here.        ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * How long an AI holds a DECIDED strike, waiting for the ball to actually be
 * on his boot, before the decision goes stale and is dropped. Ticks.
 *
 * ── The bug this exists to close (MEASURED, seeds 7/42/424242 × 3 sim-min) ──
 *
 * ai/individual.ts's applyDecisionToInput used to emit the Pass/Shoot flag on
 * the tick the decision was MADE and charge AI_KICK_COOLDOWN_TICKS (18) for it
 * unconditionally — with no idea whether ball.ts's findStriker would honour it.
 * It very often would not, because a strike needs the ball inside
 * BALL_PLAY_REACH (0.7 m) and a dribbling carrier only has it there 53 % of the
 * time (the knock runs it out to ~0.85 m between touches). So:
 *
 *   - 90 % of all AI kick-intent ticks produced NO strike at all
 *     (622 burned vs 68 landed), and
 *   - 59 % of every on-ball decision that concluded "shoot / pass / cross" never
 *     reached the input layer, because the cooldown from the PREVIOUS burned
 *     intent was still running — and decideOnBall's `canKick` gate then dropped
 *     the man through to the SHIELD branch, i.e. he turned and walked away from
 *     the pass he had just decided to play.
 *
 * That loop — decide, burn, shield 0.3 s, decide again — is the visible dwell:
 * median 18 ticks (0.30 s) and p90 52 ticks (0.87 s) between "he could legally
 * play the ball" and "he did".
 *
 * The fix is the one the client already applies to a HUMAN strike (7dfce32
 * "buffer the strike so a shot fired while RUNNING actually happens"): keep the
 * intent latched and fire it on the first tick the ball is genuinely reachable.
 * The cooldown is then charged on CONTACT, not on hope.
 *
 * ── Why 12 and not "hold it forever" ────────────────────────────────────────
 *
 * 12 ticks = 0.2 s. Two independent bounds meet there:
 *
 *  1. It must cover one dribble knock-and-run cycle, or the buffer does not do
 *     its job. sim/ball.ts's knock leaves the ball outside the boot for ~11
 *     ticks at sprint (measured in the carry-separation fix, SESSION_LOG
 *     "Untouched window 23 → 11 ticks"). 12 clears it with a tick to spare.
 *  2. The aim yaw and pass weight were solved from the positions at DECISION
 *     time, so a buffered strike is that much stale. 0.2 s is ~0.6 m of
 *     receiver movement at a jog — inside what resolveAim's lead already
 *     absorbs (§4.2). Much longer and the AI would be passing to where a man
 *     used to be, which is a worse defect than the dwell it fixes.
 *
 * Layer 2 re-decides at 10 Hz and clears the latch when it does, so in practice
 * this cap only bites while a reaction delay is holding a decision open.
 */
export const AI_STRIKE_BUFFER_TICKS = 12;

// ╔══════════════════════════════════════════════════════════════════════════╗
// ║ KICK DIRECTIVE (owner, 2026-07-25):                                      ║
// ║   "remove the curve kick for now and make sure kicks are accurate and    ║
// ║    can be kick on walk , run , air"                                      ║
// ║ Earlier, same owner: "kicks are not accurate ... make sure the kicks are ║
// ║ 100% accurate".                                                          ║
// ║                                                                          ║
// ║ TWO switches, both here, both REVERSIBLE by flipping one boolean. Both   ║
// ║ live in the SIM so AI, online play and the client all agree on the ball. ║
// ╚══════════════════════════════════════════════════════════════════════════╝

/**
 * ── CURVE OFF, "for now" (owner directive above) ────────────────────────────
 *
 * The curve mechanic is NOT deleted — the code, the constants
 * (MOUSE_CURL_SIDESPIN, FINESSE_SIDESPIN, PASS_CURL_POWER_FLOOR,
 * DRIVEN_PASS_CURL_FACTOR) and the whole curl→sidespin→Magnus chain are all
 * intact. This is the PRODUCTION DEFAULT for `MatchConfig.curveEnabled`, which
 * zeroes the DELIBERATE LATERAL spin at the two strike sites that produce it
 * (buildShot, buildPass in ball.ts). **Flip this ONE value back to `true` and the
 * curve returns exactly as it was, everywhere.**
 *
 * WHY IT IS READ THROUGH MatchConfig AND NOT DIRECTLY: a shipped-off mechanic
 * whose only coverage asserts the DEAD path rots silently, and re-enabling it
 * later is a leap in the dark. The gate is per-world config, so the curve suites
 * (curve.test.ts, curve-physics.test.ts, ball-curve.test.ts) build their world
 * with `createMatchConfig(11, { curveEnabled: true })` and keep proving the
 * curl→sidespin→Magnus chain actually bends the ball, while every production
 * world takes the default below and flies straight. No module-level mutation is
 * involved, so nothing leaks between tests and determinism is untouched.
 *
 * What is gated: the drawn mouse curl on shots AND passes, and the finesse
 * shot's built-in sidespin (that is a curve too — a finesse shot bent ~1.3 m
 * over 25 m). The finesse POWER multiplier is untouched, so the archetype still
 * exists as a placed, lower-power shot.
 *
 * What is NOT gated: back/topspin (float and dip). Those are vertical, not a
 * lateral bend, and the owner asked for kicks to go where they are AIMED —
 * which is a horizontal statement. Ground/driven-pass backspin, the lift-bias
 * float/dip and the bicycle's backspin all still act.
 */
export const CURVE_ENABLED = false;

/**
 * ── AIMED-STRIKE ERROR OFF (owner: "100% accurate") ─────────────────────────
 *
 * The gaussian aim cone on a FOOTED, AIMED strike — buildShot and buildPass.
 * Measured before this flag (8 seeds/cell, neutral 70 stats, zero pressure,
 * error measured as the perpendicular miss of the aim ray at the nominal range):
 *
 *   shot, 30 m, standing  → 2.17° mean / 3.45° worst = 1.11 m / 1.77 m off
 *   shot, 30 m, sprinting → 5.06° mean / 7.04° worst = 2.67 m / 3.73 m off
 *   shot, 18 m, sprinting → 3.63° mean / 4.97° worst = 1.15 m / 1.57 m off
 *   pass, 30 m, sprinting → 0.47° mean / 0.87° worst = 0.24 m / 0.46 m off
 *
 * A 2.7 m mean miss at 30 m is most of a goal's width: aim at a post and the
 * ball leaves the frame. That is the report, twice over.
 *
 * WHY THIS IS SAFE FOR DIFFICULTY: the AI does NOT depend on this cone. It adds
 * its own difficulty-scaled aim error upstream in ai/individual.ts
 * (`aimWithError`, ai/difficulty.ts `aimErrorRad` = 0.14 easy / 0.075 medium /
 * 0.03 hard rad ⇒ 8.0° / 4.3° / 1.7° at 1σ — four to seven times the cone this
 * flag switches off, so it dominates), which is untouched — so switching this off
 * makes the HUMAN's aimed kick land where it is pointed while the AI stays
 * imperfect and beatable. That separation is why the human/AI distinction can be
 * made at all without a "who is human" field in the sim (there is none).
 *
 * WHAT KEEPS ITS ERROR (deliberately — these are not aimed kicks):
 *   - HEADER  (HEADER_BASE_ERROR_RAD / HEADER_VERTICAL_ERROR_DEG): a header is a
 *     redirect off the forehead, not a struck kick.
 *   - BICYCLE (BICYCLE_ERROR_MULTIPLIER): spectacular, unreliable, by design.
 *   - The AI's own aimWithError, as above.
 *
 * RNG NOTE (§13.1, D10): the gain MULTIPLIES the draw, it does not remove it.
 * `pcg32GaussianClamped` is still called, in the same order and count, so the
 * shared RNG stream is byte-identical and every recorded replay still
 * reproduces. Do not "optimise" the dead draws away — that WOULD break them.
 * This is ALSO why the gate is per-world `MatchConfig.strikeAimErrorEnabled`
 * rather than an `if` around the strike: the draw COUNT must not depend on the
 * toggle, or a test that flips it on would desync the shared stream against
 * production. Multiply by 0/1; never branch around the draw.
 *
 * The value below is the PRODUCTION DEFAULT for `MatchConfig.strikeAimErrorEnabled`.
 * strike-error.test.ts builds its world with `{ strikeAimErrorEnabled: true }` so
 * the cone's 2-D shape, its stat/pressure scaling and the power-shot penalty stay
 * genuinely proven while the cone is shipped off.
 *
 * SIDE EFFECT, worth knowing: with the vertical term gated, a shot's launch
 * angle can no longer be pushed NEGATIVE by error (SHOT_ANGLE_MIN_DEG = −4 is
 * now unreachable for liftBias ≥ 0). That removes the only path by which an
 * aimed kick launched slightly DOWNWARD out of a ball flush on the turf, which
 * is the configuration where sweepBall's flush-slab guard is bypassed and Rapier
 * decides at zero separation whether the first integration step survives — a
 * ~0.5 m along-track lottery that looked exactly like "kicks are not accurate".
 */
export const STRIKE_AIM_ERROR_ENABLED = false;

// ╔══ CARRY CORNER LIMIT (2026-07-25) ═════════════════════════════════════════╗
// "when running with ball holding shift and turn ball gets left" (user, verbatim).
//
// MEASURED FIRST — sim/dribble-carry-separation.test.ts, the yaw-rate sweep. A
// sprint carry round a SUSTAINED arc:
//
//   arc      radius asked   ball-to-feet p50   behind him   for   spdP50
//   35 °/s        11.5 m              0.82 m      0.00 m      0    7.00
//   50 °/s         8.0 m              1.08 m      1.34 m    246    2.50
//   70 °/s         5.7 m              1.24 m      1.50 m    277    2.50
//   90 °/s         4.5 m              1.30 m      1.61 m    278    2.50
//
// i.e. from 50 °/s up — an EIGHT METRE radius, an ordinary bend in a run, not a
// trick — the ball came off the boot, settled behind him and never came back: the
// separation grew monotonically for the whole 6 s tape (1.34 → 1.61 m as the arc
// tightens, and still growing at the last tick) while the game reported 100 %
// possession and the carry governor pinned him to a 2.5 m/s jog. Neither carrying
// nor conceding.
//
// WHAT THE FRONTIER SWEEP SHOWED (the fix falls straight out of it). Ball kept
// (behindMax 0.00) or lost, by carrier speed × arc, over dribbling 20/50/90:
//
//   speed    35 °/s   42 °/s   46 °/s   50 °/s   90 °/s   120 °/s
//   7.00       keep     keep    lost*     lost     lost      lost
//   5.95       keep        -        -      lost     lost      lost
//   5.60       keep     keep     keep      keep     keep      keep
//   5.50       keep     keep     keep      keep     keep      keep
//                                (* dribbling 90 still keeps 46 and 50)
//
// The cliff is not at a speed and not at a curvature: it is exactly
// DRIBBLE_KNOCK_MIN_SPEED (5.8). ABOVE it the carry is a knock-and-run — discrete
// touches, the ball riding ~0.82 m ahead of the boot — and a running line rotating
// at ω sweeps that lead point sideways faster than the next knock can walk it back
// (DRIBBLE_KNOCK_LINE_RECOVER_MAX), so the ball leaves the boot's 0.7 m reach and
// then no touch can ever fire again. BELOW it the shepherding spring owns the ball
// and tracks a 0.6 m lead point every tick: measured, it holds the ball 0.58-0.68 m
// off the boot round a 120 °/s arc at every dribbling stat. So the honest fix is
// not a tighter touch model — it is that a man cornering hard with the ball is not
// knocking and running, he is carrying it in close control.
//
// This is the same statement §3.3 already makes about turning ("a player who wants
// to turn fast has to slow down first"), applied to the ball rather than the body.
// It is also what the alternative could not do: making OWNERSHIP directional would
// only relabel a ball 1.2-2.35 m behind him as loose — the ball would still be
// behind him, which is the thing the owner complained about.

/**
 * Sustained heading rotation (deg/s) a carrier may hold at FULL dribble-sprint
 * pace before he is held to close control.
 *
 * 42 is not a taste number: it is the knock-and-run's own ceiling, measured across
 * the whole dribbling range. At 7.0 m/s a carrier holds the ball round a 42 °/s arc
 * at dribbling 20, 50 AND 90 (behindMax 0.00 m, ball 0.80-0.82 m ahead of the
 * boot); by 46 °/s the worst dribbler has lost it (1.28 m behind for 196 ticks) and
 * the median one is going (1.13 m, 107 ticks). Set at the rate the WORST carrier
 * can still hold, not the median one, or the fix has a hole in it for exactly the
 * players who need it most. 42 °/s at 7.0 m/s is a 9.5 m radius — a wide bend,
 * which is what a knock-and-run physically is.
 *
 * The comparison is strict (`>`), so an arc sitting exactly ON 42 °/s is held to
 * close control rather than being the last full-pace one; full pace is retained up
 * to just under it (measured at 38 °/s: 7.00 m/s, ball 0.81 m ahead, even at
 * dribbling 20 — that row is asserted in dribble-carry-separation.test.ts).
 *
 * Applied to the rotation of the MOVEMENT heading actually achieved this tick, not
 * the requested one: a stick-slam is already handled by the §3.3 residual-angle
 * scrub (target speed × cos(residual) → a hard turn costs the speed anyway), and
 * this must only bite on a turn the carrier is genuinely TRACKING — a sustained
 * arc. On a followable arc at steady state the two are the same number, because
 * rotateTowards lands exactly on the request.
 */
export const CARRY_TURN_FULL_PACE_YAW = 42; // deg/s

/**
 * Absolute speed ceiling (m/s) for a carrier turning harder than that.
 *
 * MUST stay below DRIBBLE_KNOCK_MIN_SPEED (5.8) — that is the whole mechanism, and
 * dribble-carry-separation.test.ts asserts the ordering so it cannot drift. 5.5 is
 * MAX_SPEED_DRIBBLE, i.e. this says exactly "cornering hard with the ball is close
 * control, not a sprint": Shift buys you nothing through a tight turn. Measured at
 * 5.50 / 5.53 / 5.60 m/s the ball stays 0.58-0.68 m off the boot round 50, 90 and
 * 120 °/s arcs at dribbling 20 and 90 alike, so there is real margin under the
 * regime boundary rather than a knife edge.
 *
 * ABSOLUTE, deliberately — it is applied after the Pace stat and fatigue, unlike
 * every other cap in the ladder. The constraint belongs to the BALL and the touch
 * model, not to the player's legs: a quicker player does not get to knock-and-run
 * round a tighter arc. Stat-scaling it would put a Pace-100 carrier
 * (5.5 × 1.15 = 6.3) straight back above the knock threshold, which is the bug.
 *
 * A step, not a ramp, and that is deliberate too: the sweep shows the regime
 * boundary IS a cliff (5.95 m/s loses a 50 °/s arc, 5.60 keeps a 120 °/s one), so
 * any intermediate cap between 5.8 and 7.0 would leave the carrier in the failing
 * regime while looking like a gentler fix. DECELERATION (12 m/s²) bleeds the
 * 1.5 m/s in 0.125 s, which reads as checking his stride to bend it round.
 */
export const CARRY_TURN_TIGHT_SPEED = 5.5; // m/s — must be < DRIBBLE_KNOCK_MIN_SPEED

/**
 * How closely a carrier must be TRACKING his requested heading for the corner
 * limit above to apply — cos(residual angle), i.e. the same `align` term §3.3
 * already uses to scrub speed out of a turn.
 *
 * This is what makes the limit a SUSTAINED-ARC rule instead of a tax on every
 * change of direction. A stick-slam saturates the turn rate for a few ticks, so
 * the achieved rotation reads far above CARRY_TURN_FULL_PACE_YAW even though the
 * carrier is not holding an arc at all — and §3.3 has ALREADY charged him for that
 * cut, because target speed is multiplied by this very cosine. Charging twice
 * measurably handed back another agent's win: without this gate a 30° instant cut
 * at full sprint pushed dribble-corridor's owned-perp from 0.45 m back to 0.94 m
 * (60°: 0.97 → 1.01), for no benefit to the sustained arc this exists to fix.
 *
 * 0.99 is 8.1° of residual, and the separation is clean rather than marginal: on an
 * arc the carrier CAN follow, rotateTowards lands exactly on the request every tick
 * so the residual is 0 and align is exactly 1.0; one tick into a 30° cut at sprint
 * the residual is 26.7° and align is 0.893.
 *
 * Not a hole for a turn faster than the carrier can track (align stays low, so this
 * gate stays shut): the align scrub itself drives his target speed toward zero
 * there, and as he slows the turn-rate curve gives him back enough rate to track
 * the arc — at which point align recovers and the corner limit takes over.
 */
export const CARRY_TURN_TRACKING_ALIGN = 0.99;
// ╚════════════════════════════════════════════════════════════════════════════╝

// ╔════════════════════════════════════════════════════════════════════════════╗
// ║ SITUATIONAL TEAM SHAPE (ai/situational-shape.ts). §8.2, §10.1 Layer 3.     ║
// ║ Appended as ONE contiguous block — three agents append to this file        ║
// ║ concurrently, so a new feature's tunables land together or not at all.     ║
// ║                                                                            ║
// ║ WHY THIS BLOCK EXISTS. Before it, the block's shape was a function of the  ║
// ║ six AI_PHASE_SHIFT_* numbers and nothing else: a side 3-0 down with two    ║
// ║ minutes left stood in exactly the shape it used at 0-0 in minute one, and  ║
// ║ "out of possession" only changed the block's HEIGHT, never its LENGTH or   ║
// ║ its WIDTH. These numbers are the situations a real side re-shapes for.     ║
// ║                                                                            ║
// ║ UNITS. Every dFx below is in the same normalized attack-axis units as      ║
// ║ POSITION_ANCHORS (sim/formation-anchors.ts): 1.0 = a half-pitch = 52.5 m   ║
// ║ on a full pitch, so 0.10 ≈ 5.25 m of depth. Every *_NARROW / *_SPREAD is   ║
// ║ a MULTIPLIER on the anchor's |fz| (its distance off the centre line).      ║
// ║ MEASURED against ai/tactical-metrics.ts (§E.4) — see situational-shape.ts. ║
// ╚════════════════════════════════════════════════════════════════════════════╝

/**
 * OUT OF POSSESSION the block COMPRESSES. The forwards come back toward the
 * midfield line and the whole side narrows — this is the single biggest reason
 * the measured team length was 64 m (a side, not a block): the base anchors put
 * the strikers 37 m off the midfield band and nothing ever pulled them back.
 *
 * The forward drop is ~3x the midfield drop on purpose. A striker who tracks all
 * the way back leaves nobody to pass to on the turnover (AI_FORWARD_DROP_DAMP
 * exists for exactly that reason); a striker who never comes back at all is the
 * 37 m hole. Halfway is the football answer.
 */
/**
 * 0.24 rather than the 0.16 this started at, because of a MEASURED interaction
 * with the phase table that eats half the in/out swing.
 *
 * Solving the shape directly with the ball in the middle third, gapMidFwd came
 * out 21.7 m defending and 25.9 m in possession — a 4.2 m swing, not the ~10 m
 * the R1+R2 forward terms nominally buy. The missing 5.7 m is AI_PHASE_SHIFT_*:
 * defending, the forwards' drop is damped (× AI_FORWARD_DROP_DAMP 0.45) while
 * the midfield takes the full −0.24, so the phase table OPENS the gap by 0.132 fx
 * out of possession; in possession the forwards' +0.06 × AI_FORWARD_PHASE_GAIN
 * (1.4) barely beats the midfield's flat AI_POSSESSION_PUSH, so it opens it by
 * only 0.024 fx. Net: the phase table narrows the gap by 0.108 fx exactly when
 * R2 is trying to widen it.
 *
 * At 0.24 a striker sits ~36 m from his own goal defending a mid-block — just
 * inside his own half, which is where a 4-4-2 striker actually stands — the
 * defending gapMidFwd falls to ~17.5 m, and the in/out swing measures ~8.4 m.
 * The deeper resting striker does NOT cost the counter: R4's surge and
 * SHAPE_BUILD_UP_FWD_OUTLET both push him back up the moment the ball is ours.
 */
export const SHAPE_OOP_FORWARD_DROP = 0.24; // ≈ 12.6 m
export const SHAPE_OOP_MID_DROP = 0.05; // ≈ 2.6 m
export const SHAPE_OOP_NARROW = 0.84; // wide roles tuck ~4 m in off the touchline

/**
 * IN POSSESSION the block SPREADS and pushes up: forwards stretch the opponent's
 * back line, the midfield follows to stay connected to them, and the wide roles
 * go to the touchline to make the pitch big. The forward push is deliberately
 * larger than the OOP drop so the in/out difference is visible rather than a
 * rounding error — the shape must be legible from the stands.
 */
export const SHAPE_POSS_FORWARD_PUSH = 0.14; // ≈ 7.4 m
export const SHAPE_POSS_MID_PUSH = 0.06; // ≈ 3.2 m
export const SHAPE_POSS_SPREAD = 1.08; // wide roles go ~2 m wider

/**
 * THE BLOCK SLIDES WITH THE BALL BY THIRD. The team AI's phase FSM already
 * splits the IN-possession thirds (BuildUp / Attack / FinalThird keyed on
 * AI_BUILD_UP_FX / AI_FINAL_THIRD_FX), but out of possession it only ever split
 * the pitch in HALF: Press if the ball was in their half, DefendBlock if not.
 * A real side has three defensive heights, and the middle one is the one it
 * spends most of the match in.
 *
 * MID_BLOCK_DEF_HOLD is the important one: AI_PHASE_SHIFT_DEFEND_BLOCK (−0.24)
 * times AI_DEFENDER_PHASE_GAIN (1.25) is −0.30, which on top of a −0.70 CB
 * anchor puts the back line ON its own goal line for a ball in the MIDDLE third.
 * That is not a mid-block, it is a siege. This holds the line up until the ball
 * is genuinely in our third, where the low block belongs.
 */
export const SHAPE_LOW_BLOCK_DROP = 0.05; // our third, defending: settle another ~2.6 m deeper
export const SHAPE_LOW_BLOCK_NARROW = 0.92; // …and squeeze the box
/**
 * Middle third, defending, ball in OUR OWN HALF: hold the back line up.
 *
 * The own-half gate is not cosmetic. The team FSM splits defending at the
 * HALFWAY line (Press above it, DefendBlock below), so the middle third
 * straddles two phases whose shifts differ by 0.5 fx. Applied to both halves of
 * it, this hold stacked on top of AI_PHASE_SHIFT_PRESS + AI_PRESS_LINE_PUSH and
 * squeezed team 1's measured gapDefMid to 8.2 m with a p10 of −8.9 m — the
 * midfield band standing BEHIND the back four. Gated to the own-half side, it
 * only ever cancels part of a drop, never adds to a push.
 */
export const SHAPE_MID_BLOCK_DEF_HOLD = 0.12;
export const SHAPE_HIGH_BLOCK_PUSH = 0.08; // their third, defending: whole block steps up to press
export const SHAPE_FINAL_THIRD_FWD_PUSH = 0.05; // our attacking third, in possession: forwards attack the box
export const SHAPE_FINAL_THIRD_SPREAD = 1.06; // …and the pitch is made as wide as it will go
export const SHAPE_BUILD_UP_DEF_DROP = 0.04; // our own third, in possession: split deep to receive

/**
 * BUILD-UP OUTLET — our own third, in possession: the forwards STAY HIGH.
 *
 * The one situation where a LONG team is the correct answer, and the fix for a
 * measured regression rather than a guess. Compressing the base anchors cut
 * counterProgression (§E.4 metric 9) from 1.97/3.10 m/s to 1.33/1.84, with p90
 * falling 7.21 → 4.88: winning the ball deep now left the outlet 20 m closer to
 * our own goal, so there was nothing to break INTO and the ball got recycled
 * instead. R4's counter surge only covers 0.75 s (AI_TRANSITION_TICKS); the
 * next four seconds of a counter are spent in BuildUp, and this is the term
 * that keeps a target up there for them.
 *
 * Football-true as well as metric-true: a side playing out from the back against
 * a high press deliberately keeps a striker on the last defender — the space is
 * behind that press, and a striker who drops in to help build-up removes the
 * only reason the press is risky.
 */
export const SHAPE_BUILD_UP_FWD_OUTLET = 0.2; // ≈ 10.5 m

/**
 * A COUNTER-ATTACK — possession JUST won (the team FSM's Transition window,
 * AI_TRANSITION_TICKS = 45 ticks / 0.75 s, on the side that won it). Commit
 * runners forward NOW, while the opponent is still shaped to attack: this is the
 * one moment in football where being out of shape is correct. The surge is the
 * biggest single dFx in this block for that reason.
 *
 * COUNTER-PRESS is the same 0.75 s window on the side that LOST it, and it is
 * the opposite instruction: do not drop off, squeeze — win it back in the six
 * seconds before they are organised. Expressed as WIDTH (the tightest narrow in
 * the block) rather than depth, because dropping off is exactly what a
 * counter-press must not do.
 */
export const SHAPE_COUNTER_FWD_SURGE = 0.22; // ≈ 11.6 m — get in behind
export const SHAPE_COUNTER_MID_SURGE = 0.12; // ≈ 6.3 m — support the break
export const SHAPE_COUNTER_SPREAD = 1.05;
export const SHAPE_COUNTERPRESS_MID_PUSH = 0.06;
export const SHAPE_COUNTERPRESS_NARROW = 0.8; // hunt the ball in a tight cluster

/**
 * SCORELINE + TIME REMAINING. Same precedent, same gate, as the corner logic's
 * CORNER_LATE_GAME_REMAINING_FRAC (rules/fsm.ts isLosingLateForExtraAttacker):
 * second half only, and only inside the last fraction of it. A side 1-0 down in
 * minute 3 of a 45-minute half is not yet "leaving it all out there".
 *
 * The fraction is LARGER than the corner rule's 0.15 because shape is a
 * continuous, cumulative commitment (you push up for the last ten minutes),
 * where an extra man at a corner is a single moment. It ramps 0 → 1 across the
 * window instead of switching on, so the shape drifts forward the way a chasing
 * side's does rather than snapping into a new formation on one tick.
 *
 * SHAPE_GOAL_SWING_FULL: the deficit at which urgency saturates. Two goals down
 * is already "everything forward"; three does not make a side push harder, it
 * just makes it later.
 */
export const SHAPE_LATE_GAME_REMAINING_FRAC = 0.3;
export const SHAPE_GOAL_SWING_FULL = 2;
/** Losing late: the whole side pushes up, the forwards more, and it goes wide
 *  looking for crosses. */
export const SHAPE_CHASE_PUSH = 0.16; // ≈ 8.4 m for the whole block
export const SHAPE_CHASE_FWD_EXTRA = 0.1; // …and another ≈ 5.3 m for the forwards
export const SHAPE_CHASE_SPREAD = 1.1;
/** Protecting a lead late: drop deeper and narrow — defend the middle, make them
 *  play round you. */
export const SHAPE_PROTECT_DROP = 0.12; // ≈ 6.3 m
export const SHAPE_PROTECT_NARROW = 0.88;

// ===========================================================================
// AI BALL-TRAVEL MODEL + PASS REACHABILITY  (session 38, ai/ agent)
//
// Owner directive: "make the passes priority … make sure they try not to kick
// ball always out … bot calculate the ball posi and go there".
//
// Every number in this block was MEASURED off the real pipeline before it was
// written down — see ai/ball-travel.ts's header for the sample tables and
// ai/ball-travel.test.ts for the pins that keep them honest. None of them is a
// feel knob: they describe what the ball already does.
//
// THE FINDING THEY ENCODE: a "rolling" ball in this sim is permanently
// SKIDDING. While grounded, contact resolves every tick with a normal speed of
// about g·dt, so applyContactFriction (ball.ts) fires EVERY tick and takes a
// Coulomb bite out of the slip. The result is 5-12 m/s² of deceleration —
// roughly 30x BALL_ROLLING_RESISTANCE (0.02·g = 0.196 m/s²). A 10 m/s ground
// pass travels about NINE METRES in total and stops. Any AI lead solved from
// `v·t`, or from BALL_ROLLING_RESISTANCE, is wrong by many metres.
// ===========================================================================

/**
 * Skid-regime deceleration: a = BASE + V2·v². Fitted to the per-tick decel of a
 * real 24 m/s roll (12.52 m/s² at 23.6 m/s, 9.30 at 15.0, 8.40 at 12.1, 7.00 at
 * 4.0) — the base is the Coulomb-capped friction bite, the v² term is aero drag.
 * Reproduces every sample in 4..24 m/s to within ~9 %.
 */
export const BALL_SKID_DECEL_BASE = 7.0; // m/s²
export const BALL_SKID_DECEL_V2 = 0.0095; // m/s² per (m/s)²

/**
 * The regime change, and it is a real DISCONTINUITY in the sim, not a smoothing
 * choice: measured decel drops from 7.00 m/s² at v = 4.0 to 4.13 m/s² at
 * v = 3.7. Below it the ball has spun up far enough that the tangential slip —
 * and with it the friction impulse — collapses, and the decay goes proportional
 * to speed (4.13/3.7 = 1.12, 2.28/2.0 = 1.14, 1.28/1.1 = 1.16 → 1.15/s).
 */
export const BALL_SKID_LOW_SPEED = 3.85; // m/s
export const BALL_ROLL_DECAY_PER_S = 1.15; // 1/s

/**
 * Integration substep for the travel model, and the horizon past which no AI
 * lead is worth solving. 50 ms tracks the 60 Hz sim to a few centimetres over
 * 2 s (pinned by ball-travel.test.ts); the horizon bounds the worst-case work
 * at 40 substeps × 4 fixed-point iterations per decision, at 10 Hz.
 *
 * 2.5 s is also a football bound: nothing worth planning happens beyond it —
 * a ball still rolling after 2.5 s has been recontested several times.
 */
export const BALL_TRAVEL_SUBSTEP_S = 0.05; // s
export const BALL_TRAVEL_MAX_HORIZON_S = 2.5; // s

/**
 * ARRIVAL SPEED is what makes a pass a pass. updatePossession (ball.ts) will
 * not give a receiver the ball unless the ball-vs-player closing speed is under
 * POSSESSION_CAPTURE_REL_SPEED (5.0 m/s), and tryIntercept only reaches ~0.46 m
 * off his body. MEASURED on the pre-fix build: completed AI passes arrived at a
 * median of 12.3 m/s and the ball was loose 43-63 % of all in-play time. A pass
 * that arrives above the capture gate is not a pass, it is a 50/50 the AI
 * started against itself.
 *
 * IDEAL is the speed the scorer aims for (comfortably under the gate, with room
 * for the receiver's own closing speed); MAX is the hard ceiling past which an
 * option is REJECTED outright while any alternative exists. The gap between
 * them is the graded band the penalty ramps across.
 */
export const AI_PASS_ARRIVAL_IDEAL = 3.5; // m/s
export const AI_PASS_ARRIVAL_MAX = 6.5; // m/s
/** Score cost of a full band's worth of over-speed. Large on purpose: an
 *  unplayable pass must lose to a plain sideways one, which is exactly the
 *  trade a real player makes. */
export const AI_SCORE_ARRIVAL_PENALTY = 0.9;

/**
 * MEASURED AND REJECTED — an UNDER-HIT penalty (AI_PASS_MAX_UNDERSHOOT 4.0 m,
 * AI_SCORE_UNDERSHOOT_PENALTY 0.5), kept here as a warning rather than deleted,
 * because it looks obviously right and is not.
 *
 * The idea: charge a pass that stops short of its target, since a ball dying in
 * front of the receiver is a 50/50 offered to the nearest defender. The trap is
 * WHICH target you measure the shortfall against. The sim AIMS at the lead point
 * (resolveAim → passControlFootPoint) but WEIGHTS the pass for the man's current
 * distance (buildPass's assist fit), so a ball played to a forward running at
 * 6 m/s routinely stops several metres short of the lead point — and that is
 * correct football: he is running onto it, and the ball dies at his feet.
 *
 * Scoring that gap as an under-hit charged a penalty on nearly every pass to a
 * moving teammate. A/B on the §8 cooperative soak (2 min AI-vs-AI, seed 7):
 * pass decisions fell 131 → 78 of ~657. Removed. The arrival-speed term is
 * evaluated at the MAN instead, which is the distance the fit is calibrated for.
 */

/**
 * ANTI-OUT-OF-PLAY. The owner's second complaint, verbatim: "make sure they try
 * not to kick ball always out". MEASURED before the fix, 3 seeds x 3 sim-min:
 * 18 balls out (10 per 5 sim-minutes) of which 61 % were a pass, through-ball or
 * clearance the AI chose to play — the ball ran through the receiver and off the
 * pitch, because nothing in the decision ever asked where it would end up.
 *
 * MARGIN is how far inside the touchline/goal-line the ball's RESTING point has
 * to be for the AI to consider a pass safe: it must be small enough that a real
 * wide pass down the line is still allowed (a winger genuinely receives 2 m off
 * the paint) and big enough to cover the aim error the sim adds after the AI has
 * decided (§4.2's pressure/pace error cone).
 *
 * LOFT_OVERRUN is the extra distance allowed for a LOFTED ball (through-ball /
 * cross), which lands and then bounces before it skids. The ground model does
 * not model the bounce, so a fixed conservative allowance stands in for it.
 */
export const AI_PASS_PITCH_MARGIN = 1.5; // m inside the line
export const AI_PASS_LOFT_OVERRUN = 6.0; // m of unmodelled bounce-and-roll

/**
 * The pass-power model the SIM will actually use, mirrored so the AI can predict
 * its own pass. buildPass (ball.ts) OVERRIDES the AI's solved charge whenever
 * choosePassTarget finds a receiver: speed = (PASS_ASSIST_SPEED_BASE +
 * PASS_ASSIST_SPEED_PER_M·d) scaled by the charge across PASS_ASSIST_CHARGE_SPAN,
 * clamped to the ground-pass band. These two constants are that formula's charge
 * factor at the low charge the AI actually holds, kept here rather than
 * recomputed so the mirror is visible and reviewable in ONE place.
 *
 * This is the same discipline ballReachableBeforeMan uses to mirror
 * resolveTackle: if the pass model is ever retuned, the AI must be retuned with
 * it, and a mirrored constant makes that a diff instead of a mystery.
 */
export const AI_PASS_ASSIST_CHARGE_FACTOR = 0.88;

/**
 * A grounded ball is a straight line and a real player reads it well; a ball in
 * the AIR curls, dips and bounces and he does not. The chaser therefore solves
 * the real model, but a ball still airborne at the meeting point keeps a slice
 * of the old damped guess so a lofted ball can still wrong-foot him (§10.2 —
 * an AI that solved the real trajectory would look clairvoyant).
 */
export const AI_AIR_INTERCEPT_TRUST = 0.85;

/**
 * OUT-OF-PLAY RISK on a pass, and it is the constant that answers the owner's
 * "make sure they try not to kick ball always out" directly.
 *
 * Scored as the fraction of the ball's own RUN-OUT (ai/ball-travel.ts
 * groundRunOut — where it actually comes to rest) that lies beyond the
 * touchline/goal-line, times this. A ball whose whole run is off the pitch
 * therefore costs a full unit of score, which is more than the biggest progress
 * bonus available (AI_SCORE_PASS_PROGRESS_WEIGHT + _BONUS + _FINAL_THIRD_BONUS):
 * a ball down the line that ends up in the stand loses to a square pass, which
 * is exactly the trade a real player makes.
 *
 * Deliberately a PENALTY and not a gate. A hard reject would let a carrier with
 * only bad options hold forever — the dwell f4d9fb3 fixed — so an unplayable
 * pass has to be able to lose to dribbling while still beating nothing.
 */
export const AI_SCORE_OUT_RISK_PENALTY = 1.6;

/**
 * A blind ball upfield (a clearance, or a restart with nobody to aim at) is
 * aimed INFIELD by this fraction of the player's own lateral offset: 0 = at the
 * centre line, 1 = straight up the X axis as before.
 *
 * Halving it is the football instruction "clear it into the middle, not along
 * the paint" — the middle is where your own midfield is. It is also what stops
 * the blind ball being a coin-flip throw-in from anywhere near the touchline:
 * the old code aimed at exactly ±X, then aimWithError rotated it, and then
 * buildPass re-scanned a 60° cone and re-aimed at whoever it found.
 */
export const AI_CLEARANCE_INFIELD_FRAC = 0.5;

/**
 * THE CARRIER'S SCAN. (§10.2; individual.ts collectPassOptions)
 *
 * MEASURED, gate by gate, over every FORWARD teammate a ball-carrier had across
 * 4 seeds: even after the head-up fix (58360f6), 39.5 % of them were rejected
 * for nothing but sitting outside the 140° facing cone. Four in ten forward
 * passing options were structurally invisible, and the carrier's only remaining
 * option was to dribble or shield — which is exactly what the owner was looking
 * at when he said the bots do not pass.
 *
 * EXTRA_DEG widens the cone used for PASS OPTIONS ONLY (never for reacting to an
 * opponent, picking a shot, or spotting a presser — `canSee` is untouched), from
 * 140° up to 220° for a carrier nobody is near. That is a scan: a player with
 * the ball at his feet and time on it has checked his shoulders, which is the
 * most-coached habit in football and the reason he does not need to be facing a
 * teammate to know where he is. It still leaves a 140° blind sector directly
 * behind him.
 *
 * PRESSURE_MAX is the honesty clause, and it is the important half: the widening
 * scales to ZERO as pressure rises, reaching the plain 140° cone once an opponent
 * is inside 60 % of PRESSURE_RADIUS. Under pressure there is no time to look —
 * you play what you can see or you lose it. So this buys the AI TIME it really
 * has, not information it should not have, and it degrades in exactly the
 * situation where a real player's options also collapse.
 */
export const AI_CARRIER_SCAN_EXTRA_DEG = 80;
export const AI_CARRIER_SCAN_PRESSURE_MAX = 0.6;

/**
 * The FLOOR under the conditional half of the out-risk penalty (individual.ts
 * passExecutionPenalty case (b)): a pass aimed at a legal spot whose ball would
 * run out if the receiver missed it.
 *
 * That is conditional on him missing, so it is scaled by how hot the ball is
 * when it reaches him — but never to zero. The floor exists because "he will
 * definitely control it" is not a thing the AI gets to assume: a first touch can
 * squirt away (INTERCEPT_BALL_RETENTION), a defender can toe it on, and the
 * strike error moves the ball off the line the AI scored. A quarter of the full
 * penalty is the residual doubt.
 */
export const AI_OUT_RISK_MISS_FLOOR = 0.25;

// ═══════════════════════════════════════════════════════════════════════════
// SESSION 42 — CHANCE→PASS: the receiver of an intentional pass, support
// angles, and the box run that would not fan apart.
//
// Owner directive, revised mid-session and taken literally: *"instead of
// scoring make the game mostly pass based then it will be very competitve if
// people can score easy goals they lose intrest"*. Nothing in this block is
// tuned to raise shot volume; every value below was measured against PASS
// COMPLETION, PASS SEQUENCE LENGTH and REACHABLE OPTIONS.
//
// The baseline it was measured against (ai/chance-creation.test.ts, seeds
// 7,42,1234,20250725 x 5 sim-min, 'normal' both sides):
//   completed passes per spell of possession   mean 0.94, MEDIAN 0
//   spells with >= 2 completed passes          23.5 %
//   retention within a spell                   57.6 %
//   reachable options per carrier decision     2.27 seen, 1.29 clear
//   carrier had ANY clear option               34.9 % of decisions
//   carrier decision mix                       hold 64.2 %, pass 27.2 %
//   of a carrier's ~9 outfield teammates       69.7 % rejected: OUTSIDE THE CONE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * §3 — HOW LONG A PASS OF OURS STAYS "IN FLIGHT" for the purpose of arming its
 * intended receiver (ai/team-ai.ts applyPassIntent).
 *
 * Not a guess: it is an upper bound on how long a ball this sim kicks can still
 * be in the air or skidding toward a man. `ai/ball-travel.ts` measured a 22 m/s
 * ground ball to rest in ~2.6 s and a 10 m/s one in ~1.3 s; the longest lofted
 * through balls the AI plays hang for about 2 s. 2.5 s covers all of it with
 * margin, and it is only a CEILING — the intent is normally cleared far earlier
 * by the real end conditions (anybody touches the ball, anybody gains
 * possession, or the whistle), which is what makes this safe to set generously.
 */
export const AI_PASS_INTENT_TICKS = 150; // 2.5 s at 60 Hz

/**
 * §3 — how many ticks better placed a TEAMMATE must be before he takes a pass
 * away from the man it was played to ("leave it — mine").
 *
 * The intent has to be a strong bias, not an override. Too small and the change
 * does nothing, because the soonest-intercept man wins again on any pass that is
 * even slightly misplaced. Too large and a badly over-hit ball has one man
 * chasing it hopelessly while the teammate standing next to it holds his shape.
 *
 * 24 ticks = 0.4 s, which is a little over one Layer-3 cycle (12 ticks) and
 * roughly one reaction delay (150-350 ms): i.e. the rival must be better placed
 * by more than the time it would take him to even start. MEASURED: the man the
 * ball was aimed at was the assigned receiver 66 % of the time before, and the
 * table in SESSION_LOG.md records what this moved it to.
 */
export const AI_PASS_INTENT_MARGIN_TICKS = 24; // 0.4 s

// ═══════════════════════════════════════════════════════════════════════════
// BALL ENGAGEMENT — "the bots are glitching / ignoring the ball" (owner, 2026-07-25)
//
// MEASURED FIRST (ai/ball-engagement.test.ts, seeds 7,42,1234 x 3 sim-min,
// 'normal' both sides). The ball is LOOSE for 61-68 % of live play, and while it
// is loose:
//   40.6 % of player-ticks within 8 m of it are NOT closing on it
//   the NEAREST man of each team is not closing 37.1 % / 38.5 % of the time
//   only 1.99 of ten outfielders have a ball-going job
//   6.3-6.9 % of loose ticks a team has NO chaser assigned at all
//   22.2-26.1 % of loose ticks it HAS one but he is beyond
//     AI_CONTEST_MAX_DISTANCE, so ai/individual.ts's looseBall node sent him
//     back to his shape => on ~30 % of loose-ball ticks NOBODY on that team was
//     going for the ball. That is the defect, in one number.
//   20.1 % of all strikes were played with the ball more than 90 deg BEHIND the
//     striker's shoulders (min dot(facing, ball) = -0.996 — near-exactly
//     backwards). That is "players are showing back to the ball and kicking".
// ═══════════════════════════════════════════════════════════════════════════

/**
 * §7 — the radius inside which a loose ball is YOURS regardless of what job
 * Layer 3 gave you.
 *
 * The anti-swarm rule (ONE assigned chaser per team, ai/team-ai.ts assignLoose)
 * is right, and ai/loose-ball-organisation.test.ts is right to pin it. What was
 * wrong is what it left behind: a player who is not the chaser ran to his SHAPE
 * ANCHOR even with the ball at his feet, because looseBall's only question was
 * "am I the designated chaser". MEASURED: the nearest man of each team was not
 * closing on a loose ball within 8 m of him 37-38 % of the time.
 *
 * No footballer ignores a ball one stride away because a teammate was nominated.
 * So this is deliberately a SHORT radius, not a licence to converge: it can only
 * engage a player who is ALREADY basically on top of the ball, which is why it
 * cannot manufacture a swarm — it pulls nobody in from outside, it only stops a
 * man who is already there from turning his back and jogging off. 2.5 m is about
 * one committed stride plus leg reach (BALL_PLAY_REACH is 0.7 m, POSSESSION_RADIUS
 * 1.2 m), i.e. a ball he could put a foot on inside ~0.4 s.
 *
 * Kept BELOW the 3.5 m crowding bound loose-ball-organisation.test.ts asserts, so
 * that assertion stays a real constraint rather than one this constant tunes past.
 */
export const AI_LOOSE_CLAIM_RADIUS = 2.5; // m

/**
 * §4.2 — MINIMUM dot(facing, direction-to-ball) for a footed strike to be legal.
 *
 * You cannot kick a ball that is behind you. The sim had no facing gate at all on
 * `findStriker` (sim/ball.ts): reach was the whole test, so a player running away
 * from a ball 0.6 m off his heel struck it cleanly, and MEASURED over three seeds
 * 20.1 % of every strike in the match was played with the ball more than 90 deg
 * behind the striker's shoulders, 10 % of them past 140 deg.
 *
 * The mechanism is not an animation artifact: player-controller.ts:1404 turns the
 * body toward the aim at a RATE LIMIT (`rotateTowards`) while resolveKicks (stage
 * 6 of step.ts, after the turn at stage 4) applies the strike IMMEDIATELY from
 * `input.aimYaw`. The kick therefore beats the body around, by design — and with
 * nothing checking the body, arbitrarily far.
 *
 * -0.26 is ~105 deg: square to his own shoulders plus a 15 deg margin. NOT 0, and
 * the margin was MEASURED rather than guessed. At exactly 90 deg,
 * sim/pass-quality.test.ts's `first-time` row stops firing: its passer aims at a
 * receiver up the lane while the ball rolls in from the side, which puts the ball
 * at dot = -0.058 — three degrees past square — and the pass is refused. A real
 * player plays that ball without thinking about it. A hooked clearance level with
 * the standing foot is football; a strike behind the heel is not, and 105 deg is
 * the line between them.
 *
 * Anything tighter than this refuses genuine side-foot play. Much looser and the
 * defect returns: the measured distribution of back-to-ball strikes had its p10 at
 * dot -0.881 (152 deg), so the mass being removed is far past this bound.
 *
 * NOT applied to the bicycle kick: findBicycleStriker (ball.ts) deliberately
 * REQUIRES overhead-to-behind geometry (BICYCLE_MAX_FORWARD_DOT) and runs first,
 * so an overhead scissor is unaffected. Nor to a staged restart taker, whose
 * geometry rules/fsm.ts owns.
 */
export const STRIKE_MIN_FACING_DOT = -0.26;

/**
 * §4.2 — below this player-to-ball horizontal distance the facing gate is skipped.
 *
 * A player standing ON the ball has no meaningful direction to it, and normalizing
 * a ~zero vector to test a dot product is a divide-by-zero that would put a NaN
 * straight into the world hash (ai/no-nan.test.ts guards that class of bug). Well
 * under BALL_PLAY_REACH (0.7 m) so it exempts only the genuinely degenerate case.
 */
export const STRIKE_FACING_MIN_DIST = 0.05; // m
/**
 * §4.2 — the LOOSER facing bound that applies when the ball is ARRIVING at the
 * player (closing on him faster than STRIKE_ARRIVING_CLOSING_SPEED).
 *
 * Needed because in this sim `yaw` IS the aim (player-controller.ts:1393 sets
 * targetYaw = aimYaw), so a player redirecting an incoming ball through a wide
 * angle physically cannot also be facing it — his body is already turned to where
 * the ball is going, which is what a real player's body does on a first-time pass.
 * A pure 90 deg gate outlaws every such redirect, and sim/pass-quality.test.ts's
 * `first-time` row measures one at roughly 115-155 deg off the facing.
 *
 * -0.95 is ~162 deg: it admits the genuine first-time flick and still refuses a
 * strike delivered essentially straight backwards off the heel. Deliberately near
 * the limit rather than generous, because this is the exemption that could quietly
 * restore the defect, and it is the CLOSING test that is doing the real work — a
 * ball that is not coming to him gets no exemption at all.
 */
export const STRIKE_ARRIVING_MIN_FACING_DOT = -0.95;

/**
 * §4.2 — how fast the ball must be closing on a player before he is treated as
 * RECEIVING it rather than as having left it behind.
 *
 * 2 m/s. Below this the ball is drifting, not arriving, and no exemption is
 * warranted: POSSESSION_CAPTURE_REL_SPEED (5 m/s) is the speed at which a ball can
 * still be trapped, so anything from 2 m/s up is a ball genuinely coming at him.
 */
export const STRIKE_ARRIVING_CLOSING_SPEED = 2.0; // m/s


/**
 * §5.1 — how strongly a CORNER taker is chosen for being a wide player.
 *
 * Same magnitude as RESTART_ROLE_POSITION_WEIGHT (6.0), deliberately, so the taker
 * is scored on the same scale as the box roles that were already
 * position-weighted. Against RESTART_TAKER_DISTANCE_WEIGHT below, a full winger
 * (bias 1.0) outbids a striker on the flag (0.1) by 4.8 points ≈ 48 m of walk.
 */
export const RESTART_CORNER_WIDE_WEIGHT = 6.0;

/**
 * §5.1 — distance weight for choosing a restart TAKER, per metre.
 *
 * Much weaker than RESTART_ROLE_DISTANCE_WEIGHT (0.5/m, used for the box roles) and
 * that asymmetry is the point: a box role has to be FILLED by somebody already near
 * the box, but the taker has the whole Setup stage to walk to the ball — fsm.ts
 * stages him onto his mark and the ceremony waits for him. So for the taker
 * distance is a tie-breaker, not a qualification.
 *
 * MEASURED, and 0.2 rather than something smaller for a reason the tests found: a
 * taker chosen from far away has to WALK, and sim/rules/restart-walk-in.test.ts
 * asserts the ceremony has settled by the time the corner becomes takeable. At
 * 0.1/m the chosen winger was still in transit when the setup timer expired (he
 * missed arrival by 1 cm), which is a corner taken by nobody — worse than a corner
 * taken by the wrong man. 0.2/m puts the crossover at about 24 m: a winger within
 * 24 m still beats a striker standing on the flag, and he can always get there
 * inside CORNER_SETUP_S. Raising it to 0.3 flips the striker-on-the-flag case back
 * to the striker, which is the defect, so the usable window is narrow and this sits
 * in it.
 */
export const RESTART_TAKER_DISTANCE_WEIGHT = 0.2; // per metre

/**
 * §5.1 — bonus for taking a corner on your OWN flank (a left-sided player on the
 * left corner). Worth about 2 m of walk: a genuine preference that a clearly
 * better-placed player on the other flank still overrides. Real sides do swap
 * takers by side (and by foot), and this is the cheap half of that.
 */
export const RESTART_CORNER_SAME_SIDE_BONUS = 1.0;

// ═══════════════════════════════════════════════════════════════════════════
// MEASURED AND REJECTED — the near/far-post FAN-APART. Do not re-land without
// re-reading this; the change WORKS and the reason it is not here is the owner's
// priority, not a bug.
//
// `ai/attack-variety.test.ts`'s "forwards must fan apart, not cluster" has never
// passed (2.69 m against a required 4). It is not a mystery and it is not a NaN.
// TRACED tick by tick in the exact scenario it builds — carrier at (38, 12), two
// STs dead central, a back line at x = 44:
//
//   1. THE SPLIT IS ONLY A SEARCH CENTRE, i.e. a hint. With centres at
//      (47.4, ±4.6) and AI_BOX_RUN_SEARCH_RADIUS = 18 m, the two influence discs
//      cover almost the same cells, so both forwards' arg-max returns the SAME
//      one. Measured: both returned (31.2, ∓1.7), and by t = 2 s BOTH were
//      seeking z = −1.7.
//   2. AN ARG-MAX ON `control` RETREATS OUT OF A DEFENDED BOX. control is
//      own−opp influence, and a penalty area with two centre-backs and a keeper
//      in it is exactly where that is most negative — so the "box run" returned
//      a cell SIXTEEN METRES SHORT of the box. A forward attacking a cross does
//      not look for the cell his own team controls.
//   3. THE RULE TELLS THEM TO CROSS OVER. rank-by-BALL-side gave rank 0 the post
//      on the ball's side whatever side of the shape he started on: measured,
//      shapeZ was −1.5 for rank 0 and +8.0 for rank 1 with the ball at z = +12,
//      so the left-sided forward was sent to the RIGHT post and vice versa, with
//      the shape pull fighting the run pull the whole way.
//
// THE FIX, implemented and verified GREEN: give each man the post on HIS OWN
// side (which preserves near/far for free — whoever is already on the ball's
// side IS the near-post runner) and make the side a HARD MASK applied after the
// space search, `if (postSide * space.z < 3.5) space.z = postSide * 3.5`, the same
// idiom steering.ts uses for the offside line. 3.5 m is just inside the post
// (GOAL_WIDTH/2 = 3.66). `attack-variety` then passes with the two forwards
// 10.9 m apart on opposite sides of the goal centre, and the whole ai suite stays
// green.
//
// WHY IT IS NOT LANDED. The owner's directive changed mid-session to *"instead of
// scoring make the game mostly pass based … if people can score easy goals they
// lose intrest"*, and fanning two forwards apart costs the passing game on EVERY
// seed set tried. 8 seeds x 5 sim-min, both sides 'normal', measured against the
// same tree twice with DISJOINT seed sets:
//
//                              set 1: ctrl -> mask     set 2: ctrl -> mask
//   passes attempted                566 -> 469             490 -> 442
//   passes completed                291 -> 245             248 -> 212
//   completed passes per spell     0.90 -> 0.87           0.83 -> 0.67
//   spells with >= 2 completed    22.9% -> 21.9%         22.0% -> 15.6%
//   spells with >= 3 completed    12.4% -> 12.4%         11.3% ->  8.4%
//   retention within a spell      50.3% -> 51.2%         50.2% -> 47.4%
//   balls out of play                 31 -> 34               59 -> 62
//   clear options per decision     1.37 -> 0.90           1.41 -> 1.48
//
// Attempts −10 %/−17 % and completions −15 %/−16 %, reproducibly. The mechanism is
// not subtle: two forwards held 7-11 m apart across the goal mouth are two men
// whose lanes are blocked by the defenders now between them, and a carrier in
// midfield loses both as options. It is better football for SCORING and worse
// football for PASSING, and passing is what was asked for.
//
// If the priority ever flips back, the patch is three edits: import
// AI_BOX_RUN_POST_MIN_Z, replace the rank/nearSide block in individual.ts
// offBallAttack with the own-side + postSide version, and clamp space.z after
// bestCellNear. Do NOT weaken the assertion to get green — it is a correct
// assertion about a real defect.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * §10.2 — THE LAY-OFF RADIUS: inside this distance a teammate is a pass option
 * regardless of which way the carrier is facing (individual.ts
 * collectPassOptions). Beyond it the facing cone applies in full.
 *
 * ── The measurement this exists for ─────────────────────────────────────────
 *
 * With a per-gate tally on every outfield teammate a ball-carrier had, over 8
 * seeds x 5 sim-min: 69.7 % of them were rejected for one single reason —
 * OUTSIDE THE FACING CONE. Only 0.98 per decision were lost to a blocked lane,
 * 1.4 % to being too close and 1.4 % to being too far. So "the carrier has
 * nobody to pass to" is overwhelmingly a facing problem, and the consequence is
 * in the decision mix: the carrier HELD the ball on 64.3 % of all his decisions
 * and passed on 28.9 %.
 *
 * Session 41 widened the carrier's scan from 140° to 220° when unpressed and
 * measured that as fixing 39.5 % of FORWARD options. This is the other, larger
 * half of the same finding, and it needs a different model rather than a wider
 * cone: the 140° sector directly behind a player is where his SUPPORT is, and
 * the set-back to a supporting midfielder is one of the most common passes in
 * football. It is routinely played without looking — he was in your peripheral
 * vision two seconds ago, he is calling, and you can hear him. Modelling that as
 * unavailable models a player who must turn his whole body round to remember
 * that his own midfield exists.
 *
 * ── Why 14 m, and why this is not a vision cheat ────────────────────────────
 *
 * 14 m is a little under half AI_PASS_MAX_COMFORTABLE_DISTANCE (30 m) and a
 * little over AI_PASS_MIN_DISTANCE x 4: the band in which a teammate is close
 * enough to be heard and to have been in view moments ago. It is deliberately
 * NOT the whole awareness radius — a switch to the far touchline BEHIND you is a
 * genuine information problem and stays unavailable, which is the property §10.2
 * is protecting.
 *
 * Nothing else is relaxed. A lay-off still has to clear AI_PASS_LANE_BLOCK_RADIUS,
 * still cannot go to an offside man, and still pays AI_SCORE_BACKWARD_PASS_PENALTY
 * — so this can only add options that were safe to play anyway. It does not touch
 * `canSee`, so the AI still cannot REACT to anything behind it: not the presser
 * closing from behind, not a shot, not a carrier it has not turned toward.
 */
export const AI_PASS_LAYOFF_RADIUS = 10; // m

/**
 * §10.2 — the lay-off SECTOR: how wide the pass-option cone opens inside
 * AI_PASS_LAYOFF_RADIUS. 250° = ±125° off the carrier's facing.
 *
 * ⚠ THIS LIMIT IS LOAD-BEARING. The first version waived the facing cone
 * entirely inside the radius, and that broke TWO existing assertions in
 * `ai/pass-to-human.test.ts` — the file whose §5 is titled "the vision gate is
 * NOT weakened":
 *
 *   - "will not blind-pass to a human directly behind the carrier" (the human at
 *     exactly 180°, 10 m away) — the assertion exists precisely so co-op
 *     preference cannot smuggle a pass past §10.2, and a 360° exemption walks
 *     straight through it;
 *   - "no pass was made at all" in the four-packed-teammates case — on 'easy'
 *     (passOptions = 3) collectPassOptions keeps the NEAREST three, so three
 *     tightly-marked men clustered dead behind the carrier crowded the open
 *     forward option out of the list and he passed to nobody. A wider set of
 *     options made the decision WORSE.
 *
 * Both are the same defect and it is a real one: a man behind your back is not a
 * man you know about, however close he is. ±125° is roughly what peripheral
 * vision plus a shoulder-check actually covers, it leaves a 110° blind sector
 * directly behind the carrier, and it keeps both assertions true by construction
 * rather than by tuning.
 */
export const AI_PASS_LAYOFF_CONE_DEG = 250;

// ---------------------------------------------------------------------------
// SHOW FOR THE BALL — off-ball movement for the men Layer 3 used to forget
//
// Two previous sessions converged on the same remaining cause from opposite
// directions and both stopped one step short of it:
//
//   - from the ENGAGEMENT side: `assignAttack` names a Carrier, the forwards and
//     the two nearest bodies, and leaves EVERYONE ELSE on HoldShape, whose whole
//     Layer-2 behaviour is "seek your formation anchor, then nothing". Measured
//     over 8 seeds x 5 sim-min: purposeful outfielders 6.96 / 7.16 of 10, and the
//     two worst roles were the two that get left behind — RM 53.2 %, CM 58.2 %.
//   - from the PASSING side: the carrier HOLDS on 57.3 % of his decisions, and
//     65.4 % of the nine teammates he has are rejected for one reason only — they
//     are OUTSIDE HIS FACING CONE. That session proved widening the cone FURTHER
//     makes decisions worse (a flat 360° waiver breaks two §10.2 assertions) and
//     concluded: "the next lever is off-ball movement that puts support in the
//     carrier's view — not more cone relaxation".
//
// A player who is not the carrier, not a forward and not one of the two nearest
// now takes the OFFER assignment: he moves onto the nearest edge of the sector
// the carrier can see, AT THE RADIUS HE ALREADY HAD.
//
// ⚠ THE PRESERVED RADIUS IS THE ANTI-SWARM PROPERTY, and it is why this is not
// the under-8s huddle `ai/loose-ball-organisation.test.ts` exists to prevent. An
// offering player ROTATES around the carrier; his distance from the ball is
// exactly the distance his formation anchor already had. Nobody moves toward the
// ball who was not already going there, the Chase/Press caps are untouched, and
// it is scoped to OUR POSSESSION only — a loose ball and an opponent carry still
// organise the team the way they already did.
//
// ── ⚠ WHAT THIS DOES NOT DO, MEASURED. READ BEFORE BUILDING ON IT. ──────────
//
// It does NOT deliver the second session's hypothesis, and the honest record of
// that matters more than the win below. Traced per carrier-tick over 3 seeds
// (assignment-by-assignment, against collectPassOptions' real scan cone):
//
//     for the men who offer      inside the carrier's scan cone
//     their formation anchor              37.9 %
//     THE OFFER POINT                     49.8 %   <- the geometry works
//     WHERE THEY ACTUALLY ARE             38.0 %   <- ...and it does not arrive
//
// The offer point is markedly more visible than the anchor. The player is not:
// he sits a mean 8.6 m short of his own seek target, because the carrier's
// facing is not a target a footballer can run to — it turns far faster than a
// man moves, so this is a pursuit that never converges. That comparison is
// WITHIN one run (anchor vs target vs body, same men, same ticks), so match
// divergence cannot explain it.
//
// The aggregate gate is much noisier and does NOT settle the question: over two
// separate pinned trees the soak's OUTSIDE-THE-CONE share moved 65.4 -> 64.7 %
// on one and 69.4 -> 62.5 % on the other. The spread is dominated by which seed
// collapses into a long lone dribble in each arm (a lone carrier has his whole
// team behind him, which is a cone rejection for every one of them) — so where
// the gate did improve, the credible cause is the TEAM's geometry no longer
// collapsing, not the offering individual becoming visible.
//
// Net: the intended per-player mechanism is only partly delivered, and the cone
// rejection is substantially STRUCTURAL — an attacking team's carrier is usually
// its most advanced man, so most teammates are behind him by construction. Take
// the seek-target/body table above as the finding, and do not expect another
// round of off-ball repositioning to buy much more of this gate.
//
// ── WHAT IT DID DO, MEASURED (8 seeds x 5 sim-min, control re-run and
//    bit-identical to the pre-change baseline) ────────────────────────────────
//
//     purposeful outfielders /10   6.96 / 7.16  ->  7.75 / 7.77   (p10 2 -> 5)
//     ...on the LABEL-PROOF half   5.10 / 5.44  ->  5.81 / 5.89
//     worst role (RM)                    53.2 % ->  62.3 %
//     next worst (CM)                    58.2 % ->  64.7 %
//     passes attempted                      476 ->  512
//     passes completed                      271 ->  305   (56.9 % -> 59.6 %)
//     retention within a spell           56.3 % ->  59.2 %
//     spells with >=3 completed passes   12.6 % ->  13.8 %
//     balls out per 5 sim-min               7.3 ->  7.0
//     live play                          71.4 % ->  70.9 %
//     goals                                   6 ->  5
//
// The mechanism is therefore RETENTION, not visibility: a supporting player who
// is moving into space near his slot is a better outlet when the ball does reach
// him, and the team keeps it. It also cured a pathology outright — one seed in
// eight used to collapse into a single uncontested possession (seed 161803: ball
// loose only 34.4 % of in-play, engagement 4.53/4.43, the worst seed by a mile).
// With offers on, every seed sits in a 52-63 % loose band and the worst seed's
// engagement is 6.91. That single seed was most of the old headline's deficit.
//
// ⚠ ONE COUNTER MOVED THE WRONG WAY, ON BOTH TREES: shots 20 -> 23 on one and
// 13 -> 21 on the other, against an explicit "do not raise shots". GOALS FELL on
// both (6 -> 5 and 5 -> 3) and the share taken FROM INSIDE THE BOX fell with
// them (70 -> 61 % and 54 -> 43 %), so what rose is speculative shooting from
// worse positions, not scoring — the owner's actual concern ("if people can
// score easy goals they lose interest") is satisfied and the raw shot count is
// not. If anyone extends this, that is the counter to hold down.
// ---------------------------------------------------------------------------

export const AI_OFFER_CONE_HALF_DEG = 55;

/**
 * The distance band, from the carrier, in which offering is the right job.
 *
 * Below the minimum he is already a lay-off (AI_PASS_LAYOFF_RADIUS = 10 m waives
 * the cone for him anyway), so rotating him buys no option and only crowds the
 * ball. Above the maximum he is not a pass this sim can play — AI_PASS_MAX_
 * COMFORTABLE_DISTANCE is 30 m — so he is better employed holding the shape that
 * will be needed the moment possession turns over.
 */
export const AI_OFFER_MIN_DISTANCE = 10; // m
export const AI_OFFER_MAX_DISTANCE = 30; // m

/**
 * How far an offering player may end up from his formation anchor.
 *
 * The shape is not a suggestion: situational-shape.ts has already moved this
 * anchor for the phase, and an offer that ignores it is a player abandoning his
 * position to stand where the ball happens to be looking. 11 m is a little over
 * the measured defence-midfield line gap (10.4 m) and well under the
 * midfield-forward gap (19.6 m), so an offering player can leave his slot but
 * never another man's line.
 */
export const AI_OFFER_MAX_ANCHOR_DRIFT = 11; // m

/**
 * The space search around the offer point. Deliberately smaller than
 * AI_SUPPORT_SEARCH_RADIUS (14 m): at a 20 m radius from the carrier a 14 m disc
 * spans ±44° of bearing, which is enough for the influence arg-max to put him
 * back OUTSIDE the sector he was just rotated into. 7 m is ±20° at that radius,
 * which fits inside the margin AI_OFFER_CONE_HALF_DEG leaves.
 */
export const AI_OFFER_SEARCH_RADIUS = 7; // m

/**
 * How far OUTSIDE the offer band a man who is already offering keeps the job.
 *
 * Offering and holding shape seek very different points, so a player sitting on
 * a band edge who flips between them at 5Hz is handed two targets in opposite
 * directions — which is the owner's "glitching", literally. MEASURED with a hard
 * band: steering reversals rose from 0.261 to 0.337 per player-second (+29 %),
 * the one counter in ai/ball-engagement.test.ts that is the owner's complaint
 * verbatim. 3 m is a little over half a second of running, so a man genuinely
 * leaving the band still drops the job promptly.
 */
export const AI_OFFER_BAND_HYSTERESIS = 3; // m

// ---------------------------------------------------------------------------
// Dribble WEAVE (§3.4) — the ball crosses between the boots while running.
//
// Owner, 2026-08-09, against the reference footage: he wants the ball visibly
// moving "left foot to right, like how they dodge", not glued to one boot.
//
// It was glued by construction — and glued to the CENTRELINE, which is neither
// boot. Every target in `applyDribbleTouch` sat at `p + dir * lead`, dead centre
// between the feet, and the model then treated any lateral offset as ERROR to be
// nulled: the knock's `across` term drives it to zero over
// DRIBBLE_KNOCK_LINE_RECOVER_S, and the spring's `offLine`/`onLine` test measures
// deviation from that same centre. So the harder the carry worked, the more
// exactly the ball tracked a line running between the boots.
//
// The fix moves the TARGET rather than adding a wobble on top of it: the lead
// point, the settle point and the knock's lateral aim all shift to one boot, and
// the side alternates. The ball is shepherded exactly as tightly as before — it
// is just shepherded to a foot instead of to the midline, so the same correction
// that used to hold it dead centre now carries it across.
//
// Phase comes from `state.tick` (never a clock, never Math.random — §15.3) and
// is offset per player id, so a pair running together do not weave in lockstep.
// ---------------------------------------------------------------------------

/**
 * How far off the running line the ball is carried — half the width of the
 * weave. A boot sits ~0.10-0.12 m off a player's centreline, so this is roughly
 * "on the inside of the foot" rather than a swerve.
 *
 * Bounded well inside the two acceptance corridors the dribble model is pinned
 * to (owned-perp <= 1.25 m, envelope S-curve <= 2.3 m): at the 0.55 m lead it
 * moves the ball's DISTANCE from the carrier by sqrt(lead^2 + 0.15^2) - lead =
 * 20 mm. It changes where the ball sits, not how far away it gets.
 */
export const DRIBBLE_FOOT_OFFSET = 0.15; // m either side of the running line

/**
 * Seconds the ball spends on one foot before the touch sends it to the other —
 * half a full weave cycle. A run is ~1.6-1.8 strides/s, so 0.45 s puts a
 * crossing roughly every stride-and-a-bit: a weave, not a shimmy.
 *
 * Deliberately a TIME and not a distance or a stride index: the sim cannot see
 * the animation's stride phase (the clip plays client-side and publishes nothing
 * back), so anything claiming to sync to the actual footfall would be inventing
 * a phase it does not have.
 */
export const DRIBBLE_WEAVE_HALF_PERIOD_S = 0.45; // s on one foot

/**
 * Below this the ball does not weave at all. A carrier walking or standing still
 * keeps the old dead-centre behaviour — a stationary weave reads as the ball
 * wandering off on its own, and the standing-start and corner cases are exactly
 * where the centre-seeking spring was measured and tuned.
 */
export const DRIBBLE_WEAVE_MIN_SPEED = 2.6; // m/s

/** Speed ABOVE the minimum at which the weave reaches full width, so it fades in
 *  over a stride instead of switching on at an edge the player can feel. */
export const DRIBBLE_WEAVE_RAMP_SPEED = 1.8; // m/s

/**
 * A ground Shoot charged below this is a KNOCK-AHEAD, not a strike — the tap a
 * surrounded carrier plays past the crowd to run onto. The BALL already scales
 * with charge; this only splits what the ANIMATION shows (AnimState.ShootLight,
 * a simple in-stride kick, against the full wind-up strike). Band: charge must
 * clear SHOT_CHARGE_MIN_S (0.2) to fire at all, so light is [0.2, 0.45).
 */
export const SHOT_LIGHT_CHARGE_S = 0.45;

/**
 * How fast a striker PIVOTS INTO HIS AIM through a footed strike's windup and
 * contact window (deg/s). STRIKE_MIN_FACING_DOT (-0.26) accepts a ball up to
 * ~105 deg off the shoulders, and with contact timing on the windup to
 * NormalShot's window is ~0.14 s - 750 deg/s covers the worst legal misaim
 * inside it, so the forward strike clip plays along the true kick direction
 * instead of the ball leaving sideways off a straight-ahead swing. Measured
 * baseline before this existed: 94 deg of yaw error at the launch tick
 * (strike-aim-pivot.test.ts). The aim is the STORED actAimYaw, frozen at
 * acceptance - a strike is not a turret and does not track the stick.
 */
export const STRIKE_AIM_ALIGN_RATE = 750;

/**
 * THE KEEPER'S OWN STANCE GATE (m/s). Above this he is stepping and must be
 * SHOWN stepping; below it he is genuinely set and `GkReady` is the truth.
 *
 * `GkIdle` binds to a sustained POSE — full weight, gait underneath at zero,
 * i.e. a planted foot — so gating it on WALK_SPEED_THRESHOLD (1.4, the
 * OUTFIELD walking boundary) left the whole 0.15-1.4 band, which is where a
 * keeper spends most of a match, rendering as a statue gliding across the
 * six-yard box (owner: "make sure goal keeper never slide"). Measured on a
 * 1.0 m/s shuffle: 118 of 118 moving ticks in the planted stance.
 * `locomotionAnim` already answers a lateral shuffle with a strafe at any
 * speed, so this gate is the whole fix.
 */
export const GK_STEP_SPEED_THRESHOLD = 0.35;

/**
 * How long a keeper's DISTRIBUTION pose is held (s). His own clips run
 * 1.10-1.77 s (GkOverarmThrow 1.10, GkPuntRight 1.63, GkRollout 1.77), and the
 * release hold used to borrow THROW_ANIM_HOLD_S = 2.77 - which is the OUTFIELD
 * THROW-IN clip's length. Holding a 1.1 s throw for 2.77 s leaves the keeper
 * frozen in its last frame for over a second and a half after the ball has
 * gone, which is what "he is not throwing the ball with hands or kicking"
 * looks like from the stands: the release plays, then a statue. Sized to the
 * longest keeper release so every one of them completes and none of them
 * freezes.
 */
export const GK_RELEASE_ANIM_HOLD_S = 1.8;

/**
 * THE CONTROL BANDS, by ball height at the touch (m).
 *
 * Below KNEE the touch is footed and needs no special surface. Between KNEE
 * and CHEST_MAX the body meets it with the knee/thigh and drops it to the
 * boot; above that, up to HEADER_CONTACT_HEIGHT, it is a chest cushion — and
 * a header above that, which the strike path already handles.
 *
 * CHEST_MAX sits below HEADER_CONTACT_HEIGHT (1.6) on purpose: the two must
 * not overlap, or the same ball is both a header and a trap depending on
 * which branch runs first.
 */
export const KNEE_CONTROL_MIN_HEIGHT = 0.45;
export const CHEST_CONTROL_MIN_HEIGHT = 1.0;
export const CHEST_CONTROL_MAX_HEIGHT = 1.55;

/**
 * How long a player must be genuinely still before he is IDLE (s). The idle
 * gate used to be instantaneous - one tick under ANIM_IDLE_SPEED and the
 * hands-went-to-hips pose started, so every dribble touch and direction flip
 * flashed it (owner, 2026-08-14: "when player stop for a milli sec he starts
 * doing idle animation holding his hips"). Until this window passes a
 * stopping player keeps the walk winding down, which is what real feet do.
 */
export const IDLE_SETTLE_S = 0.35;

/**
 * Minimum dwell in a LOCOMOTION state before another locomotion state may
 * replace it (s). Measured on a 5-minute AI 11v11 (match telemetry,
 * 2026-08-14): 3 751 flapping bursts - players oscillating Run/Jog/Backpedal/
 * Strafe 5-6 times per SECOND as the AI's heading wobbled across the
 * classification edges, leaving them permanently mid-blend (the renderer
 * blends 0.15 s). A quarter second is under half a stride at jog - direction
 * reads stay honest - and actions, falls and idle transitions bypass it
 * entirely.
 */
/**
 * ⚠ 0.30 IS THE KNEE OF A MEASURED CURVE, not a round number, and it is a
 * TRADE — raising it always reduces strobing and always buys that with a clip
 * that disagrees with the speed it is played at. Swept on the 5-minute AI
 * telemetry against both failure modes at once:
 *
 *     dwell   transitions   flaps   GAIT-LIE ticks
 *     0.25        8567       573        7,297
 *     0.30        8109       310       10,744   <- here
 *     0.35        7669       275       14,666
 *     0.40        7210       268       15,457
 *     0.55        6237       255       24,384
 *
 * 0.25 -> 0.30 nearly halves visible strobing. Everything past it buys ~11 %
 * more for a third again as much foot-sliding. Re-run the sweep before moving
 * it; `flaps` alone will always argue for a bigger number.
 */
export const LOCO_DWELL_S = 0.30;

/**
 * How far the CARRIER may reach to strike HIS OWN dribbled ball (m). The
 * generic strike reach is BALL_PLAY_REACH (0.7), but a sprint dribble rides
 * the ball 0.83-0.87 m ahead BY DESIGN (measured, __pass_probe 2026-08-15) -
 * so a sprinting carrier could never pass at all: the tap waited forever for
 * geometry that never comes (owner: "pass while running is not happeing").
 * You can always play your own next touch; that is what a dribble IS. Loose
 * balls keep the 0.7 m gate - this is possession-only, not a magnet.
 */
export const STRIKE_OWN_BALL_REACH = 0.95;

/**
 * A LOFTED BALL CLEARS A MAN, AND THE FORCE SAYS BY HOW MUCH (m of apex).
 *
 * Owner, 2026-08-15: "ball is not going in the air", then "it should go from
 * above the player as per the kick force". Both are the same request. The
 * lofted pass solved its arc PURELY from the receiver's distance, so a short
 * one peaked 1.26-1.81 m MEASURED IN THE RUNNING GAME - under a standing
 * player's head (~1.8 m), which is exactly "not going in the air" to the eye
 * that pressed the modifier to go over somebody.
 *
 * So a lofted strike now has a floor: at the minimum charge it apexes at
 * LOFT_APEX_MIN_M - already clear of any player - and the charge carries it
 * to LOFT_APEX_MAX_M. The arc the distance-solver wanted is kept whenever it
 * is HIGHER; the floor only ever lifts, never flattens, so a long ball still
 * sails the way its distance demands.
 */
export const LOFT_APEX_MIN_M = 2.6;
export const LOFT_APEX_MAX_M = 6.5;
/** The chip's own ceiling angle (deg) — a Q-shot is a dink, not a punt. */
export const CHIP_SHOT_ANGLE_DEG = 34;

/**
 * THE TURN SHEPHERD — how hard a carrier drags his own ball around a turn
 * (m/s of sideways correction per second), and the angle that earns it.
 *
 * Owner, 2026-08-15: "when player is running with the ball it should always be
 * in front of him unless someone steals it". MEASURED before this existed: a
 * straight sprint keeps the ball 0.85 m ahead and 9 deg off the facing -
 * correct - but a sustained turn left it **1.9 m away at 165 deg, DIRECTLY
 * BEHIND HIM**, still owned. The radial gather cannot fix that: the ball is
 * not receding, it is simply on the old line while the man is on a new one.
 *
 * So a carrier's foot steers the ball's LINE toward where he is running, not
 * just its pace. Velocity only, capped, and it dies the moment the ball leaves
 * the control envelope - a tackled or stolen ball is on nobody's line.
 */
export const DRIBBLE_TURN_SHEPHERD_TOL_DEG = 22;
export const DRIBBLE_TURN_SHEPHERD_DV = 34;
/** How quickly the shepherd aims to have the ball back on his line (s). */
export const DRIBBLE_TURN_RECOVER_S = 0.28;
/** Above this ball-to-carrier relative speed it is not a dribble (m/s). */
export const DRIBBLE_TURN_SHEPHERD_MAX_REL = 3.5;

/**
 * WHAT A TACKLED BALL DOES (owner, 2026-08-15: "when player touches or kicks
 * the ball with slide tackle make the contact and move the ball as how it
 * should move").
 *
 * `pokeBall` used to OVERWRITE the ball's velocity with a fixed speed along
 * the tackler's facing and zero the lift - so a ball arriving at 20 m/s left
 * the contact at 6 m/s in whatever direction the tackler happened to face,
 * however the boot actually met it. That is a teleport with extra steps, and
 * it is why a tackle never looked like a collision.
 *
 * A contact instead: the ball KEEPS part of its own momentum
 * (TACKLE_BALL_RETENTION), takes the boot's push along the line from the
 * tackler to the ball blended with his facing (TACKLE_CONTACT_NORMAL_MIX -
 * a boot that meets the ball square drives it on, one that clips the side
 * deflects it), and a slide's studs get under it for a real bobble
 * (TACKLE_SLIDE_LIFT).
 */
export const TACKLE_BALL_RETENTION = 0.35;
export const TACKLE_CONTACT_NORMAL_MIX = 0.55;
export const TACKLE_SLIDE_LIFT = 2.4;
export const TACKLE_POKE_LIFT = 0.9;

/**
 * How close to a line a shielding carrier starts bending his escape back onto
 * the pitch (m). The shield runs directly away from the nearest presser, and a
 * presser arriving from the middle aims that vector at the touchline - so bots
 * reversed until the ball went out and gave away a throw (owner, 2026-08-16).
 * Inside this band the boundary progressively wins the argument.
 */
export const AI_TOUCHLINE_GUARD_M = 6;

/**
 * How far forward (m) the AI aims a headed clearance when out of shooting
 * range (owner, 2026-08-23: "AI should head it more often") — a fictional
 * "shot" target fed to emitShot purely to derive a sensible lift/direction,
 * the same way progressForwardClearance's own last-resort ball-upfield
 * fallback works. Large enough that the derived lift lands near
 * AI_SHOT_LIFT_FAR (a real header under pressure is looped clear, not
 * flat), not tied to any specific pitch geometry — it is a direction and an
 * arc, not a literal target point.
 */
export const AI_HEADER_CLEARANCE_DIST = 30;

/**
 * How much of the shield vector's raw backward pull survives the "don't
 * retreat" clamp below, 0..1 (owner, 2026-08-21/23: "the bots dribble the
 * ball backward"). 0 would fully forbid retreat; MEASURED that this broke
 * `situational-shape` R5 (a losing side stopped out-committing the same
 * side winning — a carrier who can never step back under pressure is
 * dispossessed more, not less, which nets the team DEEPER over a match, not
 * further forward). 0.35 keeps most of the fix (backward-shield-
 * diagnostic's mean backward component 0.41 -> 0.14, backwardFrac
 * 66.2% -> 50.4%) while leaving a real evasive option under genuine
 * pressure — the same "escape, not a flaw" reasoning AI_TROUBLE_PRESSURE's
 * bail-out already applies to backward/sideways passing.
 */
export const AI_SHIELD_MAX_BACKWARD = 0.65;

/**
 * THE BAIL-OUT PASS (owner, 2026-08-16: "when they are in trouble pass the
 * ball or dribble"). Above this pressure a carrier with no FORWARD option
 * looks for any free teammate in any direction rather than shielding - which
 * is what left bots reversing with the ball until it went out. Sideways and
 * backwards are football; walking it off the pitch is not.
 */
export const AI_TROUBLE_PRESSURE = 0.82;
/**
 * How much freer the receiver must be before a bail-out is worth playing.
 * ⚠ WITHOUT THIS the bail-out fired on any pressured carrier and reshaped the
 * whole match: attack-variety's box forwards stopped splitting posts (2.76 m
 * apart, bar 4) and shoulder-barge left 84 men off balance (bar 28), because
 * everybody was passing sideways instead of attacking. A bail-out is an
 * ESCAPE, and an escape into similar pressure is not one.
 */
export const AI_BAIL_RELIEF = 0.25;
/** A bail-out is a real pass, not a toe-poke to the man on your shoulder. */
export const AI_BAIL_MIN_M = 6;
export const AI_BAIL_MAX_M = 32;

/**
 * BOT BOOST BUDGET (owner: "let the bots use the boost"). InputFlags.Boost1/2
 * appeared nowhere in the AI, so the two-tier boost was human-only and a bot
 * could never win a race it was losing. Boost costs 2x (tier 1) and 4x
 * (tier 2) the run drain, so these floors keep a bot from emptying its bar on
 * a lost cause and then walking for half a minute.
 */
export const AI_BOOST_STAMINA_FLOOR = 45;
export const AI_BOOST2_STAMINA_FLOOR = 75;
/**
 * A break worth the bar: real space ahead, not merely a positive cell. The
 * carrier must ALSO be unpressed (the dribble node's own `breaking` clause) —
 * boosting a pressed carrier just runs him into the man marking him.
 */
export const AI_BOOST_SPACE_MIN = 0.25;
/**
 * A race short enough that the second gear still lands inside it. Past this
 * the ball is far away, tier 1 buys the same outcome, and tier 2 only empties
 * the bar sooner.
 */
export const AI_BOOST_TIER2_RACE_S = 1.4;
/**
 * How far a defender must still be from a ball carrier, closing the gap
 * during DuelState.Approach, before it is worth spending the bar to get
 * there. (§10.1 duel; owner 2026-08-23: "bots... using boost" alongside
 * "make sure the bots slide" — CR-8/CR-27's own finding was that defenders on
 * the competitive path often never reach tackling range at all before the
 * carrier is gone; a recovery run that closes faster is a real, untried lever
 * distinct from the tackle-decision tweaks that finding already exhausted.)
 *
 * Set to STEAL_ENGAGEMENT_RANGE (7m, "actively pressing") rather than
 * anything tighter: inside that range he is already close enough that a
 * normal sprint closes it in about a second, and the earlier "boost the
 * break" cut already measured what happens when a boost fires too readily
 * on routine movement (broke situational-shape R5, carriers boosted into
 * contact and lost the ball deep). Gating on being genuinely OUTSIDE active-
 * press range keeps this to real catch-up running, not every defensive step.
 */
export const AI_BOOST_DUEL_CLOSE_MIN_M = 7;

// ---------------------------------------------------------------------------
// PENALTY SHOOTOUT (§5.1) — owner, 2026-08-16: "penality if draw"
// ---------------------------------------------------------------------------

/** Kicks each side takes before sudden death. Law 10: five. */
export const SHOOTOUT_REGULATION_KICKS = 5;
/**
 * Hard cap on kicks per side. Sudden death between two sides who both keep
 * missing has no natural end — a real shootout is bounded only because Law 10
 * makes every eligible player take before anyone repeats, so this is that
 * bound as a number: eleven each, then the coin toss decides.
 */
export const SHOOTOUT_MAX_KICKS = 11;
/** The walk to the halfway line and the wait before the first kick. */
export const SHOOTOUT_OPENING_PAUSE_S = 6;
/** Between kicks: long enough to see the result, short enough to keep moving. */
export const SHOOTOUT_INTERVAL_S = 3.5;
/**
 * An attempt is over when the ball has effectively stopped. Above the sim's
 * own resting threshold so a ball trickling toward the line is still live.
 */
export const SHOOTOUT_BALL_REST_SPEED = 0.6;
/**
 * Backstop on a single attempt. Nothing — a ball wedged against a post, a
 * taker who never shoots, a human who disconnected on his kick — may hold the
 * match open. Generous enough that a real run-up and a scramble finish first.
 */
export const SHOOTOUT_ATTEMPT_MAX_S = 15;

// ---------------------------------------------------------------------------
// BALL CONTEST (Phase-1 Commit 2) — acceptance test G: "neither gains magical
// immediate control purely because his centre is 3 cm closer."
// ---------------------------------------------------------------------------

/**
 * How much better one claim must be than an opponent's before it is a win
 * rather than a contest. `controlScore` returns roughly 0..1, so this is a
 * meaningful edge — a clearly better body position, not a rounding error.
 */
export const BALL_CONTEST_SCORE_MARGIN = 0.12;
/**
 * A contest is a countdown to a decision, not a stalemate. Past this the
 * better claim takes it, so two players can never freeze a ball between them.
 * ~0.4 s: long enough to read as a genuine fight for it, short enough that
 * play never stalls.
 */
export const BALL_CONTEST_MAX_TICKS = 24;
/**
 * A contest needs a ball somebody could actually TAKE. Above this the ball is
 * in transit, not up for grabs, and freezing ownership while it flies past
 * means nothing shepherds it — measured at 88 goal-line restarts per 5-minute
 * match. Matched to the possession capture gate: if neither man could capture
 * it, neither is contesting it.
 */
export const BALL_CONTEST_MAX_BALL_SPEED = POSSESSION_CAPTURE_REL_SPEED;
/** controlScore weights: relative speed matters most, then distance, then
 *  whether he is even looking at it. Sum to 1. */
export const BALL_CONTROL_W_SETTLED = 0.45;
export const BALL_CONTROL_W_NEAR = 0.35;
export const BALL_CONTROL_W_FACING = 0.20;

// ---------------------------------------------------------------------------
// THE DUEL (Phase-1 Commits 4-7) — sim/duel.ts
// ---------------------------------------------------------------------------

/** Beyond this an opponent contributes no pressure at all. */
export const PRESSURE_MAX_RANGE_M = 14;
/**
 * The pressure horizon, seconds. A threat arriving now is full pressure; one
 * arriving at the horizon is none. ~1.2 s is about how long a carrier has to
 * make a decision before contact, which is what pressure is measuring.
 */
export const PRESSURE_HORIZON_S = 1.2;

/** A ball this far from the carrier's feet is fully exposed. */
export const BALL_EXPOSED_DIST_M = 2.2;
/** ...or moving this fast relative to him — the touch that got away. */
export const BALL_EXPOSED_REL_SPEED = 4.0;

/** scoreTackleOpportunity weights. Exposure dominates: a bad touch is the
 *  reason to challenge, and the rest is whether you can afford it. */
export const DUEL_OPPORTUNITY_W_EXPOSURE = 0.5;
export const DUEL_OPPORTUNITY_W_ADVANTAGE = 0.35;
export const DUEL_OPPORTUNITY_W_SUPPORT = 0.15;
/** A team-mate this close counts as cover behind the duel. */
export const DUEL_SUPPORT_RADIUS_M = 12;
/** How much of the opportunity the last man gives up. Not all of it — a last
 *  defender must still be able to challenge an exposed ball, or a bad touch in
 *  front of him is a free run at goal. */
export const DUEL_LAST_DEFENDER_PENALTY = 0.45;

// ── Duel state machine (Commit 7) ──────────────────────────────────────────
/**
 * ASYMMETRIC BY DESIGN. Equal thresholds make a defender flicker between
 * contain and engage on noise, which is the "twitchy bot" failure the owner
 * called out. Enter an engagement on a clear opportunity; leave it only when
 * the opportunity has genuinely gone.
 */
/**
 * ⚠ CALIBRATED TO THE SCORE'S REAL RANGE, not to a round number.
 *
 * These started at the 0.70/0.48 the brief sketched, and the AI then went a
 * full 12-seed card harness without committing a SINGLE challenge — zero
 * fouls, zero cards, which is its own kind of broken. `scoreTackleOpportunity`
 * tops out near 0.45 for an ordinary contain (exposure is the dominant term
 * and a controlled ball scores almost none of it), so 0.70 was unreachable and
 * the machine could never leave CONTAIN.
 *
 * The RATIO is what the brief was really specifying — enter on a clear
 * opportunity, leave only when it has properly gone — and that is preserved
 * at ~1.5x. Re-derive both if the weights in scoreTackleOpportunity move.
 */
export const DUEL_ENGAGE_ENTER = 0.32;
export const DUEL_ENGAGE_EXIT = 0.21;
/** Minimum time in a state before it may change, seconds. */
export const DUEL_MIN_APPROACH_S = 0.15;
export const DUEL_MIN_CONTAIN_S = 0.25;
export const DUEL_MIN_ENGAGE_S = 0.15;
/** After a failed challenge: no re-tackle, recover balance and goal-side. */
export const DUEL_RECOVER_S = 0.8;

/** Contain distance band, metres — the target gap while jockeying. */
export const CONTAIN_DIST_MIN_M = 0.9;
export const CONTAIN_DIST_MAX_M = 2.6;
/** How much of the band a fast attacker buys: give a sprinter more room. */
export const CONTAIN_SPEED_GIVE = 0.5;
/** ...and how much is taken back near your own goal, where you cannot show
 *  him a shot. */
export const CONTAIN_SHOOTING_TIGHTEN = 0.45;
/** Range at which shooting threat starts tightening the contain. */
export const CONTAIN_SHOOTING_RANGE_M = 26;

/**
 * The duel's own perception gates. Mirrors AI_AWARENESS_RADIUS /
 * AI_VISION_CONE_DEG — /sim cannot import /ai, and a player reacting to
 * somebody behind his head is the defect §10.2 exists to prevent, so the rule
 * is restated rather than skipped. ⚠ KEEP THE TWO IN STEP.
 */
export const DUEL_AWARENESS_RADIUS_M = 30;
export const DUEL_VISION_HALF_RAD = (150 * 0.5 * Math.PI) / 180;

// ── ENGAGE (the ENGAGE/CHALLENGE split) ────────────────────────────────────
/**
 * scoreEngageOpportunity weights. TREND leads deliberately: a rising exposure
 * is a mistake happening NOW, which is when a real defender steps; a high flat
 * exposure is just a long carry he is in control of.
 */
export const ENGAGE_W_TREND = 0.40;
export const ENGAGE_W_EXPOSURE = 0.30;
export const ENGAGE_W_ADVANTAGE = 0.20;
export const ENGAGE_W_SUPPORT = 0.10;
/** Exposure rise per second that counts as a full-blooded bad touch. */
export const ENGAGE_TREND_FULL = 1.5;
/** Beyond this the step is not worth making. */
export const ENGAGE_STEP_RANGE_M = 4.0;
/** Cap on how far ahead the step point leads the ball. */
export const ENGAGE_LEAD_MAX_S = 0.4;
/**
 * SET FROM THE MEASURED DISTRIBUTION, not by hand — the whole reason the
 * previous pair was untrustworthy is that it was chosen against a score that
 * could not reach it.
 *
 * scoreEngageOpportunity over a 90 s AI match, sampled in CONTAIN:
 *     p50 0.453   p90 0.851   p99 0.988   max 0.988
 *
 * The first provisional pair (0.30/0.18) sat BELOW the median, so a defender
 * left contain on more than half of all ticks — contain's median duration
 * collapsed 5333 ms -> 333 ms and four AI acceptance suites went red. ENGAGE
 * is meant to be the exception, not the resting state: ~p88 puts it at roughly
 * one tick in eight, which is about how often a carrier's touch actually
 * invites a step.
 *
 * ⚠ RE-DERIVE BOTH from a fresh histogram if the weights in
 * scoreEngageOpportunity move. The ratio (~1.45x) is the part that is a
 * judgement; the level is evidence.
 */
export const ENGAGE_ENTER = 0.80;
export const ENGAGE_EXIT = 0.55;

// ── ENGAGE continuation (Commit B1) ────────────────────────────────────────
/**
 * WHY A CONTINUATION EXISTS. `scoreEngageOpportunity` is 0.40 TREND by design
 * — a heavy touch is a mistake happening NOW — and a trend is one-shot: the
 * decision after entry it has fallen to zero and the score with it, from ~0.86
 * to ~0.45, under ENGAGE_EXIT before the defender's step has had time to earn
 * anything. MEASURED (8 seeds x 90 s, docs/duel/engage-diag-B05.log): 65 % of
 * ENGAGE episodes exit ENGAGE_SCORE_DROPPED at the first permitted decision
 * (duration p50 200 ms), and the ones that DID earn geometry — best margin
 * +0.87 m, ball-first legal for 5 ticks — were aborted by the same clock.
 * The trigger was being reused as the reason to keep stepping.
 *
 * So the exit is allowed a short, EARNED continuation: only while the step is
 * still working — the defender is closing on the ball, is (or is becoming)
 * ball-first, and the attacker has not regained control — and only for a
 * bounded time. Progress stops, he aborts. This does not touch ENGAGE_ENTER,
 * ENGAGE_EXIT, the challenge gate, or the trend weight; it changes what "the
 * opportunity vanished" MEANS at the exit. Entry is unchanged: the score
 * still starts ENGAGE, the continuation never does.
 */
/** Ceiling on the earned continuation, seconds. ~2-3 steps; not a sprint. */
export const ENGAGE_GRACE_MAX_S = 0.5;
/** Ball-first margin must be improving at least this fast (m/s), unless it
 *  is already legal — a flat positive margin while closing IS winning the race. */
export const ENGAGE_PROGRESS_MARGIN_RATE = 0.25;
/** Defender→ball distance must be shrinking at least this fast (m/s). */
export const ENGAGE_PROGRESS_CLOSE_RATE = 0.4;


/**
 * SPRINT GEAR HYSTERESIS (see ai/individual.ts setSprint). Both numbers exist
 * because a bare threshold limit-cycles, and the cycle was measured as 54 % of
 * every animation transition in a 5-minute match.
 */
/** Stamina band around AI_STAMINA_SPRINT_FLOOR: start above floor+band, keep
 *  going until below floor-band. Breaks the drain/regen ring. */
export const AI_SPRINT_STAMINA_BAND = 6;
/**
 * Minimum ticks in gear before the sprint decision may flip again (~0.13 s).
 *
 * ⚠ MEASURED CEILING, not a feel value. 24 ticks (0.4 s) damped the flapping
 * slightly better but starved a team of the ball — the AI-vs-AI possession
 * share fell to 4.6 % against a 5 % floor, because a chaser who cannot change
 * gear for four tenths of a second loses every race he was marginally in.
 * 8 ticks keeps the flap reduction (573 -> 347) with possession balanced.
 */
export const AI_SPRINT_HOLD_TICKS = 8;

/**
 * THE JOG/RUN *ANIMATION* EDGE — deliberately NOT MAX_SPEED_JOG.
 *
 * It used to be MAX_SPEED_JOG (3.5), and the two constants have nothing to do
 * with each other: one is the top of the jog CLIP's speed range, the other is
 * where the stamina economy starts charging you. They just shared a number,
 * and that coincidence was the single largest source of animation churn in the
 * game.
 *
 * The mechanism, measured rather than reasoned: stamina is neutral at or below
 * MAX_SPEED_JOG and drains above it, so a bot with an empty bar settles at
 * just under 3.5 and STAYS there — it cannot regenerate (that needs walking
 * pace) and will not drain further. On the 5-minute AI telemetry the median
 * stamina at a Run<->Jog flip was 4 out of 100, and 1,634 of 2,503 flips came
 * from players under 25. They were all parked precisely on the animation
 * boundary, crossing it with every tick of speed noise.
 *
 * ⚠ MEASURED AND REJECTED: moving it to 4.2 made things WORSE, not better —
 * transitions 8567 -> 8733 and flaps 573 -> 648. Wherever the edge is put,
 * some population of players sits on it; the speed distribution has mass
 * everywhere, so band-edge crossing is inherent and not an unlucky constant.
 * The alias is kept because the DECOUPLING is still correct — if the stamina
 * threshold ever moves, the clip edge should not follow it by accident.
 *
 * ⚠ Keep a gap of at least a few tenths above MAX_SPEED_JOG. If the stamina
 * threshold moves, this must move with it, and for the same reason.
 */
export const ANIM_JOG_RUN_EDGE = MAX_SPEED_JOG;

// ── CHEST / KNEE CONTROL (owner: "show how real ball deflect with the
// intensity ... if the intensity is low drop the ball on ground") ───────────
/**
 * How much harder a chest or knee cushion deadens the ball than a footed touch
 * does, at perfect quality. 0 = identical to a footed touch, 1 = stone dead.
 * A chest control taken well leaves the ball sitting; taken badly it runs.
 */
export const CONTROL_DEADEN = 0.8;
/**
 * Downward speed a perfectly-cushioned ball is given, m/s. This is what makes
 * it FALL to his feet rather than carry on at chest height — the vertical half
 * of the touch, which simply did not exist before. The knee drops it further
 * because the knee is further from the ground than the chest is... which is
 * backwards, so the knee gets the SMALLER number: from knee height the ball
 * only has to fall a boot's length.
 */
export const CHEST_CONTROL_DROP_SPEED = 2.4;
export const KNEE_CONTROL_DROP_SPEED = 1.2;

/**
 * The gather only acts on a ball a MOVING carrier is running with. Below this
 * pace there is no carry to protect and the gather is telekinesis — a standing
 * player was measured dragging a dead ball 1.29 m from a metre away, with no
 * boot within 0.6 m of it.
 *
 * Set at a walk: a man jogging with the ball still gets the gather that keeps
 * it on his boot, a man standing over it does not.
 */
export const DRIBBLE_GATHER_MIN_CARRY_SPEED = 1.4;

/**
 * CLOSE CONTROL (Ctrl) speed cap. Sits inside the free stamina band
 * (WALK_SPEED_THRESHOLD 1.4 .. MAX_SPEED_JOG 3.5) on purpose: it is both the
 * pace at which a dribbler can keep the ball on his boot AND the pace at which
 * a keyboard player stops draining, which were two separate owner reports with
 * one cause — no walk control existed at all.
 */
export const CLOSE_CONTROL_SPEED = 3.2;
/** How much tighter close control keeps the ball than an ordinary carry. */
export const CLOSE_CONTROL_LEAD_MULT = 0.55;

/**
 * Speed at or below which stamina RECHARGES.
 *
 * ⚠ DELIBERATELY ABOVE THE CLIENT'S WALK SPEED, and deliberately NOT
 * WALK_SPEED_THRESHOLD. The walk key throttles the move vector to 0.258 of a
 * 6.0 m/s run = 1.548 m/s; the old ceiling was 1.4, so holding walk recharged
 * nothing and the bar only ever refilled standing perfectly still.
 * WALK_SPEED_THRESHOLD is the Walk/Jog ANIMATION edge, tuned against foot
 * sliding — it has no business setting the stamina economy, and sharing it
 * meant a change to either silently moved the other.
 *
 * ⚠ IF WALK_THROTTLE MOVES, MOVE THIS. It must stay above the walk it is meant
 * to pay for.
 */
export const STAMINA_RECHARGE_MAX_SPEED = 1.8;

// ── KEEPER HAND DISTRIBUTION (owner's control scheme, 2026-08-16) ──────────
/** Two X taps inside this window drop the ball. It is a DOUBLE tap because a
 *  release is irreversible under Law 12 — he may not handle it again — so a
 *  single stray press would be a turnover in his own six-yard box. */
export const GK_DROP_TAP_WINDOW_S = 0.45;
/** How far a hand-pass will look for the team-mate the keeper is aiming at. */
export const GK_HAND_PASS_MAX_M = 34;
/** Half-angle of the aim cone that picks him. Generous: pointing roughly at a
 *  man should pick him, without demanding pixel accuracy on a moving target. */
export const GK_HAND_PASS_CONE_RAD = (30 * Math.PI) / 180;

// ── CARRY TURN (owner: "show it like a dribble turn") ──────────────────────
/** Below this pace a turn with the ball is a shuffle, not a carry turn. */
export const CARRY_TURN_MIN_SPEED = 2.6;
/** Heading swing, rad/s, that counts as turning rather than curving. ~110 deg/s
 *  — a real change of direction, not a gentle arc round a defender. */
export const CARRY_TURN_MIN_RATE = 1.9;
